import React, { useEffect, useMemo, useRef, useState, useCallback, useDeferredValue } from "react";
import { TurnosAntiguosBanner } from "@/components/TurnosAntiguosBanner";
import { HelpButton } from "@/components/help/HelpPanel";
import { BtPermisoGuia } from "@/components/BtPermisoGuia";
import { useNavigate } from "react-router-dom";
import Barcode from "react-barcode";
import { api, publicApi, clp, resolveMedia, MAX_LINEAS_DTE, AVISO_LIMITE_LINEAS } from "@/lib/api";
import { DescuentoManual, montoDescuentoManual } from "@/components/DescuentoManual";
import { tbkVenta, tbkTotales, tbkCierre } from "@/lib/tbkPos";
import { mpCobrar } from "@/lib/mpPoint";
import { c2cCobrar } from "@/lib/tbkC2c";
import { TRANSPORTE_VACIO, transportePayload, validarTransporte } from "@/components/TransporteFields";
import { referenciasPayload, validarReferencias } from "@/components/ReferenciasFields";
import { FacturaClienteModal } from "@/components/FacturaClienteModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { pmMeta } from "@/config/paymentMethods";
import { useAuth } from "@/lib/auth";
import { computePromotions } from "@/lib/promotions";
import { resolveRounding } from "@/lib/rounding";
import { formatRut, validateRut, normalizeRut } from "@/lib/rut";
import { calcularEdad } from "@/lib/cedula";
import { useCedulaScan } from "@/lib/useCedulaScan";
import { BRAND } from "@/config/brand";
import { ArrowLeft, Barcode as BarcodeIcon, Plus, Minus, Trash2, Wallet, Printer, X, Search, User, Ticket, TicketCheck, Cpu, Keyboard, Layers, WifiOff, Wifi, RefreshCw, CloudUpload, PauseCircle, PlayCircle, AlertOctagon, Scale, Delete, Bluetooth, BluetoothOff, BluetoothSearching, Monitor, Zap, Maximize2, FileText, Lock, AlertTriangle, KeyRound, Check, Hash, Mail, Database, Repeat, Loader2, CalendarClock, Usb, CreditCard, Megaphone, LayoutGrid } from "lucide-react";
import { enqueueSale, enqueueOp, shiftSummary, queuedUnits, flushQueue, listPending, discardItem, purgeSynced } from "@/lib/offlineQueue";
import { normalizeTiers, tierPrice, tiersLabel } from "@/lib/pricing";
import { cachedGet, cachedCatalog, cachePut, getLocalShift, setLocalShift, rememberOperatorPin, identifyOperatorOffline, getDeviceLoginOffline, isNetworkError } from "@/lib/localCache";
import { pairPrinter, printReceipt, saleToReceipt, cashMovementToReceipt, cashCloseToReceipt, suspendedToReceipt, contestVoucherToReceipt, exchangeSlipToReceipt, buildTicketBytes, isSupported as btSupported, getSavedName as btSavedName, forgetPrinter as btForget, sendRawBase64, tryReconnect as btTryReconnect, isConnected as btIsConnected, queryPrinterStatus as btQueryStatus, printBoletaLiviana, setPrinterScope as btSetScope, hasPersistentPermissions as btPermisosFijos, savedPrinter as btSavedPrinter, onPrinterState as btOnState } from "@/lib/btPrinter";
import CafFoliosBanner from "@/components/CafFoliosBanner";
import CertificadoAvisoCaja from "@/components/CertificadoAvisoCaja";
import { SaleTicket } from "@/components/SaleTicket";
import { Switch } from "@/components/ui/switch";
import { isKioskShell, closeKioskWindow } from "@/lib/kioskShell";
import { isCashMethod } from "@/lib/rounding";
import { printHtml, saleTicketHtml, cashMovementHtml, cashCloseHtml, suspendedTicketHtml, presaleTicketHtml, qrDataUrl, barcodeDataUrl, receiptUrl, cashOpenHtml, contestVoucherHtml, renderTicketPng, boletaLegalHtml, llevaCopiaCedible, exchangeSlipHtml } from "@/lib/osPrint";
import { CLIENTE_VACIO, validarCliente } from "@/components/ClienteTributario";
import { probeAgent, agentPrint, agentOpenDrawer, bytesToBase64 } from "@/lib/localAgent";
import AndroidPosGuard from "@/components/AndroidPosGuard";
import CuponPOS, { montoCupon } from "@/components/CuponPOS";
import { ElegirModificadores } from "@/components/ModificadoresDialog";
import { isUsbSupported, usbPair, usbReconnect, usbPrint, usbOpenDrawer, usbSavedName, isUsbConnected, usbStatus, usbStatusMensaje, usbStalled } from "@/lib/usbPrinter";
import NumPad from "@/components/NumPad";
import { useEntitlements } from "@/lib/entitlements";
import { PasswordInput } from "@/components/PasswordInput";
import TPValidar from "@/components/tarjetaPropia/TPValidar";
import TPAbonoDialog from "@/components/tarjetaPropia/TPAbonoDialog";
import CuadraturaCredito from "@/components/tarjetaPropia/CuadraturaCredito";

// Tarjeta de función del POS táctil (restaurante): todas las acciones viven en un solo panel
const FN_TONES = {
  slate: "bg-white border-slate-200 text-slate-700 hover:bg-slate-50",
  sky: "bg-sky-50 border-sky-200 text-sky-800 hover:bg-sky-100",
  cyan: "bg-cyan-50 border-cyan-200 text-cyan-800 hover:bg-cyan-100",
  violet: "bg-violet-50 border-violet-200 text-violet-800 hover:bg-violet-100",
  amber: "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100",
  rose: "bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100",
};
const PosFnTile = ({ testid, icon: Icon, label, hint, onClick, tone = "slate", disabled, lockProps }) => (
  <button
    type="button"
    data-testid={testid}
    disabled={disabled}
    onClick={onClick}
    {...(lockProps || {})}
    className={`rounded-2xl border px-2 py-3 flex flex-col items-center justify-center gap-1.5 text-center transition active:scale-95 disabled:opacity-40 ${FN_TONES[tone] || FN_TONES.slate}`}
  >
    {Icon ? <Icon className="w-6 h-6" /> : null}
    <span className="text-[12px] font-bold leading-tight">{label}</span>
    {hint ? <span className="text-[10px] font-semibold opacity-70 leading-none">{hint}</span> : null}
  </button>
);

const DENOMS = [1000, 2000, 5000, 10000, 20000];
// Colores reales de los billetes chilenos (Banco Central): 1.000 verde, 2.000 morado,
// 5.000 rojo, 10.000 azul, 20.000 naranja cobrizo.
const BILL_STYLES = {
  1000: "bg-emerald-100 border-emerald-500 text-emerald-900 hover:bg-emerald-200",
  2000: "bg-purple-100 border-purple-500 text-purple-900 hover:bg-purple-200",
  5000: "bg-rose-100 border-rose-500 text-rose-900 hover:bg-rose-200",
  10000: "bg-blue-100 border-blue-600 text-blue-900 hover:bg-blue-200",
  20000: "bg-orange-100 border-orange-500 text-orange-900 hover:bg-orange-200",
};

/** Texto comparable: minúsculas y sin acentos, para que "cafe" encuentre "café". */
const plano = (s) => String(s ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Etiqueta de balanza leída en el equipo (misma regla que el servidor): evita una consulta por escaneo
const parseBalanza = (cfg, code) => {
  if (!cfg?.enabled || !/^\d+$/.test(code) || code.length !== Number(cfg.largo_total || 13)) return null;
  if (!(cfg.prefijos || []).some((p) => code.startsWith(String(p)))) return null;
  const sku = code.substr(Number(cfg.sku_start), Number(cfg.sku_length));
  const raw = parseInt(code.substr(Number(cfg.valor_start), Number(cfg.valor_length)), 10);
  if (!sku || Number.isNaN(raw)) return null;
  return cfg.valor_tipo === "PRECIO" ? { sku, precio: raw } : { sku, peso: raw / 10 ** Number(cfg.peso_decimales ?? 3) };
};

/** Montos que se escriben a mano: se muestran con separador de miles (7.390). */
const soloDigitos = (v) => String(v ?? "").replace(/\D/g, "");
const miles = (v) => {
  const d = soloDigitos(v);
  return d ? Number(d).toLocaleString("es-CL") : "";
};

const TICKET_MODE_LABELS = {
  TICKET: "Ticket interno", BOLETA: "Boleta SII", FACTURA: "Factura SII",
  GUIA: "Guía", NC: "Nota crédito", PREVENTA: "Pre-venta",
};

// Nombre del documento que se imprime al cerrar la venta
const docImpreso = (sale) => (sale?.factura || sale?.factura_dte_id || sale?.doc_type === "FACTURA" ? "Factura" : "Boleta");

// Resultados de búsqueda por descripción que se pueden recorrer con las flechas
const RETAIL_MAX_RESULTADOS = 50;

export default function POS() {
  const nav = useNavigate();
  const { store, user, company } = useAuth();
  const ent = useEntitlements();
  // En la prueba gratuita sólo se cobra con los medios sin integración (Doc 10 §6).
  const payAllowed = (codigo) => String(codigo || "").toUpperCase().startsWith("OTRO_") ||
    String(codigo || "").toUpperCase() === "TARJETA_PROPIA" ||
    !ent?.payment_codes_allowed || ent.payment_codes_allowed.includes(String(codigo || "").toUpperCase());
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loyaltyCfg, setLoyaltyCfg] = useState(null);
  // Aviso de vencimiento del último producto escaneado (no bloquea la venta)
  const [expiryAlert, setExpiryAlert] = useState(null);
  useEffect(() => {
    if (!expiryAlert) return;
    const t = setTimeout(() => setExpiryAlert(null), 8000);
    return () => clearTimeout(t);
  }, [expiryAlert]);

  const [quickOpen, setQuickOpen] = useState(false);
  // Detalle opcional de la venta rápida por monto (no se imprime, queda para el jefe de tienda)
  const [quickDetalleOpen, setQuickDetalleOpen] = useState(null);
  const [quickDetalle, setQuickDetalle] = useState("");
  // Vale de cambio canjeado en esta venta
  const [valeOpen, setValeOpen] = useState(false);
  const [valeCodigo, setValeCodigo] = useState("");
  const [valeBusy, setValeBusy] = useState(false);
  const [vale, setVale] = useState(null);
  // Recuperar cotización por número o cliente
  const [cotOpen, setCotOpen] = useState(false);
  const [cotQ, setCotQ] = useState("");
  const [cotRows, setCotRows] = useState([]);
  const [cotBusy, setCotBusy] = useState(false);
  // Recuperar cotización: la pantalla queda en blanco mientras carga, así que avisamos
  const [quoteLoading, setQuoteLoading] = useState(
    () => !!new URLSearchParams(window.location.search).get("cotizacion")
  );
  const [quickMonto, setQuickMonto] = useState("");
  const [shift, setShift] = useState(null);
  const [cat, setCat] = useState("ALL");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [quoteId, setQuoteId] = useState(null);
  const [quoteNumero, setQuoteNumero] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [descManual, setDescManual] = useState({ tipo: "MONTO", valor: "" });
  const [cupon, setCupon] = useState(null);
  const [descPin, setDescPin] = useState(null);       // { pin, nombre } del jefe que autorizó
  const [facturaModal, setFacturaModal] = useState(false);
  const [tbkEstado, setTbkEstado] = useState(null);   // mensaje del POS Transbank mientras cobra
  const [tbkError, setTbkError] = useState(null);     // último rechazo/error para reintentar
  const tbkBusyRef = useRef(false);
  const mpCancelRef = useRef(false);
  const chargeRef = useRef(null);
  const pickMethodRef = useRef(() => {});
  const [payMethod, setPayMethod] = useState("EFECTIVO");
  // Tipo de documento a emitir al cobrar (varía según flags del terminal)
  const [payDocType, setPayDocType] = useState("BOLETA");
  const [recibido, setRecibido] = useState(0);
  const [recibidoTouched, setRecibidoTouched] = useState(false);
  const recibidoRef = useRef(null);
  const confirmVentaRef = useRef(null);
  const [roundCfg, setRoundCfg] = useState(null);
  // Pago en partes: cada abono aprobado queda aquí hasta que el saldo llega a $0
  const [parciales, setParciales] = useState([]);
  const [equipoActivo, setEquipoActivo] = useState(null);
  const [printingSale, setPrintingSale] = useState(null);
  const [openOpen, setOpenOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [confirmFundOpen, setConfirmFundOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [tbkTot, setTbkTot] = useState(null);   // totales del equipo Transbank al cerrar caja
  const fundInputRef = useRef(null);
  const [movOpen, setMovOpen] = useState(false);
  const [tpAuth, setTpAuth] = useState(null);        // Tarjeta Propia validada para esta venta
  const [tpAbonoOpen, setTpAbonoOpen] = useState(false);
  const movMontoRef = useRef(null);
  const [fondo, setFondo] = useState("");
  const [nowTs, setNowTs] = useState(new Date());
  const [contado, setContado] = useState(0);
  const [mov, setMov] = useState({ tipo: "RETIRO", monto: "", motivo: "" });
  const [lastSale, setLastSale] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  // Cliente
  const [custRut, setCustRut] = useState("");
  const [redeemAmount, setRedeemAmount] = useState("");
  const [custName, setCustName] = useState("");
  const [custInfo, setCustInfo] = useState(null);
  const [custLoading, setCustLoading] = useState(false);
  // Terminales
  const [terminals, setTerminals] = useState([]);
  const shiftParams = () => {
    const tid = localStorage.getItem("pos_terminal_id") || "";
    const tStore = tid ? terminals.find((t) => t.terminal_id === tid)?.store_id : null;
    return { store_id: tStore || store?.store_id || undefined, terminal_id: tid || undefined };
  };
  // pos_terminal_id lo escribe /pair al emparejar, o el mismo POS al elegir uno.
  const [terminalId, setTerminalId] = useState(() => {
    // ?terminal=tm_xxx (lo trae el acceso directo de cada caja) manda sobre lo guardado
    const fromUrl = new URLSearchParams(window.location.search).get("terminal");
    if (fromUrl) {
      localStorage.setItem("pos_terminal_id", fromUrl);
      return fromUrl;
    }
    return localStorage.getItem("pos_terminal_id") || "";
  });
  // Si el PC está pareado (tiene aipos_device_token) → terminalId queda bloqueado en el pareado
  const pairedTerminalId = (() => {
    try {
      const tok = localStorage.getItem("aipos_device_token");
      if (tok) return localStorage.getItem("pos_terminal_id") || null;
    } catch (_) {}
    return null;
  })();
  // Producto grande destacado (último escaneado)
  const [lastProduct, setLastProduct] = useState(null);
  const [retailSel, setRetailSel] = useState(0); // índice de resultado retail seleccionado
  // Cantidad multiplicador (F9)
  const [qtyBuffer, setQtyBuffer] = useState(""); // dígitos tipeados antes de F9
  const [pendingQty, setPendingQty] = useState(null); // cantidad a aplicar al siguiente escaneo
  // Modo remover
  const [removeMode, setRemoveMode] = useState(false);
  // Ticket preventa
  const [preOpen, setPreOpen] = useState(false);
  const [lastPresale, setLastPresale] = useState(null);
  // Cobro multi-preventa
  const [presaleTickets, setPresaleTickets] = useState([]); // códigos cargados
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  // Modo offline
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  // Avisos de administración pendientes de leer por el cajero
  const [avisos, setAvisos] = useState([]);
  const [avisosOpen, setAvisosOpen] = useState(false);
  const [avisoBusy, setAvisoBusy] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingRetryable, setPendingRetryable] = useState(0);
  // Frescura de los datos locales (catálogo, precios, turno) cuando se opera sin red
  const [cacheInfo, setCacheInfo] = useState({ fromCache: false, at: Date.now() });
  const [catVersion, setCatVersion] = useState("");
  const [catSyncing, setCatSyncing] = useState(false);
  // Verificación de edad (alcohol, cigarrillos) y fecha de nacimiento leída de la cédula
  const [ageOpen, setAgeOpen] = useState(false);
  // Operador del turno (PIN obligatorio al abrir el POS y después de cada cierre de caja)
  const [operator, setOperator] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("pos_operator") || "null"); } catch { return null; }
  });
  // Permisos POS por usuario (Usuarios y roles): funciones apagadas para el cajero identificado
  const posOff = useMemo(() => new Set(operator?.pos_off || []), [operator]);
  const puede = (k) => !posOff.has(k);
  const abrirMov = () => {
    if (!puede("retiro") && !puede("ingreso")) { toast.error("Tu usuario no tiene habilitado ingreso ni retiro"); return; }
    setMov({ tipo: puede("retiro") ? "RETIRO" : "INGRESO", monto: "", motivo: "" });
    setMovOpen(true);
  };
  const negar = (k) => { if (puede(k)) return false; toast.error("Tu usuario no tiene habilitada esta función"); return true; };
  const lk = (k) => (puede(k) ? {} : { disabled: true, title: "Función deshabilitada para tu usuario", "data-locked": "true" });
  const [ageAuth, setAgeAuth] = useState(null);
  const ageAuthRef = useRef(null);
  const [custNacimiento, setCustNacimiento] = useState(null);
  // Datos tributarios del cliente para la factura electrónica (DTE 33 / 34)
  const [facturaData, setFacturaData] = useState(CLIENTE_VACIO);
  const [facturaObs, setFacturaObs] = useState("");
  const [facturaTransp, setFacturaTransp] = useState(TRANSPORTE_VACIO);
  const [facturaRefs, setFacturaRefs] = useState([]);
  // Turno "crudo" (como lo conoce el servidor o como se abrió offline). Los totales que se
  // muestran = turno crudo + lo que haya en la cola offline, así el cierre cuadra sin red.
  const shiftRawRef = useRef(null);
  // Unidades vendidas offline por producto: el stock mostrado sin red es estimado
  const [offlineUnits, setOfflineUnits] = useState({});
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [syncing, setSyncing] = useState(false);
  // Producto no registrado
  const [unknownCode, setUnknownCode] = useState(null);
  // Ventas suspendidas
  const [suspendedList, setSuspendedList] = useState([]);
  const [suspendedCount, setSuspendedCount] = useState(0);
  const [suspendedOpen, setSuspendedOpen] = useState(false);
  const [lastSuspended, setLastSuspended] = useState(null);
  const [suspendTicketOpen, setSuspendTicketOpen] = useState(false);
  // Peso manual para pesable
  const [weightModal, setWeightModal] = useState(null); // { product }
  const [weightInput, setWeightInput] = useState("");
  // Última venta (para reimprimir)
  const [reprintTicketOpen, setReprintTicketOpen] = useState(false);
  const [reprintSale, setReprintSale] = useState(null);
  // Devolución
  const [returnModal, setReturnModal] = useState(null); // { sale, returned_qty }
  const [returnQty, setReturnQty] = useState({}); // {product_id: cantidad}
  // Último producto agregado (para repetir con Enter)
  const [lastAddedProduct, setLastAddedProduct] = useState(null);
  // Cierre de caja
  const [arqueo, setArqueo] = useState({}); // {"20000": 3, ...}
  const [closePreview, setClosePreview] = useState(null); // {payment_totals, ...}

  const searchRef = useRef(null);
  const productsRef = useRef([]);
  useEffect(() => { productsRef.current = products; }, [products]);
  const [balanzaCfg, setBalanzaCfg] = useState(undefined); // undefined = aún no cargada → se consulta al servidor

  const terminal = useMemo(() => terminals.find((t) => t.terminal_id === terminalId) || null, [terminals, terminalId]);
  const isPreventaTerminal = terminal?.ticket_mode === "PREVENTA" || terminal?.tipo === "PREVENTA";
  const confirmClearCart = terminal?.confirm_clear_cart !== false; // default true
  const preventaBarcodeType = terminal?.preventa_barcode_type || "CODE39";
  // Retail por defecto; mientras carga el terminal se usa la última interfaz vista en este equipo (sin parpadeo)
  const uiMode = terminal ? (terminal.ui_mode || "RETAIL") : (localStorage.getItem(`aipos_ui_mode_${terminalId}`) || "RETAIL"); // TILE | RETAIL
  useEffect(() => { if (terminal) localStorage.setItem(`aipos_ui_mode_${terminalId}`, terminal.ui_mode || "RETAIL"); }, [terminal, terminalId]);
  // Enter con el buscador vacío repite el último producto marcado (parámetro del terminal)
  const enterRepiteUltimo = terminal?.enter_repite_ultimo !== false;
  // Venta restringida 18+: en botillerías el aviso por cada venta estorba, se apaga por terminal
  const pedirAutorizacionEdad = terminal?.pedir_autorizacion_edad !== false;
  const isRetail = uiMode === "RETAIL";
  const isTile = !isRetail;
  // Interfaz restaurante: todas las funciones en un solo panel para dejar la pantalla limpia
  const [funcOpen, setFuncOpen] = useState(false);
  // Cierre de turno: lo define el administrador en la configuración del terminal (el cajero no elige)
  const closeMode = String(terminal?.close_mode || "SIMPLE").toUpperCase() === "ARQUEO" ? "ARQUEO" : "SIMPLE";

  // Abierto desde el acceso directo .bat: sin acceso a administración
  const kioskShell = isKioskShell();
  const exitPos = async () => {
    if (!kioskShell) { nav("/admin"); return; }
    const stillOpen = await closeKioskWindow();
    if (stillOpen) toast.info("Cierra la ventana con Alt + F4");
  };
  // Reloj de la barra de estado (fecha y hora del equipo)
  useEffect(() => {
    const t = setInterval(() => setNowTs(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // Modo de impresora del terminal: único y excluyente (OS 80 mm, Bluetooth 58 mm o ninguna)
  const printerMode = terminal?.printer_mode
    || (/^USB/i.test(terminal?.printer_type || "") ? "OS_80MM" : (terminal?.bt_printer_name ? "BT_58MM" : "NONE"));
  const bannerColor = terminal?.last_item_color || "sky";
  // Mensaje al pie del vale/boleta definido por el administrador para este terminal
  const graciasMsg = terminal?.ticket_gracias || "";
  // Ticket(s) de cambio que acompañan al documento: uno por venta o uno por producto.
  // No llevan precios (sirven de regalo) y se identifican con el N° de venta.
  const cambioSlips = (sale) => {
    // En venta offline el número es el código local (no numérico): el ticket no se podría
    // buscar después en Ventas, así que no se imprime hasta que la venta sincronice.
    if (!terminal?.ticket_cambio_enabled || !sale || sale.offline) return [];
    const items = (sale.items || [])
      .filter((it) => Number(it.cantidad) > 0)
      .map((it) => ({ cantidad: it.cantidad, nombre: it.nombre, sku: it.sku }));
    if (!items.length) return [];
    return terminal?.ticket_cambio_modo === "PRODUCTO" ? items.map((it) => [it]) : [items];
  };
  const BANNER_COLORS = {
    sky: "from-sky-500 to-sky-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
    violet: "from-violet-500 to-violet-600",
    orange: "from-orange-500 to-orange-600",
    cyan: "from-cyan-500 to-cyan-600",
    slate: "from-slate-700 to-slate-800",
  };

  // Totales visibles del turno = turno crudo + operaciones que están en la cola offline.
  // Así el resumen y el cierre cuadran aunque no haya red.
  const applyOfflineTotals = useCallback(async (raw) => {
    const base = raw || shiftRawRef.current;
    if (!base?.shift_id) {
      shiftRawRef.current = base || null; setShift(base || null);
      await setLocalShift(null);
      return;
    }
    shiftRawRef.current = base;
    const sum = await shiftSummary(base.shift_id);
    setShift({
      ...base,
      ventas_efectivo: (base.ventas_efectivo || 0) + sum.efectivo,
      ventas_total: (base.ventas_total || 0) + sum.total,
      num_ventas: (base.num_ventas || 0) + sum.num_ventas,
      ingresos: (base.ingresos || 0) + sum.ingresos,
      retiros: (base.retiros || 0) + sum.retiros,
    });
    // Siempre guardamos el turno abierto en el equipo (también el del servidor): si se cae la red
    // y se reinicia el POS, la caja sigue abierta en modo offline en vez de pedir abrirla de nuevo.
    await setLocalShift(base);
  }, []);

  const load = useCallback(async () => {
    // Cada GET se guarda en IndexedDB: si el equipo arranca sin red, el POS usa la copia local.
    const cg = (key, url, opts) => cachedGet(key, () => api.get(url, opts));
    // Producto interno de venta rápida: se precarga para que el atajo funcione sin conexión
    cachedGet("quick_item", () => api.post("/pos/quick-item")).catch(() => {});
    const [p, c, m, pr, lc, t, rc] = await Promise.all([
      // Catálogo versionado: en catálogos grandes solo se descarga si cambió; el stock llega aparte
      cachedCatalog(
        "catalog_v2",
        () => api.get("/products/catalog-version"),
        () => api.get("/products?scope=branch"),
      ),
      cg("categories", "/categories"),
      cg("payment_methods", "/payment-methods"),
      cg("promotions", "/promotions?canal=POS"),
      cg("loyalty_cfg", "/loyalty/config", { silent402: true }).catch(() => ({ data: null })),
      cg("terminals", "/terminals").catch(() => ({ data: [] })),
      cg("rounding_cfg", "/rounding/config").catch(() => ({ data: null })),
    ]);
    const fromCacheAt = [p, c, m, pr].filter((x) => x.fromCache).map((x) => x.at);
    setCatVersion(p.version || "");
    setCacheInfo(fromCacheAt.length ? { fromCache: true, at: Math.min(...fromCacheAt) } : { fromCache: false, at: Date.now() });
    // Stock fresco (liviano) sobre el catálogo reutilizado
    const snap = await cachedGet("stock_snapshot", () => api.get("/inventory/snapshot"));
    const stockMap = snap.data?.stock || {};
    setProducts(
      p.data
        .filter((x) => x.is_active && x.visible_pos)
        .map((x) => (x.product_id in stockMap ? { ...x, stock: stockMap[x.product_id] } : x)),
    );
    setCategories(c.data); setMethods(m.data); setPromotions(pr.data);
    // El turno NUNCA se lee de caché (un turno ya cerrado no debe "revivir"):
    // con red manda el servidor; sin red solo vale el turno abierto localmente.
    try {
      // El turno es de la CAJA (terminal) y de su sucursal, no del usuario que entra con PIN
      const s = await api.get("/cash/current", { params: shiftParams() });
      await applyOfflineTotals(s.data);
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      await applyOfflineTotals(await getLocalShift());
    }
    setLoyaltyCfg(lc.data);
    setRoundCfg(rc.data);
    cachedGet("balance_cfg", () => api.get("/balance-labels/config")).then((r) => setBalanzaCfg(r.data || null)).catch(() => setBalanzaCfg(null));
    // Filtrar terminales tipo POS o PREVENTA de la tienda actual
    const posTerminals = (t.data || []).filter((x) => (x.tipo === "POS" || x.tipo === "PREVENTA") && x.is_active && x.store_id === store?.store_id);
    setTerminals(posTerminals);
    // Auto-selección: si el PC está pareado a un terminal, forzarlo; sino tomar el guardado o el primero
    if (pairedTerminalId && posTerminals.some((x) => x.terminal_id === pairedTerminalId)) {
      setTerminalId(pairedTerminalId);
    } else if (posTerminals.length && !posTerminals.some((x) => x.terminal_id === (localStorage.getItem("pos_terminal_id") || ""))) {
      setTerminalId(posTerminals[0].terminal_id);
    }
  }, [store?.store_id, pairedTerminalId, applyOfflineTotals]);
  useEffect(() => { load().catch(() => toast.error("No se pudieron cargar los datos del POS")); }, [load]);

  /** Sincronización en vivo del catálogo: cada minuto se consulta solo la huella (consulta
   *  mínima) y si cambió se baja el catálogo nuevo. El carrito en curso NO se toca: los
   *  productos ya agregados conservan su precio y lo nuevo rige desde la venta siguiente. */
  const sincronizarCatalogo = useCallback(async (manual = false) => {
    if (catSyncing) return;
    setCatSyncing(true);
    try {
      const v = (await api.get("/products/catalog-version")).data?.version || "";
      if (!manual && v === catVersion) return;
      const [full, snap, promos] = await Promise.all([
        api.get("/products?scope=branch"),
        api.get("/inventory/snapshot"),
        api.get("/promotions?canal=POS").catch(() => ({ data: null })),
      ]);
      const stockMap = snap.data?.stock || {};
      await cachePut("catalog_v2", { version: v, items: full.data });
      await cachePut("stock_snapshot", snap.data);
      setProducts(
        full.data
          .filter((x) => x.is_active && x.visible_pos)
          .map((x) => (x.product_id in stockMap ? { ...x, stock: stockMap[x.product_id] } : x)),
      );
      if (promos.data) { setPromotions(promos.data); await cachePut("promotions", promos.data); }
      setCacheInfo({ fromCache: false, at: Date.now() });
      if (v !== catVersion) {
        setCatVersion(v);
        toast.success("Catálogo actualizado", { duration: 2500 });
      } else if (manual) {
        toast.success("Catálogo al día", { duration: 2000 });
      }
    } catch (e) {
      if (manual) toast.error("No se pudo sincronizar el catálogo");
    } finally {
      setCatSyncing(false);
    }
  }, [catVersion, catSyncing]);

  useEffect(() => {
    const id = setInterval(() => { sincronizarCatalogo(false); }, 60000);
    return () => clearInterval(id);
  }, [sincronizarCatalogo]);
  // Auto-focus del textbox de código de barras. Usar refocusScan() después de cada operación.
  const refocusScan = useCallback(() => {
    setTimeout(() => {
      // Nunca robar el foco mientras hay un diálogo abierto (PIN, cobro, autorización…):
      // si no, lo que teclea el cajero cae en el buscador y sale "producto no registrado".
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      searchRef.current?.focus();
    }, 30);
  }, []);
  // Devuelve el foco cuando se cierran modales de cobro/tickets/etc
  useEffect(() => {
    if (operator && !payOpen && !openOpen && !closeOpen && !movOpen && !preOpen && !ticketOpen && !clearConfirmOpen && !suspendedOpen && !weightModal && !suspendTicketOpen && !unknownCode && !reprintTicketOpen && !returnModal && !printingSale && !ageOpen && !avisosOpen) {
      refocusScan();
      // Seguro anti-pantalla congelada: si un modal dejó el body bloqueado, lo liberamos
      setTimeout(() => {
        try {
          if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = "";
          document.body.removeAttribute("data-scroll-locked");
        } catch (_) {}
      }, 400);
    }
  }, [payOpen, openOpen, closeOpen, movOpen, preOpen, ticketOpen, clearConfirmOpen, suspendedOpen, weightModal, suspendTicketOpen, unknownCode, reprintTicketOpen, returnModal, printingSale, ageOpen, operator, refocusScan, avisosOpen]);

  useEffect(() => {
    if (!terminalId) return;
    const cambio = localStorage.getItem("pos_terminal_id") !== terminalId;
    localStorage.setItem("pos_terminal_id", terminalId);
    // Al cambiar de caja se trae el turno de ESA caja
    if (cambio && navigator.onLine) {
      api.get("/cash/current", { params: shiftParams() }).then((r) => applyOfflineTotals(r.data)).catch(() => {});
    }
  }, [terminalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==== OFFLINE MODE ====
  const refreshPendingCount = useCallback(async () => {
    const list = await listPending();
    setPendingList(list);
    setPendingCount(list.filter((x) => x.status !== "SYNCED").length);
    // Reintentables automáticamente: lo que nunca se envió o falló pocas veces (evita loops con errores del servidor)
    setPendingRetryable(list.filter((x) => x.status !== "SYNCED" && (x.status !== "FAILED" || (x.tries || 0) < 3)).length);
    setOfflineUnits(await queuedUnits());
  }, []);

  const syncPending = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const r = await flushQueue();
      if (!silent && r.synced) toast.success(`${r.synced} operación(es) sincronizada(s)`);
      if (r.failed && !silent) toast.error(`${r.failed} operación(es) con error`);
      await refreshPendingCount();
      if (r.synced > 0) {
        if (r.remaining === 0) await setLocalShift(null);   // el turno real toma el control
        load();
      }
    } finally { setSyncing(false); }
  }, [refreshPendingCount, load]);

  // Latido de conectividad real: navigator.onLine miente (WiFi sin Internet, caída del servidor).
  // Cada 10 s se consulta /health; al recuperar la conexión se sincroniza la cola sin recargar el POS.
  const syncRef = useRef({ syncing: false, pending: 0, online: true });
  useEffect(() => { syncRef.current = { syncing, pending: pendingRetryable, online: isOnline }; }, [syncing, pendingRetryable, isOnline]);

  useEffect(() => {
    let dead = false;
    const probe = async () => {
      let up = false;
      try {
        await publicApi.get("/health", { timeout: 7000 });
        up = true;
      } catch (e) {
        up = !!e?.response;   // el servidor respondió (aunque sea 500): hay red
      }
      if (dead) return;
      const prev = syncRef.current.online;
      setIsOnline(up);
      if (up && syncRef.current.pending > 0 && !syncRef.current.syncing) {
        syncPending(prev);   // primer aviso visible al recuperar la conexión
      }
    };
    const on = () => probe();
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    refreshPendingCount();
    probe();
    const id = setInterval(probe, 10000);
    return () => {
      dead = true;
      clearInterval(id);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==== VENTAS SUSPENDIDAS ====
  const loadSuspended = useCallback(async () => {
    try {
      const r = await api.get("/suspended-sales");
      setSuspendedList(r.data || []);
      setSuspendedCount((r.data || []).length);
    } catch (_) { /* silencioso */ }
  }, []);
  useEffect(() => { loadSuspended(); }, [loadSuspended]);

  const suspendCurrentSale = async () => {
    if (negar("suspender")) return;
    if (!cart.length) return;
    try {
      const r = await api.post("/suspended-sales", {
        store_id: store.store_id,
        terminal_id: terminalId || undefined,
        items: cart.map((x) => ({
          product_id: x.product_id, sku: x.sku, nombre: x.nombre,
          cantidad: x.cantidad, precio_unitario: x.precio, ...(x.mods_sel ? { modificadores: x.mods_sel } : {}),
        })),
        cliente_rut: normalizeRut(custRut) || undefined,
        cliente_nombre: custName?.trim() || undefined,
      });
      setLastSuspended(r.data);
      // Se imprime igual que un vale de venta: directo a la impresora del terminal
      let printed = false;
      if (agentOk) {
        const t = toast.loading("Imprimiendo vale suspendida…");
        try {
          await agentPrintDocEscpos(suspendedToReceipt(
            { codigo: r.data.codigo, items: r.data.items, total: r.data.total, created_at: r.data.created_at },
            { company_name: company?.nombre_comercial || "Ai-POS", store_name: store?.nombre || "",
              caja: terminal?.nombre, user_name: operator?.name || user?.name,
              feedBeforeCut: terminal?.cut_feed_lines, width: cols80 }));
          printed = true;
          toast.success("Vale impreso", { id: t });
        } catch (e) { toast.error(usbMode ? (e?.message || "No se pudo imprimir (impresora USB)") : "No se pudo imprimir (agente)", { id: t }); }
      } else if (printerMode === "OS_80MM") {
        await printHtml(suspendedTicketHtml(r.data, {
          company_name: company?.nombre_comercial, store_name: store?.nombre,
          logo_url: ticketLogo, footer_message: store?.ticket_footer,
          cashier: operator?.name || user?.name,
          barcode: barcodeDataUrl(r.data.codigo, preventaBarcodeType),
        }), `Suspendida ${r.data.codigo}`);
        printed = true;
      } else if (printerMode === "BT_58MM") {
        printed = await autoPrintSuspendedBt(r.data);
      }
      if (!printed) setSuspendTicketOpen(true);
      setCart([]); setLastProduct(null); resetCustomer();
      loadSuspended();
      toast.success(`Venta suspendida ${r.data.codigo}${printed ? " · impresa" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al suspender"); }
  };

  // Cotización → venta con un clic: /pos?cotizacion=ID carga el carrito y el cliente
  useEffect(() => {
    const cid = new URLSearchParams(window.location.search).get("cotizacion");
    if (!cid) return;
    setQuoteLoading(true);
    (async () => {
      try {
        const { data: q } = await api.get(`/quotes/${cid}`);
        setCart(q.items.map((it) => ({
          product_id: it.product_id, sku: it.sku, nombre: it.nombre,
          cantidad: it.cantidad, precio: it.precio_unitario,
        })));
        if (q.cliente_rut) setCustRut(formatRut(q.cliente_rut));
        if (q.cliente_nombre) setCustName(q.cliente_nombre);
        setQuoteId(q.quote_id);
        setQuoteNumero(q.numero);
        toast.success(`Cotización ${q.numero} cargada · cobra para convertirla en venta`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar la cotización");
      }
      setQuoteLoading(false);
      window.history.replaceState({}, "", "/pos");
    })();
    /* eslint-disable-next-line */
  }, []);

  const consumeSuspended = async (codigo) => {    try {
      const r = await api.post(`/suspended-sales/consume/${encodeURIComponent(codigo)}`);
      const d = r.data;
      // Cargar items al carrito (reemplaza el actual)
      setCart(d.items.map((it) => ({
        product_id: it.product_id, sku: it.sku, nombre: it.nombre,
        cantidad: it.cantidad, precio: it.precio_unitario,
      })));
      if (d.cliente_rut) setCustRut(formatRut(d.cliente_rut));
      if (d.cliente_nombre) setCustName(d.cliente_nombre);
      setLastProduct({ nombre: `♻ Venta ${d.codigo} recuperada`, precio: d.total, cantidad: d.items.length, sku: d.codigo });
      setSuspendedOpen(false);
      loadSuspended();
      toast.success(`Venta ${d.codigo} recuperada · vale quemado`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vale no encontrado"); }
  };

  const deleteSuspended = async (sid) => {
    try {
      await api.delete(`/suspended-sales/${sid}`);
      loadSuspended();
    } catch (_) { }
  };

  const openWeightModal = (product) => {
    setWeightModal({ product });
    setWeightInput("");
  };
  const confirmWeight = () => {
    const w = parseFloat(weightInput);
    if (!w || w <= 0) { toast.error("Peso inválido"); return; }
    const rounded = Math.round(w * 1000) / 1000; // hasta 3 decimales
    addToCart(weightModal.product, rounded);
    setWeightModal(null); setWeightInput("");
  };

  // Reimprimir última venta de este terminal
  const openReprintLast = async () => {
    if (negar("reimprimir")) return;
    try {
      const params = terminalId ? `?terminal_id=${encodeURIComponent(terminalId)}` : "";
      const r = await api.get(`/sales/last${params}`);
      setReprintSale(r.data);
      setReprintTicketOpen(true);
      logTerminalEvent("REIMPRESION", { sale_id: r.data?.sale_id, numero: r.data?.numero });
    } catch (e) { toast.error(e?.response?.data?.detail || "Sin ventas previas"); }
  };
  const printReprint = () => {
    if (agentOk && reprintSale) {
      (async () => {
        const t = toast.loading("Reimprimiendo…");
        try { await autoPrintSaleAgent(reprintSale, { skipDrawer: true }); toast.success("Reimpreso", { id: t }); }
        catch (e) { toast.error(usbMode ? (e?.message || "No se pudo reimprimir (impresora USB)") : "No se pudo reimprimir (agente)", { id: t }); }
      })();
      return;
    }
    if (printerMode === "OS_80MM" && reprintSale) {
      (async () => {
        const qr = await qrDataUrl(receiptUrl(reprintSale.sale_id));
        printHtml(saleTicketHtml(reprintSale, company?.nombre_comercial, store?.nombre, ticketLogo, store?.ticket_footer,
          { cashier: reprintSale.operator_name || operator?.name, caja: terminal?.nombre, qr, gracias_message: graciasMsg, ...salePayCtx(reprintSale) }), `Vale ${reprintSale?.numero || ""}`);
      })();
      return;
    }
    window.print();
  };

  // Devolución de la última venta
  const openReturnFromLast = async () => {
    try {
      const params = terminalId ? `?terminal_id=${encodeURIComponent(terminalId)}` : "";
      const r = await api.get(`/sales/last${params}`);
      const sale = r.data;
      if (sale.returned) { toast.error("Esta venta ya fue devuelta"); return; }
      const already = sale.returned_qty || {};
      setReturnModal({ sale, already });
      const seed = {};
      sale.items.forEach((it) => { seed[it.product_id] = 0; });
      setReturnQty(seed);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sin ventas previas"); }
  };
  const confirmReturn = async () => {
    if (!returnModal) return;
    const items = Object.entries(returnQty).filter(([, q]) => q > 0).map(([product_id, cantidad]) => ({ product_id, cantidad }));
    if (!items.length) { toast.error("Selecciona al menos un ítem"); return; }
    try {
      const r = await api.post(`/sales/${returnModal.sale.sale_id}/return`, {
        sale_id: returnModal.sale.sale_id,
        items,
        motivo: "Devolución cajero",
        terminal_id: terminalId || undefined,
      });
      toast.success(`Nota crédito ${r.data.return_id} por ${clp(r.data.total)}`);
      setReturnModal(null); setReturnQty({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // Cierre parametrizado
  const [cuadratura, setCuadratura] = useState(null);
  const openCloseModal = async () => {
    if (negar("cierre_caja")) return;
    // Turno abierto offline (o sin red): los totales salen de la cola local
    const local = (!isOnline || shift?.offline) ? await shiftSummary(shift?.shift_id) : null;
    const totales = local ? {
      efectivo: local.efectivo,
      esperado: (shift.fondo_inicial || 0) + local.efectivo + local.ingresos - local.retiros,
      ingresos: local.ingresos, retiros: local.retiros, fondo: shift.fondo_inicial || 0,
    } : {
      efectivo: shift.ventas_efectivo || 0,
      esperado: shift.fondo_inicial + shift.ventas_efectivo + shift.ingresos - shift.retiros,
      ingresos: shift.ingresos || 0, retiros: shift.retiros || 0,
      fondo: shift.fondo_inicial || 0,
    };
    setClosePreview(totales);
    setCuadratura(null);
    setArqueo({});
    setCloseOpen(true);
    try {
      const r = await api.get(`/cash/shifts/${shift.shift_id}/cuadratura`);
      setCuadratura(r.data);
      setClosePreview({ ...totales, esperado: r.data.efectivo_esperado });
    } catch (_) { /* se queda con el cálculo local */ }
  };
  // Denominaciones CLP (billetes + monedas)
  const DENOMS_ARQUEO = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10];
  const arqueoTotal = DENOMS_ARQUEO.reduce((s, d) => s + (parseInt(arqueo[d] || 0, 10) * d), 0);

  // Índices del catálogo (se arman una vez por catálogo): el escaneo busca en O(1) y el filtro
  // por texto no re-normaliza 25.000 nombres con cada dígito que tipea el lector.
  const { codeIndex, skuIndex, searchKeys } = useMemo(() => {
    const codeIndex = new Map(), skuIndex = new Map();
    for (const p of products) if (p.barcode && !codeIndex.has(String(p.barcode))) codeIndex.set(String(p.barcode), p);
    for (const p of products) if (p.sku && !skuIndex.has(String(p.sku))) skuIndex.set(String(p.sku), p);
    return { codeIndex, skuIndex, searchKeys: products.map((p) => `${plano(p.nombre)}\u0001${plano(p.sku)}\u0001${p.barcode || ""}`) };
  }, [products]);
  const buscarCodigo = (code) => codeIndex.get(code) || skuIndex.get(code) || skuIndex.get(code.replace(/^0+/, ""));
  const hayTexto = (txt) => { const k = plano(txt); return searchKeys.some((x) => x.includes(k)); };
  const qFiltro = useDeferredValue(q);
  const filtered = useMemo(() => {
    const k = plano(qFiltro);
    return products.filter((p, i) =>
      p.sku !== "VENTA-RAPIDA" && (cat === "ALL" || p.category_id === cat) && (!k || searchKeys[i].includes(k)));
  }, [products, searchKeys, cat, qFiltro]);

  // Catálogos grandes (25.000 SKU): la grilla dibuja por tramos. Dibujar todo congelaba el POS.
  const PAGINA_GRID = 120;
  const [visibles, setVisibles] = useState(PAGINA_GRID);
  useEffect(() => { setVisibles(PAGINA_GRID); }, [cat, q]);

  // Sin conexión (o con catálogo leído del equipo) el stock es estimado: descontamos
  // las unidades que ya se vendieron offline y aún están en la cola.
  const stockApprox = !isOnline || cacheInfo.fromCache;
  const stockOf = (p) =>
    Math.round((((p.stock ?? 0) - (offlineUnits[p.product_id] || 0)) + Number.EPSILON) * 1000) / 1000;


  const [modsPick, setModsPick] = useState(null);
  const addToCart = useCallback((p, qty = 1, precio = null) => {
    // Local de comidas: si el producto tiene opciones, primero se eligen (quitar tomate, agregar mayo…)
    if (p.modificadores?.length && !p._mods) { setModsPick({ p, qty, precio }); return; }
    setCart((c) => {
      // Precio por cantidad: si el producto tiene tramos, la línea toma el precio del tramo alcanzado
      const base = (precio ?? p.precio) + (p._mods?.extra || 0);
      const tiers = precio == null && !p._mods ? normalizeTiers(p.precios_cantidad) : [];
      const modsKey = p._mods ? JSON.stringify(p._mods.sel) : "";
      const ex = c.find((x) => x.product_id === p.product_id && (x.precio_base ?? x.precio) === base && (x.modsKey || "") === modsKey);
      if (ex) {
        const nuevaCant = +(ex.cantidad + qty).toFixed(3);
        return c.map((x) => x === ex
          ? { ...x, cantidad: nuevaCant, precio: tierPrice(base, x.tiers || tiers, nuevaCant) }
          : x);
      }
      if (c.length >= MAX_LINEAS_DTE) {
        toast.error(AVISO_LIMITE_LINEAS, { id: "limite-lineas", duration: 8000 });
        return c;
      }
      return [...c, {
        product_id: p.product_id, nombre: p.nombre, sku: p.sku,
        category_id: p.category_id,
        precio: tierPrice(base, tiers, qty), precio_base: base, tiers,
        cantidad: qty, es_pesable: !!p.es_pesable,
        detalle: p.detalle || null,
        ...(p._mods ? { modsKey, mods_sel: p._mods.sel, modificadores: p._mods.txt } : {}),
        requiere_autorizacion: !!p.requiere_autorizacion,
        edad_minima: Number(p.edad_minima) || 18,
      }];
    });
    setLastProduct({ nombre: p.nombre, precio: precio ?? p.precio, cantidad: qty, sku: p.sku });
    setLastAddedProduct(p);
    // Control de vencimientos: aviso al cajero según el lote más próximo a vencer
    if (p.dias_vence != null) {
      if (p.dias_vence < 0) {
        setExpiryAlert({ nombre: p.nombre, fecha: p.vence_el, dias: p.dias_vence });
        toast.error(`${p.nombre} está VENCIDO (${p.vence_el}) · retíralo de la sala`, { duration: 6000 });
      } else if (p.dias_vence <= 7) {
        setExpiryAlert({ nombre: p.nombre, fecha: p.vence_el, dias: p.dias_vence });
        toast.warning(`${p.nombre} vence en ${p.dias_vence} día(s) (${p.vence_el})`, { duration: 4000 });
      }
    }
  }, []);

  // Al cambiar cantidades (teclado, +/-, escáner) la línea se recalcula al tramo que corresponda
  useEffect(() => {
    setCart((c) => {
      let cambio = false;
      const next = c.map((x) => {
        if (!x.tiers?.length) return x;
        const pr = tierPrice(x.precio_base ?? x.precio, x.tiers, x.cantidad);
        if (pr !== x.precio) { cambio = true; return { ...x, precio: pr }; }
        return x;
      });
      return cambio ? next : c;
    });
  }, [cart]);

  const applyPromotion = useCallback((promo) => {
    // Agrega todos los items del bundle. computePromotions detecta y aplica el descuento.
    const prodMap = Object.fromEntries(productsRef.current.map((p) => [p.product_id, p]));
    let ok = true;
    const preview = [];
    for (const it of (promo.items || [])) {
      const p = prodMap[it.product_id];
      if (!p) { ok = false; break; }
      preview.push({ p, qty: it.cantidad });
    }
    if (!ok) { toast.error("Un producto de la promoción no está disponible en esta tienda"); return; }
    preview.forEach(({ p, qty }) => addToCart(p, qty));
    setLastProduct({ nombre: `🎉 ${promo.nombre}`, precio: promo.precio_bundle, cantidad: preview.length, sku: "PROMO" });
    toast.success(`Promoción "${promo.nombre}" agregada`);
  }, [addToCart]);

  const removeUnitFromCart = useCallback((productId) => {
    setCart((c) => {
      const item = c.find((x) => x.product_id === productId);
      if (!item) return c;
      if (item.cantidad <= 1) return c.filter((x) => x.product_id !== productId);
      return c.map((x) => x.product_id === productId ? { ...x, cantidad: x.cantidad - 1 } : x);
    });
  }, []);

  const inc = (id, d) => setCart((c) => c.map((x) => x.product_id === id ? { ...x, cantidad: Math.max(1, x.cantidad + d) } : x));
  const rm = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  const rawSubtotal = cart.reduce((s, x) => s + Math.round(x.precio * x.cantidad), 0);
  const promoResult = useMemo(() => computePromotions(cart, promotions, "canal_pos"), [cart, promotions]);
  const total = Math.max(0, rawSubtotal - promoResult.savings);
  const iva = Math.round(total - total / 1.19);
  const neto = total - iva;
  // Cargar una preventa por código (para modo BOLETAS que consolida uno o varios tickets)
  const loadPresaleByCode = useCallback(async (code) => {
    if (presaleTickets.some((t) => t.codigo === code.toUpperCase())) {
      toast.info("Ese ticket ya está cargado");
      return;
    }
    try {
      const r = await api.get(`/presales/by-code/${encodeURIComponent(code.toUpperCase())}`);
      if (r.data.estado !== "PENDIENTE") {
        toast.error(`Ticket ya ${r.data.estado}`);
        return;
      }
      setPresaleTickets((arr) => [...arr, r.data]);
      // Agregar items al carrito
      for (const it of r.data.items) {
        setCart((c) => {
          const ex = c.find((x) => x.product_id === it.product_id && x.precio === it.precio_unitario);
          if (ex) return c.map((x) => x === ex ? { ...x, cantidad: +(x.cantidad + it.cantidad).toFixed(3), _fromPresale: true } : x);
          if (c.length >= MAX_LINEAS_DTE) {
            toast.error(AVISO_LIMITE_LINEAS, { id: "limite-lineas", duration: 8000 });
            return c;
          }
          return [...c, { product_id: it.product_id, nombre: it.nombre, sku: it.sku, precio: it.precio_unitario, cantidad: it.cantidad, _fromPresale: true }];
        });
      }
      setLastProduct({ nombre: `Ticket ${r.data.codigo} cargado`, precio: r.data.total, cantidad: r.data.items.length, sku: r.data.codigo });
      toast.success(`Ticket ${r.data.codigo} · ${clp(r.data.total)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ticket no encontrado");
    }
  }, [presaleTickets]);

  // Escaneo (Enter en el input) + arrow-key nav en modo retail
  const onBarcodeEnter = async (e) => {
    // Modo retail con búsqueda por descripción: navegación con flechas
    if (uiMode === "RETAIL" && q.trim().length >= 2 && filtered.length > 0 && !(e.key === "Enter" && buscarCodigo(q.trim()))) {
      const results = filtered.slice(0, RETAIL_MAX_RESULTADOS);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setRetailSel((s) => Math.min(s + 1, results.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setRetailSel((s) => Math.max(s - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const p = results[retailSel];
        if (p) {
          if (removeMode) {
            // Modo remover: el producto escaneado sale de la lista (no se suma)
            if (cart.some((x) => x.product_id === p.product_id)) {
              removeUnitFromCart(p.product_id);
              setLastProduct({ nombre: `➖ ${p.nombre}`, precio: p.precio, cantidad: -1, sku: p.sku });
              setRemoveMode(false);
            } else {
              toast.error(`${p.nombre} NO está en el carrito. Modo remover sigue activo.`);
            }
          } else if (p.es_pesable) openWeightModal(p);
          else addWithPendingQty(p);
          setQ(""); setRetailSel(0);
          refocusScan();
        }
        return;
      }
    }
    if (e.key !== "Enter") return;
    const code = q.trim();
    // Búsqueda por texto sin resultados: avisar, no mostrar el cartel de código no registrado.
    // Excepción: los códigos de pre-venta (PV) y venta suspendida (SV) se resuelven más abajo.
    if (uiMode === "RETAIL" && code.length >= 2 && !buscarCodigo(code) && !hayTexto(code)
        && !/^\d{4,}$/.test(code) && !/^(PV|SV)[A-Z0-9]{3,}$/i.test(code)) {
      toast.error(`Sin coincidencias para «${code}»`, { duration: 2500 });
      return;
    }
    // Enter con textbox vacío → repetir el último producto marcado (configurable por terminal)
    if (!code) {
      if (enterRepiteUltimo && lastAddedProduct) {
        addToCart(lastAddedProduct, 1);
      }
      return;
    }
    // Atajo de cantidad: "3" + Enter → 3 unidades más del último producto marcado.
    // Solo con números cortos (1..999) que no sean el código ni el SKU de ningún producto.
    if (enterRepiteUltimo && lastAddedProduct && !removeMode && /^\d{1,3}$/.test(code)) {
      const n = parseInt(code, 10);
      const esCodigoDeProducto = codeIndex.has(code) || skuIndex.has(code);
      if (n >= 1 && !esCodigoDeProducto) {
        addToCart(lastAddedProduct, n);
        setQ("");
        toast.info(`${lastAddedProduct.nombre} ×${n}`, { duration: 1500 });
        refocusScan();
        return;
      }
    }
    // 1) Detectar preventa (prefijo PV) o venta suspendida (prefijo SV)
    if (/^PV[A-Z0-9]{4,}$/i.test(code)) {
      await loadPresaleByCode(code);
      setQ("");
      return;
    }
    if (/^SV[A-Z0-9]{4,}$/i.test(code)) {
      await consumeSuspended(code.toUpperCase());
      setQ("");
      return;
    }
    // 2) Etiqueta de balanza: con la configuración ya cargada se resuelve en el equipo, al instante
    try {
      let r;
      if (balanzaCfg !== undefined) {
        const b = balanzaCfg && parseBalanza(balanzaCfg, code);
        const bp = b && (skuIndex.get(b.sku) || skuIndex.get(b.sku.replace(/^0+/, "") || "0"));
        r = { data: !b ? { is_balance_label: false } : !bp ? { is_balance_label: true, found: false }
          : { is_balance_label: true, found: true, product: bp,
              cantidad: b.precio != null ? 1 : Math.round(b.peso * 1000) / 1000,
              precio_unitario: b.precio != null ? b.precio : Number(bp.precio) } };
      } else {
        r = await api.get(`/balance-labels/parse?code=${encodeURIComponent(code)}`);
      }
      if (r.data.is_balance_label && r.data.found) {
        const p = r.data.product;
        const cantidad = r.data.cantidad;
        const precioUnit = r.data.precio_unitario;
        // Modo remover: restar 1 y salir
        if (removeMode) {
          if (cart.some((x) => x.product_id === p.product_id)) {
            removeUnitFromCart(p.product_id);
            setLastProduct({ nombre: `➖ ${p.nombre}`, precio: precioUnit, cantidad: -1, sku: p.sku });
            setRemoveMode(false); // una operación y vuelve a modo venta normal
          } else {
            toast.error(`${p.nombre} NO está en el carrito. Modo remover sigue activo.`);
          }
          setQ("");
          return;
        }
        const effectiveQty = pendingQty !== null ? pendingQty : cantidad;
        addToCart(p, effectiveQty, precioUnit);
        setPendingQty(null);
        setQ("");
        return;
      }
      if (r.data.is_balance_label && !r.data.found) {
        setUnknownCode(code);
        setQ("");
        return;
      }
    } catch (_) { /* fallback */ }
    // 3) Búsqueda directa por barcode / SKU (índice en memoria)
    const found = buscarCodigo(code);
    if (found) {
      if (removeMode) {
        if (cart.some((x) => x.product_id === found.product_id)) {
          removeUnitFromCart(found.product_id);
          setLastProduct({ nombre: `➖ ${found.nombre}`, precio: found.precio, cantidad: -1, sku: found.sku });
          setRemoveMode(false);
        } else {
          toast.error(`${found.nombre} NO está en el carrito. Modo remover sigue activo.`);
        }
      } else {
        const eff = pendingQty !== null ? pendingQty : 1;
        if (found.es_pesable && pendingQty === null) {
          // Pesable sin qty preseteada → abre keypad
          openWeightModal(found);
          setQ("");
          return;
        }
        addToCart(found, eff);
      }
      setPendingQty(null);
      setQ("");
    } else {
      // Producto NO registrado — modal rojo bloqueante
      setUnknownCode(code);
      setQ("");
    }
  };

  // Atajos de teclado globales
  useEffect(() => {
    const onKey = (e) => {
      // Modal producto no registrado: solo ESC dismiss
      if (unknownCode) {
        if (e.key === "Escape") { e.preventDefault(); setUnknownCode(null); searchRef.current?.focus(); }
        else { e.preventDefault(); }
        return;
      }
      // Movimiento de caja abierto: ← Ingreso / → Retiro, y el foco vuelve siempre al Monto
      if (movOpen && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        e.preventDefault();
        const t = e.key === "ArrowLeft" ? "INGRESO" : "RETIRO";
        if (puede(t === "INGRESO" ? "ingreso" : "retiro")) setMov((m) => ({ ...m, tipo: t }));
        setTimeout(() => {
          try { movMontoRef.current?.focus(); movMontoRef.current?.select(); } catch (_) { /* noop */ }
        }, 10);
        return;
      }
      // Ignorar si un modal está abierto o si todavía no se identificó el cajero (pantalla de PIN)
      if (!operator || payOpen || openOpen || closeOpen || movOpen || preOpen || clearConfirmOpen || suspendedOpen || weightModal || suspendTicketOpen || ageOpen || avisosOpen) return;
      // F1: venta rápida por monto
      if (e.key === "F1") {
        e.preventDefault();
        if (!shift || isPreventaTerminal || negar("venta_rapida")) return;
        setQuickMonto(""); setQuickOpen(true);
        return;
      }
      // F2: ingreso / retiro de efectivo (abre en Retiro, igual que el botón)
      if (e.key === "F2") {
        e.preventDefault();
        if (!shift) { toast.error("Abre la caja primero"); return; }
        abrirMov();
        return;
      }
      // F3: suspender la venta en curso
      if (e.key === "F3") {
        e.preventDefault();
        if (!cart.length) { toast.error("No hay ítems para suspender"); return; }
        suspendCurrentSale();
        return;
      }
      // F8: cerrar la sesión del cajero
      if (e.key === "F8") {
        e.preventDefault();
        clearOperator(); toast.info("Sesión del cajero cerrada");
        return;
      }
      // F10: cerrar caja
      if (e.key === "F10") {
        e.preventDefault();
        if (!shift) { toast.error("No hay caja abierta"); return; }
        openCloseModal();
        return;
      }
      // F9: usar el contenido del textbox como cantidad (1..1000)
      if (e.key === "F9") {
        e.preventDefault();
        if (negar("cantidad")) return;
        const n = parseFloat(q);
        if (!(n >= 1 && n <= 1000)) {
          toast.error("Cantidad inválida (debe ser entre 1 y 1000)");
          return;
        }
        setPendingQty(n);
        setQ("");
        toast.info(`Próximo producto ×${n}`, { duration: 2000 });
        refocusScan();
        return;
      }
      // FIN: cobrar
      if (e.key === "End") {
        e.preventDefault();
        if (cart.length && shift) openPayModal();
        return;
      }
      // SUPR (Delete): limpiar
      if (e.key === "Delete") {
        e.preventDefault();
        if (!cart.length || negar("limpiar")) return;
        if (confirmClearCart) setClearConfirmOpen(true);
        else clearAll();
        return;
      }
      // REPAG (PageUp): modo remover
      if (e.key === "PageUp") {
        e.preventDefault();
        if (negar("remover")) return;
        setRemoveMode((v) => {
          const nv = !v;
          if (nv) {
            setLastProduct({ nombre: "🚫 ESCANEAR PRODUCTO A QUITAR", precio: 0, cantidad: 0, sku: "REMOVER" });
            toast.info("Modo remover activo: escanea el producto a quitar", { duration: 2500 });
          } else {
            setLastProduct(null);
          }
          setTimeout(refocusScan, 50);
          return nv;
        });
        return;
      }
      // ENTER: repite el último producto marcado (parámetro del terminal). Solo cuando el foco
      // no está en un campo de texto ni hay diálogos abiertos: el Enter del buscador ya lo maneja.
      if (e.key === "Enter" && enterRepiteUltimo && document.activeElement !== searchRef.current) {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        const editable = tag === "input" || tag === "textarea" || tag === "select" || tag === "button"
          || document.activeElement?.isContentEditable;
        const modalAbierto = payOpen || openOpen || closeOpen || movOpen || preOpen
          || clearConfirmOpen || suspendedOpen || weightModal || suspendTicketOpen;
        if (!editable && !modalAbierto && lastAddedProduct) {
          e.preventDefault();
          addToCart(lastAddedProduct, 1);
          refocusScan();
        }
        return;
      }
      // Solo dígitos y punto para el buffer si el foco está en searchRef
      if (document.activeElement === searchRef.current) return;
      if (e.key === "Escape") {
        setPendingQty(null); setRemoveMode(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, shift, q, qtyBuffer, removeMode, payOpen, openOpen, closeOpen, movOpen, preOpen, clearConfirmOpen, unknownCode, suspendedOpen, weightModal, suspendTicketOpen, enterRepiteUltimo, lastAddedProduct, confirmClearCart, operator, ageOpen]);

  // Respeta la cantidad pendiente (tecla F9 o botón Cantidad) también al tocar en pantalla
  const addWithPendingQty = (p, precio = null) => {
    // Modo remover activo: el producto se quita de la lista en vez de sumarse
    if (removeMode) {
      if (cart.some((x) => x.product_id === p.product_id)) {
        removeUnitFromCart(p.product_id);
        setLastProduct({ nombre: `➖ ${p.nombre}`, precio: p.precio, cantidad: -1, sku: p.sku });
        setRemoveMode(false);
      } else {
        toast.error(`${p.nombre} NO está en el carrito. Modo remover sigue activo.`);
      }
      return;
    }
    const qty = pendingQty !== null ? pendingQty : 1;
    addToCart(p, qty, precio);
    setPendingQty(null);
  };

  const clearAll = () => { setCart([]); setPresaleTickets([]); setLastProduct(null); setPendingQty(null); setQtyBuffer(""); setQuoteId(null); setQuoteNumero(null); };

  const applyQtyInput = () => {
    const n = parseFloat(qtyInput);
    if (!(n >= 1 && n <= 1000)) { toast.error("Cantidad inválida (debe ser entre 1 y 1000)"); return; }
    setPendingQty(n);
    setQtyOpen(false);
    toast.info(`Próximo producto ×${n}`, { duration: 2000 });
    refocusScan();
  };

  // Venta rápida: pide el monto y lo agrega al carrito como ítem "Venta Rápida"
  // Vale de cambio: se busca por código y se aplica como descuento de esta venta
  const buscarVale = async () => {
    setValeBusy(true);
    try {
      const r = await api.get(`/exchange-tickets/by-code/${valeCodigo.trim()}`);
      if (r.data.estado !== "VIGENTE") {
        toast.error(`El vale ya está ${String(r.data.estado).toLowerCase()}`);
        return;
      }
      setVale(r.data);
      setValeOpen(false); setValeCodigo("");
      toast.success(`Vale #${r.data.codigo} aplicado · ${clp(r.data.total)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se encontró el vale");
    } finally { setValeBusy(false); }
  };

  // Cotizaciones: buscar por número, RUT o nombre y cargarlas al carro
  const buscarCotizaciones = async () => {
    setCotBusy(true);
    try {
      const r = await api.get(`/quotes?q=${encodeURIComponent(cotQ.trim())}`);
      const rows = (r.data?.quotes || []).filter((x) => x.estado !== "CONVERTIDA");
      setCotRows(rows);
      if (rows.length === 0) toast.info("Sin cotizaciones vigentes con ese dato");
    } catch (e) {
      toast.error("No se pudo buscar cotizaciones");
    } finally { setCotBusy(false); }
  };

  const cargarCotizacion = async (c) => {
    setCotOpen(false);
    // Se carga en la misma pantalla (sin recargar el POS) para que sea instantáneo
    setQuoteLoading(true);
    try {
      const { data: q } = await api.get(`/quotes/${c.quote_id}`);
      setCart(q.items.map((it) => ({
        product_id: it.product_id, sku: it.sku, nombre: it.nombre,
        cantidad: it.cantidad, precio: it.precio_unitario,
      })));
      if (q.cliente_rut) setCustRut(formatRut(q.cliente_rut));
      if (q.cliente_nombre) setCustName(q.cliente_nombre);
      setQuoteId(q.quote_id);
      setQuoteNumero(q.numero);
      toast.success(`Cotización ${q.numero} cargada · cobra para convertirla en venta`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la cotización");
    }
    setQuoteLoading(false);
    refocusScan();
  };

  const addQuickSaleItem = async () => {
    const monto = Math.round(Number(quickMonto) || 0);
    if (monto <= 0) { toast.error("Escribe un monto mayor a cero"); return; }
    // Se pide un detalle opcional para que el jefe de tienda sepa qué se cobró así
    setQuickOpen(false);
    setQuickDetalle("");
    setQuickDetalleOpen(monto);
  };

  const confirmarQuickSale = async (detalle) => {
    const monto = Number(quickDetalleOpen) || 0;
    try {
      // El producto interno "Venta Rápida" se guarda en el equipo: así funciona sin conexión
      const { data: p } = await cachedGet("quick_item", () => api.post("/pos/quick-item"));
      addToCart({ ...p, detalle: (detalle || "").trim() || null }, 1, monto);
      setQuickDetalleOpen(null); setQuickMonto(""); setQuickDetalle("");
      refocusScan();
    } catch (e) {
      toast.error(isNetworkError(e)
        ? "Venta rápida no disponible sin conexión en este equipo: úsala una vez con internet para habilitarla"
        : (e?.response?.data?.detail || "No se pudo agregar la venta rápida"));
    }
  };

  // Operador del turno (PIN obligatorio al abrir el POS y después de cada cierre de caja)
  const [pinValue, setPinValue] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState("");
  const [qtyOpen, setQtyOpen] = useState(false);
  const [qtyInput, setQtyInput] = useState("");
  const movMotivoRef = useRef(null);

  // Avisos de administración para el cajero (se confirman leídos desde la caja)
  const cargarAvisos = React.useCallback(async () => {
    if (!operator?.user_id || !isOnline) return;
    try {
      const { data } = await api.get("/pos/avisos", {
        params: { terminal_id: terminalId || undefined, store_id: store?.store_id || undefined, operator_user_id: operator.user_id },
      });
      setAvisos(data);
    } catch (_) { /* sin avisos si falla la consulta */ }
  }, [operator?.user_id, terminalId, store?.store_id, isOnline]);
  useEffect(() => {
    if (!operator?.user_id) { setAvisos([]); setAvisosOpen(false); return; }
    cargarAvisos();
    const t = setInterval(cargarAvisos, 60000);
    return () => clearInterval(t);
  }, [operator?.user_id, cargarAvisos]);
  // Los urgentes se abren apenas llegan (y suenan); los normales esperan a que termine la venta
  const hayAvisoUrgente = avisos.some((a) => a.prioridad === "URGENTE");
  const ventaEnCurso = cart.length > 0 || payOpen || printingSale;
  const sonarAlerta = React.useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      // Tres pitidos cortos: se escuchan sobre el ruido de la tienda sin ser molestos
      [0, 0.45, 0.9].forEach((t) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.3);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + t);
        osc.stop(ctx.currentTime + t + 0.32);
      });
      setTimeout(() => ctx.close(), 1600);
    } catch (_) { /* si el navegador bloquea el audio, igual se ve el aviso en rojo */ }
  }, []);
  const avisoSonadoRef = React.useRef("");
  useEffect(() => {
    if (!avisos.length || avisosOpen) return;
    if (hayAvisoUrgente || !ventaEnCurso) setAvisosOpen(true);
  }, [avisos, ventaEnCurso]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const urgente = avisos.find((a) => a.prioridad === "URGENTE");
    if (!urgente || avisoSonadoRef.current === urgente.aviso_id) return;
    avisoSonadoRef.current = urgente.aviso_id;
    sonarAlerta();
  }, [avisos, sonarAlerta]);
  const confirmarAviso = async (aviso) => {
    setAvisoBusy(aviso.aviso_id);
    try {
      await api.post(`/pos/avisos/${aviso.aviso_id}/leer`, {
        terminal_id: terminalId || undefined, operator_user_id: operator?.user_id, operator_name: operator?.name,
      });
      const quedan = avisos.filter((a) => a.aviso_id !== aviso.aviso_id);
      setAvisos(quedan);
      if (!quedan.length) { setAvisosOpen(false); refocusScan(); }
    } catch (e) { toast.error("No se pudo confirmar el aviso"); }
    setAvisoBusy("");
  };

  const identifyOperator = async () => {
    setPinBusy(true);
    try {
      const { data } = await api.post("/pos-pin/identify", { pin: pinValue, terminal_id: terminalId || undefined });
      setOperator(data);
      sessionStorage.setItem("pos_operator", JSON.stringify(data));
      rememberOperatorPin(pinValue, data);   // habilita identificarse luego sin red
      setPinValue("");
      logTerminalEvent("LOGIN_CAJERO", { nombre: data.name, user_id: data.user_id });
      toast.success(`Turno de ${data.name}`);
      refocusScan();
    } catch (e) {
      // Sin conexión: validamos contra el hash del PIN guardado en este equipo
      if (isNetworkError(e)) {
        let local = await identifyOperatorOffline(pinValue);
        if (!local) {
          // Respaldo: el PIN con el que se inició sesión en este equipo también identifica al operador
          const dev = await getDeviceLoginOffline(pinValue);
          if (dev?.user) local = { user_id: dev.user.user_id, name: dev.user.name || dev.user.email, role: dev.user.role };
        }
        setPinValue("");
        if (local) {
          setOperator(local);
          sessionStorage.setItem("pos_operator", JSON.stringify(local));
          toast.success(`Turno de ${local.name} · modo offline`);
          refocusScan();
        } else {
          toast.error("Sin conexión: este PIN no está guardado en el equipo");
        }
      } else {
        setPinValue("");
        toast.error(e?.response?.data?.detail || "PIN incorrecto");
      }
    } finally { setPinBusy(false); }
  };

  const clearOperator = () => {
    sessionStorage.removeItem("pos_operator");
    setOperator(null);
    setPinValue("");
  };

  // Antes de abrir caja confirmamos el fondo inicial (evita errores de tipeo del cajero).
  const requestOpenFund = () => {
    if (fondo === "" || isNaN(Number(fondo)) || Number(fondo) < 0) {
      toast.error("Escribe el fondo inicial");
      try { fundInputRef.current?.focus(); } catch (_) {}
      return;
    }
    setConfirmFundOpen(true);
  };

  const openShift = async () => {
    if (negar("apertura_caja")) return;
    const payload = { store_id: store?.store_id, caja: terminal?.nombre || "Caja 1", fondo_inicial: Number(fondo), terminal_id: terminalId || undefined, operator_user_id: operator?.user_id, operator_name: operator?.name };
    let shiftDoc = null;
    try {
      const r = await api.post("/cash/open", payload);
      shiftDoc = r.data;
      await applyOfflineTotals(r.data);
      setOpenOpen(false); toast.success("Caja abierta");
      // Aviso preventivo al abrir caja: certificado digital por vencer
      try {
        const cert = await api.get("/sii/cert-aviso-caja", { silent402: true });
        if (cert.data?.aviso) {
          toast.error(
            cert.data.estado === "VENCIDO"
              ? `Certificado digital VENCIDO el ${cert.data.vence_el} · no podrás emitir boleta electrónica`
              : `Tu certificado digital vence el ${cert.data.vence_el} (${cert.data.dias_restantes} día(s)). Renuévalo con tu entidad certificadora.`,
            { duration: 15000 },
          );
        }
      } catch (_) { /* el aviso nunca bloquea la apertura */ }
    } catch (e) {
      if (!isNetworkError(e)) { toast.error(e?.response?.data?.detail || "Error"); return; }
      // Sin conexión: abrimos el turno en local y lo encolamos para sincronizar
      const tempId = `OFF-SH-${Date.now()}`;
      shiftDoc = {
        shift_id: tempId, store_id: store?.store_id, caja: payload.caja,
        fondo_inicial: Number(fondo), opened_at: new Date().toISOString(),
        estado: "ABIERTA", terminal_id: terminalId || null, offline: true,
        operator_name: operator?.name,
      };
      await enqueueOp("SHIFT_OPEN", "/cash/open", payload, { temp_shift_id: tempId });
      await setLocalShift(shiftDoc);
      await applyOfflineTotals(shiftDoc);
      setOpenOpen(false);
      refreshPendingCount();
      toast.warning("Sin conexión · caja abierta en modo offline", { duration: 4000 });
    }
    try {
      // Comprobante de apertura: fecha, hora, cajero y fondo inicial
      const ctx = {
        company_name: company?.nombre_comercial || "Ai-POS",
        store_name: store?.nombre || "", caja: terminal?.nombre || "Caja 1",
        user_name: operator?.name || user?.name, logo_url: ticketLogo,
        feedBeforeCut: terminal?.cut_feed_lines, width: 32,
      };
      const doc = { opened_at: shiftDoc?.opened_at || new Date().toISOString(), fondo_inicial: Number(fondo) || 0 };
      if (agentOk) {
        if (terminal?.cash_drawer_enabled) await openCashDrawer("APERTURA_CAJA");  // gaveta primero
        const t = toast.loading("Imprimiendo apertura…");
        try {
          await agentPrintDocEscpos(cashMovementToReceipt(
            { tipo: "APERTURA DE CAJA", monto: doc.fondo_inicial, motivo: "Fondo inicial", at: doc.opened_at },
            { ...ctx, width: cols80 }));
          toast.success("Apertura impresa", { id: t });
        } catch (e) { toast.error(usbMode ? (e?.message || "No se pudo imprimir (impresora USB)") : "No se pudo imprimir (agente)", { id: t }); }
      } else if (printerMode === "OS_80MM") {
        await printHtml(cashOpenHtml(doc, ctx), "Apertura de caja");
      } else if (btOk && btName) {
        try {
          await printReceipt(cashMovementToReceipt({ tipo: "APERTURA DE CAJA", monto: doc.fondo_inicial, motivo: "Fondo inicial", at: doc.opened_at }, ctx));
        } catch (_) { /* el cajero puede reimprimirlo desde Caja */ }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const tbkIntegrado = !!(terminal?.tbk_enabled ?? terminal?.payment_machine === "TBK_INTEGRATED");
  const mpHabilitado = !!(terminal?.mp_enabled ?? terminal?.payment_machine === "MP_POINT");
  const c2cHabilitado = !!(terminal?.c2c_enabled && terminal?.c2c_terminal_id);
  const pedirCierre = () => {
    setConfirmCloseOpen(true);
    if (!tbkIntegrado || !isOnline) { setTbkTot(null); return; }
    setTbkTot({ cargando: true });
    tbkTotales()
      .then((r) => setTbkTot(r.aprobado ? { ok: true, cantidad: r.cantidad, total: r.total } : { ok: false, mensaje: r.mensaje }))
      .catch((e) => setTbkTot({ ok: false, mensaje: e?.message || "El equipo Transbank no respondió" }));
  };
  const cierreTbk = async () => {
    if (!tbkIntegrado || !isOnline) return null;
    const base = { totales_ok: !!tbkTot?.ok, cantidad: tbkTot?.cantidad, total: tbkTot?.total };
    const t = toast.loading("Cierre Transbank en el equipo…");
    try {
      const r = await tbkCierre();
      if (r.aprobado) toast.success("Cierre Transbank realizado", { id: t });
      else toast.warning(`Cierre Transbank no realizado: ${r.mensaje}`, { id: t, duration: 6000 });
      return { ...base, cierre_ok: r.aprobado, mensaje: r.mensaje };
    } catch (e) {
      const msg = e?.message || "El equipo Transbank no respondió";
      toast.warning(`Cierre Transbank no realizado: ${msg}`, { id: t, duration: 6000 });
      return { ...base, cierre_ok: false, mensaje: msg };
    }
  };
  const closeShift = async () => {
    const contadoVal = closeMode === "ARQUEO" ? arqueoTotal : (closePreview?.esperado || 0);
    const payload = { efectivo_contado: Number(contadoVal), operator_user_id: operator?.user_id, operator_name: operator?.name, shift_id: shift?.shift_id };
    const tbkRes = await cierreTbk();
    if (tbkRes) payload.tbk_cierre = tbkRes;
    let cuad = null;
    try {
      const r = await api.post("/cash/close", payload);
      cuad = r.data.cuadratura;
      toast.success(closeMode === "ARQUEO" ? `Caja cerrada. Diferencia: ${clp(r.data.diferencia)}` : "Caja cerrada");
    } catch (e) {
      if (!isNetworkError(e)) { toast.error(e?.response?.data?.detail || "Error"); return; }
      // Sin conexión: cuadratura con los datos locales; el servidor la recalcula al sincronizar
      const sum = await shiftSummary(shift?.shift_id);
      const fondo0 = Number(shift?.fondo_inicial || 0);
      const esperado = fondo0 + sum.efectivo + sum.ingresos - sum.retiros;
      cuad = {
        caja: shift?.caja, fondo_inicial: fondo0, num_ventas: sum.num_ventas,
        opened_at: shift?.opened_at, closed_at: new Date().toISOString(),
        ventas_cobrado: sum.total, ventas_documento: sum.total,
        ingresos: sum.ingresos, retiros: sum.retiros,
        efectivo_esperado: esperado, esperado, redondeo_dia: 0,
        efectivo_contado: Number(contadoVal), diferencia: Number(contadoVal) - esperado,
        medios: sum.efectivo ? [{ codigo: "EFECTIVO", n: sum.num_ventas, monto: sum.efectivo }] : [],
        ventas_nulas: 0, monto_nulas: 0, ventas_simuladas: 0,
        offline: true,
      };
      await enqueueOp("SHIFT_CLOSE", "/cash/close", payload);
      await setLocalShift(null);
      refreshPendingCount();
      toast.warning("Sin conexión · cierre guardado para sincronizar", { duration: 5000 });
    }
    try {
      // Cierre simple: se abre la gaveta para retirar el dinero del turno
      if (closeMode !== "ARQUEO" && terminal?.cash_drawer_enabled) openCashDrawer("CIERRE_CAJA");
      // Comprobante de cierre por medio de pago
      if (cuad) {
        const detalle = closeMode === "ARQUEO"
          ? DENOMS_ARQUEO.filter((d) => parseInt(arqueo[d] || 0, 10) > 0)
              .map((d) => ({ denominacion: d, cantidad: parseInt(arqueo[d], 10), subtotal: parseInt(arqueo[d], 10) * d }))
          : [];
        const ctx = {
          company_name: company?.nombre_comercial || "Ai-POS",
          store_name: store?.nombre || "", user_name: operator?.name || user?.name,
          caja: terminal?.nombre, logo_url: ticketLogo, arqueo_detalle: detalle,
          feedBeforeCut: terminal?.cut_feed_lines, width: 32,
        };
        if (agentOk) {
          const t = toast.loading("Imprimiendo cierre…");
          try {
            await agentPrintDocEscpos(cashCloseToReceipt(cuad, { ...ctx, width: cols80 }));
            toast.success("Comprobante de cierre impreso", { id: t });
          } catch (e) { toast.error(usbMode ? (e?.message || "No se pudo imprimir (impresora USB)") : "No se pudo imprimir (agente)", { id: t }); }
        } else if (printerMode === "OS_80MM") {
          await printHtml(cashCloseHtml(cuad, ctx), "Cierre de caja");
        } else if (btOk && btName) {
          try {
            await printReceipt(cashCloseToReceipt(cuad, ctx));
            toast.success("Comprobante de cierre enviado a impresora BT");
          } catch (_) { /* el usuario puede imprimirlo desde Caja */ }
        }
        setCuadratura(cuad);
      }
      setShift(null); setCloseOpen(false);
      shiftRawRef.current = null;
      await setLocalShift(null);   // la caja quedó cerrada: no debe "revivir" al reabrir sin red
      // Nuevo turno = nuevo PIN (puede ser otro vendedor)
      clearOperator();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const saveMov = async () => {
    if (negar(mov.tipo === "INGRESO" ? "ingreso" : "retiro")) return;
    const monto = Number(mov.monto);
    if (!monto || monto <= 0) { toast.error("Escribe un monto mayor a cero"); return; }
    if (!mov.motivo || !mov.motivo.trim()) {
      toast.error("El motivo es obligatorio");
      try { movMotivoRef.current?.focus(); } catch (_) {}
      return;
    }
    const motivo = mov.motivo.trim();
    try {
      try {
        await api.post("/cash/movement", { shift_id: shift.shift_id, ...mov, motivo, monto, operator_user_id: operator?.user_id });
        const s = await api.get("/cash/current", { params: shiftParams() }); setShift(s.data);
      } catch (e) {
        if (!isNetworkError(e)) throw e;
        // Sin conexión: el movimiento queda en la cola y se imprime igual
        await enqueueOp("CASH_MOVEMENT", "/cash/movement", { shift_id: shift.shift_id, ...mov, motivo, monto, operator_user_id: operator?.user_id });
        await applyOfflineTotals();
        refreshPendingCount();
        toast.warning("Sin conexión · movimiento guardado para sincronizar", { duration: 4000 });
      }
      await imprimirComprobante({ tipo: mov.tipo, monto, motivo, at: new Date().toISOString() }, true);
      setMovOpen(false); setMov({ tipo: "RETIRO", monto: "", motivo: "" });
      toast.success("Movimiento registrado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const loyaltyActive = !!(loyaltyCfg?.enabled && loyaltyCfg?.canal_pos);
  // Canje de puntos como descuento (Doc 11 §9): nunca puede exceder el saldo ni el total
  const maxRedeem = Math.min(Number(custInfo?.balance || 0), Math.floor(total));
  const redeemValue = Math.min(Number(redeemAmount || 0), maxRedeem);
  // Redondeo legal chileno (Doc 06 §4.2): sólo afecta lo que el cliente paga en efectivo
  // Descuento manual del cajero: solo en cajas POS con la opción habilitada (no en pre-ventas)
  const puedeDescuentoManual = terminal?.tipo === "POS" && !!terminal?.permite_descuento_manual && presaleTickets.length === 0 && puede("descuento_manual");
  const descManualMonto = puedeDescuentoManual && !cupon ? montoDescuentoManual(descManual, total - (redeemValue || 0)) : 0;
  const puedeCupon = terminal?.tipo === "POS" && presaleTickets.length === 0;
  const cuponMonto = puedeCupon && cupon && !descManualMonto ? montoCupon(cupon, total - (redeemValue || 0)) : 0;
  // El vale de cambio se descuenta YA del total a cobrar (antes solo se veía al imprimir)
  const valeMonto = vale ? Math.min(Number(vale.total || 0), Math.max(0, total - (redeemValue || 0) - descManualMonto - cuponMonto)) : 0;
  const netTotalUi = Math.max(0, total - (redeemValue || 0) - descManualMonto - cuponMonto - valeMonto);
  const descTope = Number(terminal?.descuento_max_pct || 0);
  // Regla: con vale de cambio el cliente siempre paga una diferencia (mínimo $1)
  const avisoValeSinSaldo = () => toast.error("El vale cubre todo el total: agrega productos para que el cliente pague una diferencia (mínimo $1)", { duration: 6000 });
  // Una caja puede tener Transbank integrado y Mercado Pago Point a la vez: cada medio va a su equipo
  const esCodigoMp = (pm) => /MERCADO|^MP/i.test(pm || "");
  const esCodigoC2c = (pm) => /^TBK_SMART$|C2C/i.test(pm || "");
  const esTarjetaGenerica = (pm) => String(pm || "").toUpperCase() !== "TARJETA_PROPIA" && !esCodigoMp(pm) && !/TBK/i.test(pm || "") && /TARJ|DEB|CRED|CARD|REDCOMPRA/i.test(pm || "");
  const usaC2c = (pm) => esCodigoC2c(pm) || (c2cHabilitado && !tbkIntegrado && !mpHabilitado && esTarjetaGenerica(pm));
  const usaTbk = (pm) => tbkIntegrado && pm !== "EFECTIVO" && !esCodigoC2c(pm) && (/TBK/i.test(pm || "") || esTarjetaGenerica(pm));
  const usaMp = (pm) => esCodigoMp(pm) || (mpHabilitado && !tbkIntegrado && esTarjetaGenerica(pm));
  const esIntegrado = (pm) => usaTbk(pm) || usaMp(pm) || usaC2c(pm);
  const descExcedeTope = descTope > 0 && descManualMonto > 0
    && (descManualMonto * 100) / Math.max(1, total - (redeemValue || 0)) > descTope + 0.5;
  const autorizarDescuento = async (pin) => {
    try {
      const { data } = await api.post("/pos/descuento/autorizar", { pin });
      setDescPin({ pin, nombre: data.nombre });
      toast.success(`Descuento autorizado por ${data.nombre}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "PIN no válido"); }
  };
  const pagadoParcial = parciales.reduce((s, p) => s + Number(p.monto || 0), 0);
  const saldoUi = Math.max(0, Math.round((netTotalUi - pagadoParcial) * 100) / 100);
  const pagoBloqueado = parciales.some((p) => p.via);
  const roundInfo = resolveRounding(saldoUi, payMethod, roundCfg);
  const totalAPagar = roundInfo.total_pagar;
  const vuelto = isCashMethod(payMethod) ? Math.max(0, Number(recibido || 0) - totalAPagar) : 0;
  // Con descuento manual el "Recibido" sugerido sigue al nuevo saldo (si el cajero no lo tocó)
  useEffect(() => {
    if (payOpen && !recibidoTouched) setRecibido(totalAPagar);
  }, [descManualMonto, cuponMonto]); // eslint-disable-line react-hooks/exhaustive-deps
  const montoSugerido = (codigo, saldo = saldoUi) => resolveRounding(saldo, codigo, roundCfg).total_pagar;
  // Elegir un medio: los integrados cobran al instante; con un monto menor al saldo se abona
  const pickMethod = (m) => {
    if (!payAllowed(m.codigo)) {
      toast.error(`${m.nombre} se activa al contratar un plan`, {
        description: "Durante la prueba gratuita puedes cobrar con Efectivo, Transferencia y Tarjeta sin integración.",
        action: { label: "Ver planes", onClick: () => nav("/planes") }, duration: 7000,
      });
      return;
    }
    if (parciales.some((p) => p.codigo === m.codigo)) { toast.error(`${m.nombre} ya se usó en este pago: elige otro medio`); return; }
    if (m.codigo === "TARJETA_PROPIA") {
      if (parciales.length) { toast.error(`${m.nombre} paga el total de la venta: quita los abonos de otros medios`); return; }
      setPayMethod(m.codigo); setTbkError(null); setRecibidoTouched(false); setRecibido(montoSugerido(m.codigo));
      return;
    }
    setPayMethod(m.codigo);
    setTbkError(null);
    const abono = recibidoTouched && Number(recibido || 0) > 0 && Number(recibido || 0) < saldoUi;
    if (esIntegrado(m.codigo) || abono) { chargeRef.current({ methodOverride: m.codigo }); return; }
    if (!recibidoTouched) setRecibido(montoSugerido(m.codigo));
  };
  pickMethodRef.current = pickMethod;
  const quitarParcial = (i) => {
    if (parciales[i]?.via) return;
    const resto = parciales.filter((_, idx) => idx !== i);
    setParciales(resto);
    setRecibidoTouched(false);
    setRecibido(montoSugerido(payMethod, Math.max(0, netTotalUi - resto.reduce((s, p) => s + Number(p.monto || 0), 0))));
  };
  const cerrarCobro = (v) => {
    if (v) { setPayOpen(true); return; }
    if (pagoBloqueado) { toast.error("Hay un pago con tarjeta aprobado: termina de pagar el saldo pendiente"); return; }
    setParciales([]);
    setPayOpen(false);
  };
  const lookupCustomer = async (rutDirecto = null) => {
    const norm = normalizeRut(rutDirecto || custRut);
    if (!norm) { toast.error("RUT inválido"); return; }
    setCustLoading(true);
    try {
      const r = await api.get(`/loyalty/customer_by_rut?rut=${encodeURIComponent(norm)}`);
      setCustInfo(r.data);
      if (r.data?.found && r.data?.name && !custName) setCustName(r.data.name);
      if (!r.data?.found) toast.info("Cliente nuevo · se registrará al confirmar la venta");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setCustLoading(false); }
  };
  const resetCustomer = () => { setCustRut(""); setCustName(""); setCustInfo(null); setRedeemAmount(""); setCustNacimiento(null); };

  // Lector de cédula en el campo de fidelización
  const lectorCedula = useCedulaScan({
    onCedula: (ced) => {
      setCustRut(formatRut(ced.rut));
      setCustNacimiento(ced.nacimiento || null);
      toast.success(`Cédula leída · ${formatRut(ced.rut)}${ced.edad != null ? ` · ${ced.edad} años` : ""}`);
      lookupCustomer(ced.rut);
    },
    onError: () => {
      setCustRut("");
      toast.error("No se pudo leer la cédula. Escribe el RUT o vuelve a escanear");
    },
  });

  // Productos del carrito con venta restringida por edad (alcohol, cigarrillos)
  const itemsRestringidos = useMemo(
    () => cart.filter((x) => x.requiere_autorizacion),
    [cart],
  );
  const edadMinimaCarro = useMemo(
    () => Math.max(18, ...itemsRestringidos.map((x) => Number(x.edad_minima) || 18)),
    [itemsRestringidos],
  );
  // Si se vacía el carro o se quita el producto restringido, la autorización se cae
  useEffect(() => {
    if (itemsRestringidos.length === 0 && ageAuthRef.current) {
      ageAuthRef.current = null;
      setAgeAuth(null);
    }
  }, [itemsRestringidos.length]);

  const openPayModal = () => {
    if (isPreventaTerminal) { generatePresale(); return; }
    if (vale && netTotalUi < 1) { avisoValeSinSaldo(); return; }
    // Alcohol, cigarrillos: el cajero debe verificar la cédula antes de cobrar
    if (pedirAutorizacionEdad && itemsRestringidos.length > 0 && !ageAuthRef.current) { setAgeOpen(true); return; }
    // Precarga el monto exacto a pagar en efectivo (con redondeo legal ya aplicado):
    // así FIN + Enter cobra exacto con vuelto 0.
    const exacto = resolveRounding(Math.max(0, total - valeMonto), "EFECTIVO", roundCfg).total_pagar;
    setPayMethod("EFECTIVO"); setTbkError(null); setTpAuth(null); setRecibido(exacto); setRecibidoTouched(false); setReceiptEmail(""); setParciales([]); setPayOpen(true); resetCustomer();
    setFacturaData(CLIENTE_VACIO); setFacturaObs(""); setFacturaTransp(TRANSPORTE_VACIO); setFacturaRefs([]);
    // El documento lo decide el sistema (boleta afecta o exenta según los productos).
    // Solo se puede pedir Factura cuando el terminal la tiene habilitada.
    setPayDocType(terminal?.ticket_mode === "FACTURA" && terminal?.puede_emitir_factura ? "FACTURA" : "BOLETA");
  };

  // Boleta automática: el sistema decide afecta (39) o exenta (41) según los productos.
  // Solo se ofrece elegir Factura, cuando el terminal la tiene habilitada.
  const enabledDocs = useMemo(() => {
    if (!terminal) return [];
    const out = [];
    if (terminal.puede_emitir_factura) out.push({ key: "FACTURA", label: "Factura", hint: "Requiere RUT" });
    if (terminal.tipo === "POS" && terminal.permite_ticket && puede("ticket") && !presaleTickets.length) out.push({ key: "TICKET", label: "Ticket", hint: "Vale-QR sin DTE" });
    return out;
  }, [terminal, posOff, presaleTickets.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (payDocType === "TICKET" && !enabledDocs.some((d) => d.key === "TICKET")) setPayDocType("BOLETA");
  }, [enabledDocs, payDocType]);

  const generatePresale = async () => {
    try {
      const r = await api.post("/presales", {
        store_id: store.store_id,
        terminal_id: terminalId || undefined,
        items: cart.map((x) => ({ product_id: x.product_id, cantidad: x.cantidad, precio_unitario: x.precio, ...(x.mods_sel ? { modificadores: x.mods_sel } : {}) })),
        cliente_rut: normalizeRut(custRut) || undefined,
        cliente_nombre: custName?.trim() || undefined,
      });
      setLastPresale(r.data);
      setCart([]); resetCustomer();
      // Vale de pre-venta con código de barras. Solo PREVENTA puede imprimir más de una copia
      // (una para el cliente que va a pagar y otra que retiene el vendedor con los productos).
      const copies = isPreventaTerminal ? Math.max(1, Number(terminal?.preventa_copies || 1)) : 1;
      const barcode = barcodeDataUrl(r.data.codigo, preventaBarcodeType);
      const presHtml = presaleTicketHtml(r.data, {
        company_name: company?.nombre_comercial, store_name: store?.nombre,
        logo_url: ticketLogo, footer_message: store?.ticket_footer,
        cashier: operator?.name || user?.name, caja: terminal?.nombre, barcode,
      });
      let printed = false;
      for (let i = 0; i < copies; i++) {
        if (agentOk) {
          try {
            const png = await renderTicketPng(presHtml, `Pre-venta ${r.data.codigo}`);
            if (png) { await hwPrint([{ type: "raster", data_base64: png }]); printed = true; }
          } catch (_) { /* seguimos */ }
        } else if (printerMode === "OS_80MM") {
          await printHtml(presHtml, `Pre-venta ${r.data.codigo}`);
          printed = true;
        } else if (printerMode === "BT_58MM") {
          printed = (await autoPrintPresaleBt(r.data)) || printed;
        }
      }
      if (!printed) setPreOpen(true);
      toast.success(`Pre-venta ${r.data.codigo} creada${printed ? ` · ${copies} copia${copies > 1 ? "s" : ""}` : ""}`);
    } catch (e) {
      if (isNetworkError(e)) {
        toast.error("La pre-venta necesita conexión (el código lo emite el servidor). Cobra la venta directamente y se guardará offline.", { duration: 6000 });
      } else {
        toast.error(e?.response?.data?.detail || "Error");
      }
    }
  };

  // Libera bloqueos de UI que puede dejar un modal al cerrarse mientras imprimimos
  // (Radix deja pointer-events:none en el body si su animación de salida no termina).
  const releaseUiLocks = () => {
    try {
      if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = "";
      document.body.removeAttribute("data-scroll-locked");
    } catch (_) {}
  };

  // Cierre de venta común (venta normal y cobro de pre-ventas): pantalla de vuelto,
  // impresión directa por el agente / impresora del sistema / Bluetooth y gaveta.
  const finalizeSale = async (sale, { vuelto = null, isCash = false } = {}) => {
    setPrintingSale({ sale, vuelto, done: false });
    // Dejamos que React pinte el cierre del modal antes del trabajo pesado de impresión
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    releaseUiLocks();
    const modo = printerMode;
    const printedBt = modo === "BT_58MM" ? await autoPrintSaleBt(sale) : false;
    const printedAgent = (!printedBt && (modo === "OS_80MM" || modo === "USB_80MM") && agentOk) ? await autoPrintSaleAgent(sale) : false;
    if (!printedBt && !printedAgent) {
      if (modo === "OS_80MM") {
        // §3.2 · Vale directo a la impresora de 80 mm: sin vista previa y sin preguntar
        await printSaleOnOsPrinter(sale);
      } else if (sale._usbPendiente) {
        // Impresora USB no lista: el cajero la revisa y reimprime con «Reimprimir» (sin vista previa HTML)
        setPrintingSale(null);
      } else {
        setPrintingSale(null);
        setTicketOpen(true);
      }
    }
    setPrintingSale((p) => (p ? { ...p, done: true } : null));
    setTimeout(() => { setPrintingSale(null); releaseUiLocks(); }, 2500);
    // La gaveta vía agente ya fue disparada dentro de autoPrintSaleAgent
    if (!printedAgent && !sale._drawerOpened && !sale._drawerTried && isCash && terminal?.cash_drawer_enabled && terminal?.cash_drawer_auto_open !== false) {
      openCashDrawer("VENTA_EFECTIVO", sale.sale_id);
    }
    return { printedBt, printedAgent };
  };

  // Cobro en el equipo físico del medio elegido. false = rechazado (no se registra nada)
  const cobrarEnEquipo = async (metodo, monto) => {
    if (usaC2c(metodo)) {
      if (!c2cHabilitado) {
        const msg = "Transbank C2C aún no está configurado en esta caja: no se puede usar este medio de pago";
        setTbkError(msg); toast.error(msg, { duration: 7000 }); return false;
      }
      tbkBusyRef.current = true; mpCancelRef.current = false;
      setTbkEstado("Enviando el cobro al equipo Transbank C2C…"); setEquipoActivo("C2C");
      try {
        const cr = await c2cCobrar({ terminalId: terminal.terminal_id, monto, onEstado: setTbkEstado, cancelRef: mpCancelRef });
        if (!cr.aprobado) { setTbkError(`Transbank C2C: ${cr.mensaje}`); toast.error(`Transbank C2C: ${cr.mensaje}`); return false; }
        setTbkError(null);
        toast.success(`Transbank aprobado${cr.marca ? ` · ${cr.marca}` : ""}${cr.ultimos4 ? ` ****${cr.ultimos4}` : ""}${cr.autorizacion ? ` · Aut. ${cr.autorizacion}` : ""}`);
        return { via: "C2C", c2c_trace_id: cr.trace_id };
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "No se pudo cobrar en Transbank C2C";
        setTbkError(msg); toast.error(msg); return false;
      } finally { setTbkEstado(null); tbkBusyRef.current = false; }
    }
    if (usaMp(metodo)) {
      if (!mpHabilitado || !terminal?.mp_device_id) {
        const msg = "Mercado Pago Point aún no está configurado en esta caja: no se puede usar este medio de pago";
        setTbkError(msg); toast.error(msg, { duration: 7000 }); return false;
      }
      tbkBusyRef.current = true; mpCancelRef.current = false;
      setTbkEstado("Enviando la venta al equipo Mercado Pago Point…"); setEquipoActivo("MP");
      try {
        const mr = await mpCobrar({ terminalId: terminal.terminal_id, monto, onEstado: setTbkEstado, cancelRef: mpCancelRef });
        if (!mr.aprobado) { setTbkError(`Mercado Pago: ${mr.mensaje}`); toast.error(`Mercado Pago: ${mr.mensaje}`); return false; }
        setTbkError(null);
        toast.success(`Mercado Pago aprobado${mr.metodo ? ` · ${mr.metodo}` : ""}`);
        return { via: "MP", mp_order_id: mr.order_id };
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "No se pudo cobrar en Mercado Pago Point";
        setTbkError(msg); toast.error(msg); return false;
      } finally { setTbkEstado(null); tbkBusyRef.current = false; }
    }
    if (usaTbk(metodo)) {
      tbkBusyRef.current = true;
      setTbkEstado("Enviando la venta al POS Transbank…"); setEquipoActivo("TBK");
      try {
        const tr = await tbkVenta(monto, String(Date.now()).slice(-6), setTbkEstado);
        if (!tr.aprobado) { setTbkError(tr.mensaje); toast.error(`Transbank: ${tr.mensaje}`); return false; }
        setTbkError(null);
        toast.success(`Transbank aprobado · ${tr.marca || ""} ****${tr.ultimos4 || ""} · Aut. ${tr.autorizacion}`);
        return { via: "TBK", tbk: tr };
      } catch (e) {
        const msg = e?.message || "No se pudo cobrar en el POS Transbank";
        setTbkError(msg); toast.error(msg); return false;
      } finally { setTbkEstado(null); tbkBusyRef.current = false; }
    }
    return null;
  };

  // Comprobante interno (ingreso/retiro de caja, abono de Tarjeta Propia) en la impresora del terminal
  const imprimirComprobante = async (movDoc, gaveta) => {
    const ctx = {
      company_name: company?.nombre_comercial || "Ai-POS", store_name: store?.nombre || "",
      user_name: operator?.name || user?.name, logo_url: ticketLogo,
      caja: terminal?.nombre, feedBeforeCut: terminal?.cut_feed_lines, width: agentOk ? cols80 : 32,
    };
    if (agentOk) {
      if (gaveta && terminal?.cash_drawer_enabled) await openCashDrawer(movDoc.tipo);  // gaveta primero
      const t = toast.loading("Imprimiendo comprobante…");
      try { await agentPrintDocEscpos(cashMovementToReceipt(movDoc, ctx)); toast.success("Comprobante impreso", { id: t }); }
      catch (e) { toast.error(usbMode ? (e?.message || "No se pudo imprimir (impresora USB)") : "No se pudo imprimir (agente)", { id: t }); }
    } else if (printerMode === "OS_80MM") {
      await printHtml(cashMovementHtml(movDoc, ctx), `${movDoc.tipo}`);
    } else if (btOk && btName) {
      try { await printReceipt(cashMovementToReceipt(movDoc, ctx)); toast.success("Voucher enviado a impresora BT"); }
      catch (e) { btErrorToast(e); }
    }
  };
  const tpMetodo = methods.find((m) => m.codigo === "TARJETA_PROPIA" && m.canal_pos !== false && m.is_active !== false);
  const imprimirAbono = (a) => imprimirComprobante({
    tipo: `ABONO ${String(a.medio || "").toUpperCase()}`, monto: a.monto, at: a.created_at,
    motivo: `${a.nombre} · RUT ${formatRut(a.rut)} · Pagado con ${a.payment_label}${a.vuelto ? ` · Vuelto ${clp(a.vuelto)}` : ""} · Deuda ${clp(a.deuda_despues)} · Cupo disponible ${clp(a.disponible_despues)}`,
  }, a.payment_method === "EFECTIVO");

  const charge = async (opts = {}) => {    // `cashOverride`: monto en efectivo ya conocido (botón de billete) — evita esperar el state
    const metodo = opts?.methodOverride || payMethod;
    if (tbkBusyRef.current) return;
    const cashOverride = typeof opts?.cashOverride === "number" ? opts.cashOverride : null;
    const recibidoNum = cashOverride != null ? cashOverride : Number(recibido || 0);
    const netTotal = Math.max(0, total - (redeemValue || 0) - descManualMonto - cuponMonto - valeMonto);
    if (descExcedeTope && !descPin) { toast.error(`El descuento supera el tope de ${descTope}%: pide el PIN del jefe`); return; }
    // Pago en partes: lo escrito en "Recibido" se abona al medio elegido hasta llegar a saldo $0
    if (vale && netTotal < 1) { avisoValeSinSaldo(); return; }
    const pagado = parciales.reduce((acc, p) => acc + Number(p.monto || 0), 0);
    const saldo = Math.max(0, Math.round((netTotal - pagado) * 100) / 100);
    // Saldo ya cubierto por abonos (p. ej. tarjeta aprobada y la venta falló al registrarse): solo se registra
    const cierreParcial = saldo <= 0.5 && parciales.length > 0;
    const esCash = !cierreParcial && isCashMethod(metodo);
    const saldoPagar = resolveRounding(saldo, metodo, roundCfg).total_pagar;
    if (!cierreParcial) {
      if (parciales.some((p) => p.codigo === metodo)) { toast.error("Ese medio ya se usó en este pago: elige otro"); return; }
      if (usaTbk(metodo) && parciales.some((p) => p.via === "TBK")) { toast.error("Ya se cobró una parte en el POS Transbank: usa otro medio para el saldo"); return; }
      if (usaMp(metodo) && parciales.some((p) => p.via === "MP")) { toast.error("Ya se cobró una parte en Mercado Pago Point: usa otro medio para el saldo"); return; }
      if (saldo > 0 && !(recibidoNum > 0)) { toast.error("Escribe el monto a pagar"); return; }
      if (!esCash && recibidoNum > saldoPagar + 0.5) {
        toast.error(`El monto supera el saldo de ${clp(saldoPagar)} (solo el efectivo da vuelto)`); return;
      }
    }
    const esFinal = cierreParcial || recibidoNum >= saldoPagar - 0.5;
    if (!esFinal && presaleTickets.length > 0) { toast.error("Las pre-ventas se cobran con un solo medio de pago"); return; }
    if (!esFinal && parciales.length >= 3) { toast.error("Máximo 4 medios de pago por venta"); return; }
    const esTP = metodo === "TARJETA_PROPIA" && !cierreParcial;
    if (esTP) {
      if (parciales.length > 0 || !esFinal) { toast.error(`${tpMetodo?.nombre || "Tarjeta Propia"} paga el total de la venta: no se combina con otros medios`); return; }
      if (!tpAuth) { toast.error("Valida la tarjeta del cliente antes de cobrar"); return; }
      if (Math.abs(tpAuth.monto - saldoPagar) > 0.5) { setTpAuth(null); toast.error("El total cambió: vuelve a validar la tarjeta"); return; }
      if (!navigator.onLine) { toast.error("Sin conexión no se puede validar el cupo"); return; }
    }
    const montoParte = esFinal ? saldo : recibidoNum;
    const cobrar = esFinal ? saldoPagar : recibidoNum;
    let normRut = null;
    if (loyaltyActive && custRut) {
      normRut = normalizeRut(custRut);
      if (!normRut) { toast.error("RUT inválido"); return; }
      if (!custName.trim()) { toast.error("Ingresa el nombre del cliente"); return; }
    }
    // Factura electrónica: el SII exige RUT, razón social, giro y dirección del cliente
    let facturaRut = null;
    if (payDocType === "FACTURA") {
      const falta = validarCliente(facturaData);
      if (falta) { toast.error(falta); return; }
      facturaRut = normalizeRut(facturaData.rut);
      if (!facturaRut) { toast.error("El RUT del cliente no es válido"); return; }
      const errExtra = validarTransporte(facturaTransp) || validarReferencias(facturaRefs);
      if (errExtra) { toast.error(errExtra); setFacturaModal(true); return; }
    }
    if (!esFinal) {
      const equipo = await cobrarEnEquipo(metodo, cobrar);
      if (equipo === false) return;
      const nombre = methods.find((m) => m.codigo === metodo)?.nombre || metodo;
      const nuevos = [...parciales, { codigo: metodo, nombre, monto: montoParte, ...(esCash ? { monto_recibido: montoParte } : {}), ...(equipo || {}) }];
      setParciales(nuevos);
      const resto = Math.max(0, Math.round((netTotal - nuevos.reduce((acc, p) => acc + Number(p.monto || 0), 0)) * 100) / 100);
      const sig = posPayMethods.find((m) => payAllowed(m.codigo) && !nuevos.some((p) => p.codigo === m.codigo));
      if (sig) setPayMethod(sig.codigo);
      setRecibidoTouched(false);
      setRecibido(resolveRounding(resto, sig?.codigo || "EFECTIVO", roundCfg).total_pagar);
      toast.success(`${nombre} ${clp(montoParte)} abonado · saldo ${clp(resto)}`);
      setTimeout(() => { const el = recibidoRef.current; if (el) { el.focus(); el.select?.(); } }, 50);
      return;
    }
    try {
      // Si hay preventas cargadas, cobrarlas con endpoint multi
      if (presaleTickets.length > 0) {
        const r = await api.post("/presales/close-multi", {
          codigos: presaleTickets.map((t) => t.codigo),
          shift_id: shift?.shift_id,
          payment_method: metodo,
          monto_recibido: metodo === "EFECTIVO" ? recibidoNum : total,
          terminal_id: terminalId || undefined,
        });
        setLastSale(r.data.sale); setCart([]); setPresaleTickets([]);
        setPayOpen(false); setRecibido(0); resetCustomer();
        setLastProduct(null); setPendingQty(null); setQtyBuffer("");
        toast.success(`${r.data.presales_count} ticket(s) cobrado(s)`);
        // Boleta directa (agente / impresora del sistema / Bluetooth), igual que una venta normal
        await finalizeSale(r.data.sale, {
          vuelto: metodo === "EFECTIVO" ? (r.data.sale?.vuelto || 0) : null,
          isCash: metodo === "EFECTIVO",
        });
        setParciales([]);
        load();
        return;
      }
      const salePayload = {
        store_id: store.store_id,
        shift_id: shift?.shift_id,
        items: cart.map((x) => ({ product_id: x.product_id, cantidad: x.cantidad, precio_unitario: x.precio, ...(x.mods_sel ? { modificadores: x.mods_sel } : {}), ...(x.detalle ? { detalle: x.detalle } : {}) })),
        payment_method: metodo,
        monto_recibido: esCash ? recibidoNum : netTotal,
        ...(esTP ? { credito_auth: tpAuth.credito_auth } : {}),
        canal: "POS",
        terminal_id: terminalId || undefined,
        // Idempotencia: evita venta/cobro duplicado por doble clic o reintento de la cola offline
        idempotency_key: (window.crypto?.randomUUID?.() || `pos-${Date.now()}-${Math.random().toString(36).slice(2)}`),
        ...(normRut ? { customer_rut: normRut, customer_name: custName.trim() } : {}),
        ...(normRut && redeemValue > 0 ? { loyalty_redeem: redeemValue } : {}),
        ...(operator ? { operator_user_id: operator.user_id, operator_name: operator.name } : {}),
        ...(ageAuth ? { age_auth: ageAuth } : {}),
        ...(custNacimiento ? { customer_nacimiento: custNacimiento } : {}),
        ...(vale ? { exchange_codigo: vale.codigo, descuento: valeMonto } : {}),
        ...(quoteId ? { quote_id: quoteId, quote_numero: quoteNumero } : {}),
        ...(cuponMonto > 0 ? { coupon_code: cupon.code, coupon_monto: cuponMonto } : {}),
        ...(descManualMonto > 0 ? {
          descuento_manual_tipo: descManual.tipo,
          descuento_manual_valor: Number(descManual.valor),
          descuento_manual_monto: descManualMonto,
          ...(descPin ? { descuento_supervisor_pin: descPin.pin } : {}),
        } : {}),
        ...(facturaRut ? {
          doc_type: "FACTURA",
          customer_rut: facturaRut,
          customer_name: facturaData.razon_social.trim(),
          customer_giro: facturaData.giro.trim(),
          customer_address: facturaData.direccion.trim(),
          customer_comuna: facturaData.comuna.trim() || undefined,
          customer_ciudad: facturaData.ciudad.trim() || undefined,
          doc_observaciones: facturaObs.trim() || undefined,
          transporte: transportePayload(facturaTransp),
          referencias: referenciasPayload(facturaRefs),
        } : {}),
        ...(payDocType === "TICKET" && !facturaRut ? { doc_type: "TICKET" } : {}),
      };
      const equipo = cierreParcial ? null : await cobrarEnEquipo(metodo, cobrar);
      if (equipo === false) return;
      if (cierreParcial) { salePayload.payment_method = parciales[parciales.length - 1].codigo; salePayload.monto_recibido = netTotal; }
      if (equipo?.tbk) salePayload.tbk = equipo.tbk;
      if (equipo?.mp_order_id) salePayload.mp_point_order_id = equipo.mp_order_id;
      if (equipo?.c2c_trace_id) salePayload.tbk_c2c_trace_id = equipo.c2c_trace_id;
      const previoC2c = parciales.find((p) => p.c2c_trace_id);
      if (previoC2c) salePayload.tbk_c2c_trace_id = previoC2c.c2c_trace_id;
      const previoTbk = parciales.find((p) => p.tbk);
      const previoMp = parciales.find((p) => p.mp_order_id);
      if (previoTbk) salePayload.tbk = previoTbk.tbk;
      if (previoMp) salePayload.mp_point_order_id = previoMp.mp_order_id;
      if (parciales.length > 0) {
        salePayload.payments = [
          ...parciales.map((p) => ({ codigo: p.codigo, monto: p.monto, ...(p.monto_recibido ? { monto_recibido: p.monto_recibido } : {}), ...(p.via ? { via: p.via } : {}) })),
          ...(cierreParcial ? [] : [{ codigo: metodo, monto: montoParte, ...(esCash ? { monto_recibido: recibidoNum } : {}), ...(equipo?.via ? { via: equipo.via } : {}) }]),
        ];
      }
      try {
        // Pantalla de cierre inmediata: el cajero ve el vuelto mientras el servidor responde
        const vueltoPrevio = esCash ? Math.max(0, recibidoNum - cobrar) : null;
        setPayOpen(false);
        setPrintingSale({ sale: null, vuelto: vueltoPrevio, done: false, pendiente: true });
        const r = await api.post("/sales", salePayload);
        setTpAuth(null);
        setLastSale(r.data); setCart([]); setPayOpen(false); setRecibido(0); resetCustomer();
        // Factura electrónica: sale impresa en la caja; el PDF queda en el panel web
        if (r.data?.factura) {
          toast.success(`Factura electrónica N° ${r.data.factura.folio} emitida`);
          setFacturaData(CLIENTE_VACIO);
          setFacturaObs("");
          setFacturaTransp(TRANSPORTE_VACIO);
          setFacturaRefs([]);
        } else if (r.data?.factura_error) {
          toast.error(`Venta guardada, pero la factura no se emitió: ${r.data.factura_error}`, { duration: 10000 });
        }
        setAgeAuth(null); ageAuthRef.current = null; setVale(null);
        setDescManual({ tipo: "MONTO", valor: "" }); setDescPin(null); setCupon(null);
        setLastProduct(null); setPendingQty(null); setQtyBuffer("");
        // Cotización convertida en venta: se liga en segundo plano (no frena la impresión)
        if (quoteId) {
          api.post(`/quotes/${quoteId}/convert`, { sale_id: r.data.sale_id })
            .then(() => toast.success("Cotización convertida en venta"))
            .catch(() => {});
          setQuoteId(null);
          setQuoteNumero(null);
        }
        if (terminal?.email_receipt_enabled && receiptEmail.trim()) {
          api.post(`/sales/${r.data.sale_id}/email-receipt`, { email: receiptEmail.trim() })
            .then(() => toast.success(`Boleta enviada a ${receiptEmail.trim()}`))
            .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo enviar la boleta por correo"));
        }
        // Pantalla de cierre de venta: vuelto + "Imprimiendo tu boleta" hasta que termine
        const { printedBt } = await finalizeSale(r.data, {
          vuelto: metodo === "EFECTIVO" ? (r.data.vuelto || 0) : null,
          isCash: metodo === "EFECTIVO",
        });
        setParciales([]);
        if (terminal?.c2c_imprimir_boleta && c2cHabilitado) {
          api.post("/tbk-c2c/imprimir", { terminal_id: terminal.terminal_id, sale_id: r.data.sale_id })
            .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo enviar la boleta al equipo Transbank"));
        }
        toast.success(`Venta #${r.data.numero} procesada${printedBt ? " · impresa BT" : ""}`);
        load();
      } catch (e) {
        // Sin respuesta del servidor → red caída → encolar
        if (!e?.response) {
          const item = await enqueueSale(salePayload);
          await applyOfflineTotals();   // el turno local refleja la venta recién encolada
          // Boleta offline con número temporal
          const offlineSale = {
            ...salePayload,
            sale_id: item.offline_id,
            numero: item.offline_id,
            offline: true,
            created_at: item.created_at,
            iva: Math.round(total - total / 1.19),
            total: total,
            vuelto: metodo === "EFECTIVO" ? Math.max(0, recibidoNum - cobrar) : 0,
            items: cart.map((x) => ({
              product_id: x.product_id, sku: x.sku, nombre: x.nombre,
              cantidad: x.cantidad, precio_unitario: x.precio, ...(x.mods_sel ? { modificadores: x.mods_sel } : {}),
              subtotal: Math.round(x.precio * x.cantidad),
              // Precio por cantidad: el vale offline también muestra el ahorro conseguido
              precio_lista: (x.precio_base ?? x.precio) > x.precio ? (x.precio_base ?? x.precio) : null,
              ahorro_cantidad: Math.max(0, Math.round(((x.precio_base ?? x.precio) - x.precio) * x.cantidad)),
            })),
            ahorro_cantidad: cart.reduce(
              (s, x) => s + Math.max(0, Math.round(((x.precio_base ?? x.precio) - x.precio) * x.cantidad)), 0),
          };
          setLastSale(offlineSale); setCart([]); setPayOpen(false); setRecibido(0); resetCustomer();
          setLastProduct(null); setPendingQty(null); setQtyBuffer("");
          setIsOnline(false);
          await refreshPendingCount();
          // Boleta directa a la impresora, igual que una venta normal (sin vista previa)
          await finalizeSale(offlineSale, {
            vuelto: metodo === "EFECTIVO" ? (offlineSale.vuelto || 0) : null,
            isCash: metodo === "EFECTIVO",
          });
          setParciales([]);
          toast.warning("Sin conexión · venta guardada offline", { duration: 4000 });
        } else {
          setPrintingSale(null);
          setPayOpen(true);
          // El equipo ya aprobó el cobro: queda como abono para no volver a cobrar la tarjeta al reintentar
          if (equipo?.via) {
            setParciales((prev) => [...prev, { codigo: metodo, nombre: methods.find((m) => m.codigo === metodo)?.nombre || metodo, monto: montoParte, ...equipo }]);
            setRecibido(0); setRecibidoTouched(false);
          }
          throw e;
        }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  chargeRef.current = charge;


  // Logo del vale: sólo si el terminal lo tiene activado (y existe logo configurado)
  const ticketLogo = terminal?.print_logo
    ? resolveMedia(company?.ticket_logo_url || company?.logo_url)
    : null;
  // Pre-cacheamos el logo como data URL: así el render raster no espera la descarga
  // en cada impresión (clave para la velocidad y para que funcione sin conexión).
  const [ticketLogoData, setTicketLogoData] = useState(null);
  useEffect(() => {
    if (!ticketLogo) { setTicketLogoData(null); return; }
    let alive = true;
    fetch(ticketLogo)
      .then((r) => r.blob())
      .then((b) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); }))
      .then((d) => { if (alive) setTicketLogoData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ticketLogo]);

  // Boleta tradicional (timbre PDF417 + folio del SII): el formato se define por terminal.
  const boletaPdf417 = terminal?.boleta_print_mode === "BOLETA_PDF417";
  const fetchBoletaLegal = async (sale) => {
    // La factura SIEMPRE sale en formato clásico con timbre: solo las boletas pueden ir como vale + QR
    const esFactura = !!(sale?.factura || sale?.factura_dte_id);
    if ((!boletaPdf417 && !esFactura) || !sale?.sale_id || sale.offline) return null;
    try {
      const { data } = await publicApi.get(`/public/receipt/${sale.sale_id}`);
      return data?.dte?.pdf417 ? data : null;
    } catch (_) { return null; }
  };
  const boletaLegalCtx = (sale) => ({
    logo_url: ticketLogoData || ticketLogo,
    caja: terminal?.nombre, cashier: operator?.name || user?.name,
    footer_message: store?.ticket_footer || "", gracias_message: graciasMsg, ...salePayCtx(sale),
  });
  // Segunda copia CEDIBLE (con acuse de recibo) en la impresora Bluetooth
  const printCedibleBt = async (legal, sale) => {
    if (!llevaCopiaCedible(legal, terminal)) return;
    await printBoletaLiviana(legal, { ...boletaLegalCtx(sale), cedible: true, feedBeforeCut: terminal?.cut_feed_lines });
  };

  // Vale a la impresora de 80 mm del sistema: iframe propio de 80 mm, QR de la boleta digital
  // y nombre del cajero. Si el navegador abrió el diálogo de Windows, avisamos cómo evitarlo.
  // El paso "Imprime tu primer ticket" del ayudante se marca con CUALQUIER impresión
  // (agente, 80 mm del sistema o Bluetooth), no solo con el botón del modal.
  const markTicketPrinted = () => { api.post("/activation/event/primer_ticket").catch(() => {}); };

  // Avisos de la pantalla de cocina: pedido listo para llamar al cliente
  useEffect(() => {
    if (!terminal?.terminal_id) return;
    let cancelado = false;
    const beep = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator(); const g = ctx.createGain();
        osc.frequency.value = 980; g.gain.value = 0.2;
        osc.connect(g); g.connect(ctx.destination);
        osc.start(); osc.stop(ctx.currentTime + 0.25);
      } catch (_) { /* sin sonido */ }
    };
    const check = async () => {
      try {
        const { data } = await api.get("/pos/alerts", { params: { terminal_id: terminal.terminal_id } });
        for (const al of data.items || []) {
          if (cancelado) return;
          beep();
          toast.success(`Pedido N° ${al.numero_atencion} listo`, {
            description: "Llama al cliente y entrégale su pedido",
            duration: 15000,
          });
          api.post(`/pos/alerts/${al.alert_id}/ack`).catch(() => {});
        }
      } catch (_) { /* reintenta */ }
    };
    check();
    const iv = setInterval(check, 15000);
    return () => { cancelado = true; clearInterval(iv); };
  }, [terminal?.terminal_id]);

  // Journal electrónico del terminal (lo ve Superadmin y la mesa de ayuda)
  const logTerminalEvent = (tipo, detalle = {}) => {
    api.post("/terminal-events", { tipo, terminal_id: terminal?.terminal_id, detalle }).catch(() => {});
  };

  // Formas de pago visibles en la caja (en este orden se asignan los atajos F1–F4)
  const posPayMethods = useMemo(
    () => methods.filter((m) => m.canal_pos && m.is_active !== false),
    [methods]
  );
  // F1–F4 eligen la forma de pago sin usar el mouse; Enter cobra
  useEffect(() => {
    if (!payOpen) return;
    const onKey = (e) => {
      const idx = { F1: 0, F2: 1, F3: 2, F4: 3 }[e.key];
      if (idx == null) return;
      const m = posPayMethods[idx];
      if (!m) return;
      e.preventDefault();
      pickMethodRef.current(m);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [payOpen, posPayMethods]);

  const printSaleOnOsPrinter = async (sale) => {
    markTicketPrinted();
    // Boleta legal y QR en paralelo: el papel empieza a salir antes
    const [legal, qr] = await Promise.all([
      fetchBoletaLegal(sale),
      qrDataUrl(receiptUrl(sale.sale_id)),
    ]);
    const body = legal
      ? boletaLegalHtml(legal, boletaLegalCtx(sale))
      : saleTicketHtml(sale, company?.nombre_comercial, store?.nombre, ticketLogoData || ticketLogo, store?.ticket_footer,
          { cashier: operator?.name || user?.name, caja: terminal?.nombre,
            qr, gracias_message: graciasMsg, ...salePayCtx(sale) });
    const { silent } = await printHtml(body, `Boleta ${sale?.numero || ""}`);
    if (legal && llevaCopiaCedible(legal, terminal)) {
      await printHtml(boletaLegalHtml(legal, { ...boletaLegalCtx(sale), cedible: true }), `Factura ${legal.dte.folio} cedible`);
    }
    if (!silent) {
      toast.warning(
        "Windows pidió elegir impresora. Abre la caja con el acceso directo de impresión directa (Terminales → Impresora → Descargar acceso directo).",
        { duration: 9000 }
      );
    }
    // Vales de concurso: cada uno como trabajo separado para que la impresora
    // corte el papel tras la boleta y emita el vale a continuación.
    const vouchers = sale.contest_vouchers || [];
    for (const v of vouchers) {
      try {
        const vqr = await qrDataUrl(v.voucher_code);
        await printHtml(contestVoucherHtml(v, { qr: vqr, caja: terminal?.nombre, cashier: operator?.name || user?.name }), `Vale concurso ${v.voucher_code}`);
      } catch (_) { /* si un vale falla, seguimos con el resto */ }
    }
    await printCambioSlipsOs(sale);
    return silent;
  };

  // Ticket(s) de cambio en la impresora de 80 mm del sistema (cada uno como trabajo aparte)
  const printCambioSlipsOs = async (sale) => {
    const slips = cambioSlips(sale);
    if (!slips.length) return;
    const bc = barcodeDataUrl(String(sale?.numero ?? ""));
    for (const items of slips) {
      try {
        await printHtml(exchangeSlipHtml(sale, {
          company_name: company?.nombre_comercial, store_name: store?.nombre,
          logo_url: ticketLogoData || ticketLogo, caja: terminal?.nombre, items, barcode: bc,
        }), `Ticket de cambio ${sale?.numero || ""}`);
      } catch (_) { /* si uno falla, seguimos con el resto */ }
    }
  };

  const printTicket = () => {
    markTicketPrinted();
    if ((printerMode === "OS_80MM" || printerMode === "USB_80MM") && agentOk && lastSale) { autoPrintSaleAgent(lastSale, { skipDrawer: true }); return; }
    if (printerMode === "OS_80MM" && lastSale) { printSaleOnOsPrinter(lastSale); return; }
    window.print();
  };

  // ============== AGENTE DE IMPRESIÓN LOCAL (Windows) ==============
  // Construye los trabajos de impresión para el agente. "Ambos" formatos:
  // raster (fiel al diseño, con logo) si el terminal tiene logo; si no, ESC/POS texto.
  // Etiqueta amigable del medio de pago y detalle de pago mixto (para no imprimir "MIXTO").
  const methodLabel = (codigo) => methods.find((m) => m.codigo === codigo)?.nombre || String(codigo || "").replace(/_/g, " ");
  const salePayCtx = (sale) => {
    const pays = sale?.payments || [];
    return {
      paymentLines: pays.length > 1
        ? pays.map((p) => ({ label: methodLabel(p.codigo || p.payment_method), value: Number(p.monto_cobrado ?? p.monto) }))
        : null,
      paymentLabel: methodLabel(sale?.payment_method),
    };
  };

  const buildSaleAgentJobs = async (sale) => {
    const useRaster = !!(terminal?.print_logo && ticketLogo);
    const cajaName = terminal?.nombre || "";
    const cashierName = operator?.name || user?.name || "";
    const feedBeforeCut = terminal?.cut_feed_lines;
    const payCtx = salePayCtx(sale);
    const saleEscposJob = () => {
      const receipt = saleToReceipt(sale, {
        company_name: company?.nombre_comercial || "Ai-POS",
        store_name: store?.nombre || "",
        footer_message: store?.ticket_footer || "", gracias_message: graciasMsg,
        caja: cajaName, cashier: cashierName,
        qr_data: receiptUrl(sale.sale_id),
        feedBeforeCut, width: cols80, ...payCtx,
      });
      return { type: "escpos", data_base64: bytesToBase64(buildTicketBytes(receipt)) };
    };
    const jobs = [];
    const legal = await fetchBoletaLegal(sale);
    if (legal) {
      // Boleta tradicional: se imprime como imagen para que salga el timbre PDF417
      const png = await renderTicketPng(boletaLegalHtml(legal, boletaLegalCtx(sale)), `Boleta ${legal.dte.folio}`);
      jobs.push(png ? { type: "raster", data_base64: png } : saleEscposJob());
      if (llevaCopiaCedible(legal, terminal)) {
        const ced = await renderTicketPng(boletaLegalHtml(legal, { ...boletaLegalCtx(sale), cedible: true }), `Factura ${legal.dte.folio} cedible`);
        if (ced) jobs.push({ type: "raster", data_base64: ced });
      }
    } else if (useRaster) {
      const qr = await qrDataUrl(receiptUrl(sale.sale_id));
      const saleHtml = saleTicketHtml(sale, company?.nombre_comercial, store?.nombre, ticketLogoData || ticketLogo,
        store?.ticket_footer, { cashier: cashierName, caja: cajaName, qr, gracias_message: graciasMsg, ...payCtx });
      const png = await renderTicketPng(saleHtml, `Vale ${sale?.numero || ""}`);
      jobs.push(png ? { type: "raster", data_base64: png } : saleEscposJob());
    } else {
      jobs.push(saleEscposJob());
    }
    for (const v of (sale.contest_vouchers || [])) {
      try {
        if (useRaster) {
          const vqr = await qrDataUrl(v.voucher_code);
          const vhtml = contestVoucherHtml(v, { qr: vqr, caja: cajaName, cashier: cashierName });
          const vpng = await renderTicketPng(vhtml, `Vale concurso ${v.voucher_code}`);
          if (vpng) { jobs.push({ type: "raster", data_base64: vpng }); continue; }
        }
        const bytes = buildTicketBytes(contestVoucherToReceipt({
          nombre: v.contest_name, detalle: v.contest_detail, codigo: v.voucher_code,
          store_identifier: v.store_id, sale_number: v.sale_number, sale_total: v.sale_total,
          fecha: v.created_at,
        }, { company_name: company?.nombre_comercial || "Ai-POS", caja: cajaName, cashier: cashierName, feedBeforeCut, width: cols80 }));
        jobs.push({ type: "escpos", data_base64: bytesToBase64(bytes) });
      } catch (_) { /* si un vale falla, seguimos */ }
    }
    for (const items of cambioSlips(sale)) {
      const bytes = buildTicketBytes(exchangeSlipToReceipt(sale, {
        company_name: company?.nombre_comercial || "Ai-POS", store_name: store?.nombre || "",
        caja: cajaName, items, feedBeforeCut, width: cols80,
      }));
      jobs.push({ type: "escpos", data_base64: bytesToBase64(bytes) });
    }
    return jobs;
  };

  const autoPrintSaleAgent = async (sale, { skipDrawer = false } = {}) => {
    if (!agentOk || !sale) return false;
    markTicketPrinted();
    const port = terminal?.agent_port || 7777;
    const token = terminal?.agent_token || "";
    try {
      // 1) GAVETA PRIMERO (instantáneo): el cajero da el vuelto mientras imprime.
      //    No esperamos al backend: el agente abre con su propia config y el evento se
      //    registra en segundo plano (clave para la velocidad y para trabajar sin conexión).
      const methods = (sale.payments && sale.payments.length)
        ? sale.payments.map((p) => String(p.codigo || p.payment_method || "").toUpperCase())
        : [String(sale.payment_method || "").toUpperCase()];
      const isCash = methods.includes("EFECTIVO");
      if (usbMode) {
        // Impresora no lista (tapa/papel): no enviamos nada para no dejar la caja esperando
        const m = usbStatusMensaje(await usbStatus());
        if (m && m.nivel === "error") {
          sale._drawerTried = true;
          const err = new Error(`${m.texto} Luego usa «Reimprimir»${isCash ? " y abre la gaveta con el botón" : ""}.`);
          err.code = "USB_NOT_READY";
          throw err;
        }
      }
      if (!skipDrawer && isCash && terminal?.cash_drawer_enabled && terminal?.cash_drawer_auto_open !== false) {
        sale._drawerTried = true;
        try {
          await hwDrawer();
          sale._drawerOpened = true;
          api.post("/cash-drawer/open", {
            terminal_id: terminalId, motivo: "VENTA_EFECTIVO", sale_id: sale.sale_id }).catch(() => {});
        } catch (_) { /* seguimos imprimiendo */ }
      }
      // 2) Imprimir boleta + vales de concurso.
      const jobs = await buildSaleAgentJobs(sale);
      await hwPrint(jobs);
      return true;
    } catch (e) {
      toast.error(usbMode ? (e?.message || "No se pudo imprimir en la impresora USB") : "No se pudo imprimir por el agente local. Revisa que esté corriendo.",
        { id: "usb-estado", duration: 8000 });
      if (usbMode && (e?.code === "USB_NOT_READY" || e?.code === "USB_STALLED")) sale._usbPendiente = true;
      return false;
    }
  };

  // Imprime un documento interno (movimiento, apertura, cierre, suspendida) por el agente.
  const agentPrintDocEscpos = async (receipt) => {
    await hwPrint([{ type: "escpos", data_base64: bytesToBase64(buildTicketBytes(receipt)) }]);
  };
  // Transporte de impresión 80 mm: agente local (Windows) o USB directo por WebUSB (Android)
  const hwPrint = (jobs) => (usbMode
    ? usbPrint(jobs, { feedBeforeCut: terminal?.cut_feed_lines, dots: cols80 === 42 ? 512 : 576 }).finally(() => revisarUsb(true))
    : agentPrint(terminal?.agent_port || 7777, terminal?.agent_token || "", jobs, {}));
  const hwDrawer = () => (usbMode
    ? usbOpenDrawer(terminal?.cash_drawer_pin || 0)
    : agentOpenDrawer(terminal?.agent_port || 7777, terminal?.agent_token || ""));

  // ================= IMPRESORA BLUETOOTH =================
  // Prioridad: terminal.bt_printer_name (config compartida) > localStorage (override por browser)
  // El nombre se persiste en localStorage → sobrevive a logout y cierre de navegador,
  // por lo que el usuario NO tiene que re-vincular. La conexión BLE se re-establece
  // automáticamente al primer intento de impresión (Web Bluetooth exige un gesto).
  const [btName, setBtName] = useState(() => btSavedPrinter()?.name || btSavedName());
  useEffect(() => {
    // La impresora recordada en este equipo manda; si no hay, se adopta la del terminal
    const propia = btSavedPrinter();
    if (propia?.name) { setBtName(propia.name); return; }
    if (terminal?.bt_printer_name) {
      setBtName(terminal.bt_printer_name);
      try { localStorage.setItem("aipos_bt_printer", terminal.bt_printer_name); } catch (_) {}
    } else if (!btSavedName()) {
      setBtName(null);
    }
  }, [terminal?.bt_printer_name, terminal?.terminal_id]);
  const [btBusy, setBtBusy] = useState(false);
  const btOk = btSupported();
  // Agente de impresión local (Windows): sólo para terminales OS_80MM con el agente activado.
  const [agentUp, setAgentOk] = useState(false);
  const androidPos = (terminal?.plataforma || "").toUpperCase() === "ANDROID";
  const cols80 = Number(terminal?.print_columns) === 42 ? 42 : 48;
  const [kbOn, setKbOn] = useState(false);
  const agentWanted = terminal?.printer_mode === "OS_80MM" && !!terminal?.agent_enabled;
  // Android + impresora USB 80 mm (WebUSB): mismo flujo que el agente (gaveta inteligente, corte, raster)
  const usbMode = terminal?.printer_mode === "USB_80MM";
  const [usbReady, setUsbReady] = useState(() => isUsbConnected());
  const [usbBusy, setUsbBusy] = useState(false);
  const agentOk = agentUp || (usbMode && usbReady);
  useEffect(() => {
    if (!usbMode || !isUsbSupported()) return;
    let alive = true;
    const sync = () => usbReconnect().then((ok) => { if (alive) setUsbReady(ok); });
    sync();
    const iv = setInterval(() => { if (alive) setUsbReady(isUsbConnected()); }, 5000);
    navigator.usb.addEventListener?.("connect", sync);
    navigator.usb.addEventListener?.("disconnect", sync);
    return () => { alive = false; clearInterval(iv); navigator.usb.removeEventListener?.("connect", sync); navigator.usb.removeEventListener?.("disconnect", sync); };
  }, [usbMode]);
  // Estado de papel / tapa (DLE EOT): se consulta cada 15 s y después de cada impresión
  const [usbEstado, setUsbEstado] = useState(null);
  const usbAviso = useRef(null);
  const revisarUsb = async (trasImprimir = false) => {
    if (!usbMode || !isUsbConnected()) return;
    const st = await usbStatus();
    const m = usbStalled()
      ? { nivel: "error", texto: "La impresora no responde: revisa la tapa y el papel. Luego usa «Reimprimir»." }
      : usbStatusMensaje(st);
    setUsbEstado(m);
    if (m && m.nivel === "error" && usbAviso.current !== m.texto && !trasImprimir) toast.error(m.texto, { id: "usb-estado", duration: 8000 });
    if (!m || m.nivel !== "error") toast.dismiss("usb-estado");
    usbAviso.current = m?.texto || null;
  };
  useEffect(() => {
    if (!usbMode || !usbReady) { setUsbEstado(null); return; }
    revisarUsb();
    const iv = setInterval(() => revisarUsb(), 15000);
    return () => clearInterval(iv);
  }, [usbMode, usbReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const connectUsbPrinter = async () => {
    setUsbBusy(true);
    try { const n = await usbPair(); setUsbReady(true); toast.success(`Impresora USB conectada · ${n}`); }
    catch (e) { if (e?.name !== "NotFoundError") toast.error(e?.message || "No se pudo conectar la impresora USB"); }
    setUsbBusy(false);
  };
  useEffect(() => {
    if (!agentWanted) { setAgentOk(false); return; }
    let alive = true;
    probeAgent(terminal?.agent_port).then((st) => { if (alive) setAgentOk(!!st); });
    const iv = setInterval(() => {
      probeAgent(terminal?.agent_port).then((st) => { if (alive) setAgentOk(!!st); });
    }, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [agentWanted, terminal?.agent_port]);
  // ¿La impresora Bluetooth está conectada en ESTA ventana? Web Bluetooth no comparte la
  // conexión entre recargas: la vinculación del terminal guarda el nombre, pero cada equipo
  // debe abrir la conexión una vez. Si el navegador permite recuperar el permiso, se hace solo.
  const [btReady, setBtReady] = useState(() => btIsConnected());
  const btWanted = terminal?.printer_mode === "BT_58MM" || (!terminal && !!btName);
  // Cada equipo recuerda su propia impresora, y cada caja la suya dentro del equipo
  useEffect(() => { btSetScope(terminal?.terminal_id); }, [terminal?.terminal_id]);
  // La impresora avisa si se cae o si vuelve sola: el cajero no tiene que tocar nada
  useEffect(() => btOnState(({ conectada }) => setBtReady(!!conectada)), []);
  const [btGuiaOpen, setBtGuiaOpen] = useState(false);
  // Chrome en Android pide elegir la impresora en cada apertura hasta activar los
  // permisos persistentes: se explica una sola vez por equipo.
  const btPersiste = btOk && btPermisosFijos();
  useEffect(() => {
    if (!btWanted || !btOk || btPersiste) return;
    if (localStorage.getItem("aipos_bt_guia_vista") === "1") return;
    const t = setTimeout(() => { setBtGuiaOpen(true); localStorage.setItem("aipos_bt_guia_vista", "1"); }, 1200);
    return () => clearTimeout(t);
  }, [btWanted, btOk, btPersiste]);
  useEffect(() => {
    if (!btOk || !btWanted) return;
    let alive = true;
    // Reintento silencioso en cadena: la impresora suele tardar en responder al encender
    const probar = async (intentos = 3) => {
      for (let i = 0; i < intentos && alive; i++) {
        if (await btTryReconnect()) { if (alive) setBtReady(true); return; }
        await new Promise((r) => setTimeout(r, 2500 * (i + 1)));
      }
      if (alive) setBtReady(btIsConnected());
    };
    probar();
    return () => { alive = false; };
  }, [btOk, btWanted, terminal?.bt_printer_name, terminal?.terminal_id]);

  // Estado real de la impresora Bluetooth durante el turno (sin papel / tapa abierta / apagada).
  // Sólo es posible en Bluetooth: la impresora de 80 mm del sistema no reporta su estado al navegador.
  const [btStatus, setBtStatus] = useState(null);
  const btAlertRef = useRef("");
  useEffect(() => {
    if (!btOk || !btWanted || !btReady) { setBtStatus(null); return; }
    let alive = true;
    const check = async () => {
      const st = await btQueryStatus();
      if (!alive) return;
      setBtStatus(st);
      const code = st ? [st.offline && "off", st.paper_out && "paper", st.cover_open && "cover"].filter(Boolean).join("-") : "";
      if (code && code !== btAlertRef.current) {
        toast.error(st.paper_out ? "La impresora se quedó sin papel"
          : st.cover_open ? "La impresora tiene la tapa abierta"
          : "La impresora está apagada o fuera de alcance", { duration: 8000 });
      }
      btAlertRef.current = code;
    };
    check();
    const iv = setInterval(check, 45000);
    return () => { alive = false; clearInterval(iv); };
  }, [btOk, btWanted, btReady]);

  const btProblem = btStatus && (btStatus.paper_out || btStatus.cover_open || btStatus.offline)
    ? (btStatus.paper_out ? "Sin papel" : btStatus.cover_open ? "Tapa abierta" : "Apagada o fuera de alcance")
    : null;

  const connectBtPrinter = async () => {    if (!btOk) { toast.error("Web Bluetooth no disponible. Usa Chrome/Edge en Windows o Android."); return false; }
    setBtBusy(true);
    try {
      if (await btTryReconnect()) {
        setBtReady(true);
        toast.success("Impresora Bluetooth conectada");
        return true;
      }
      const dev = await pairPrinter();
      setBtName(dev.name);
      setBtReady(btIsConnected());
      if (terminal?.terminal_id && terminal.printer_mode === "BT_58MM") {
        try { await api.post(`/terminals/${terminal.terminal_id}/bt-printer`, { name: dev.name }); } catch (_) {}
      }
      toast.success(`Impresora "${dev.name}" conectada`);
      return true;
    } catch (e) {
      if (e?.name !== "NotFoundError") toast.error(e?.message || "No se pudo conectar la impresora");
      return false;
    } finally { setBtBusy(false); }
  };

  const btErrorToast = (e) => {
    toast.error(e?.message || "Error de impresión Bluetooth", {
      duration: 9000,
      action: { label: "Conectar", onClick: () => connectBtPrinter() },
    });
  };

  const doPairPrinter = async () => {
    if (!btOk) { toast.error("Web Bluetooth no disponible. Usa Chrome/Edge o Android."); return; }
    try {
      setBtBusy(true);
      const dev = await pairPrinter();
      setBtName(dev.name);
      // Queda guardada en el terminal para todos los cajeros de esta caja
      if (terminal?.terminal_id && (terminal.printer_mode === "BT_58MM")) {
        try { await api.post(`/terminals/${terminal.terminal_id}/bt-printer`, { name: dev.name }); } catch (_) {}
      }
      toast.success(`Impresora "${dev.name}" vinculada`);
    } catch (e) {
      if (e?.name !== "NotFoundError") toast.error(e?.message || "Error al vincular");
    } finally { setBtBusy(false); }
  };
  const doPrintBt = async (sale) => {
    if (!sale) return;
    if (!btOk) { toast.error("Web Bluetooth no disponible en este navegador"); return; }
    try {
      setBtBusy(true);
      const legal = await fetchBoletaLegal(sale);
      if (legal) {
        await printBoletaLiviana(legal, { ...boletaLegalCtx(sale), feedBeforeCut: terminal?.cut_feed_lines });
        await printCedibleBt(legal, sale);
        await printCambioSlipsBt(sale);
        setBtReady(true);
        toast.success("Boleta enviada a impresora BT");
        return;
      }
      const receipt = saleToReceipt(sale, {
        company_name: company?.nombre_comercial || "Ai-POS",
        store_name: store?.nombre || "",
        footer_message: store?.ticket_footer || "", gracias_message: graciasMsg,
        caja: terminal?.nombre, cashier: operator?.name || user?.name,
        qr_data: sale.offline ? null : receiptUrl(sale.sale_id), qrScale: 4,
        width: 32, ...salePayCtx(sale),
      });
      await printReceipt(receipt);
      await printCambioSlipsBt(sale);
      setBtReady(true);
      toast.success("Ticket enviado a impresora BT");
    } catch (e) {
      btErrorToast(e);
    } finally { setBtBusy(false); }
  };
  // Ticket(s) de cambio por Bluetooth, a continuación del documento de la venta
  const printCambioSlipsBt = async (sale) => {
    for (const items of cambioSlips(sale)) {
      await printReceipt(exchangeSlipToReceipt(sale, {
        company_name: company?.nombre_comercial || "Ai-POS", store_name: store?.nombre || "",
        caja: terminal?.nombre, items, width: 32, feedBeforeCut: terminal?.cut_feed_lines,
      }));
    }
  };
  const doForgetBt = async () => {
    btForget(); setBtName(null); setBtReady(false);
    if (terminal?.terminal_id && terminal.printer_mode === "BT_58MM") {
      try { await api.post(`/terminals/${terminal.terminal_id}/bt-printer`, { name: null }); } catch (_) {}
    }
    toast.info("Impresora BT olvidada");
  };

  // ================= SEGUNDA PANTALLA (customer display) =================
  const secondScreenRef = useRef(null);
  // Abre la ventana del cliente. Si el navegador expone la Window Management API
  // (Chrome con permiso "Administrar ventanas en todas tus pantallas"), la coloca
  // en el monitor secundario y la pone en pantalla completa automáticamente.
  const openSecondScreen = useCallback(async ({ auto = false, force = false } = {}) => {
    const existing = secondScreenRef.current;
    if (existing && !existing.closed) {
      if (!force) { if (!auto) existing.focus(); return true; }
      // "force": se vuelve a abrir con la activación del clic para lograr pantalla completa
      try { existing.close(); } catch (_) {}
      secondScreenRef.current = null;
    }
    let target = null;
    let permiso = true;
    try {
      if (window.getScreenDetails) {
        const det = await window.getScreenDetails();
        target = (det.screens || []).find((s) => !s.isPrimary) || null;
      } else {
        permiso = false;
      }
    } catch (_) { permiso = false; /* permiso denegado */ }
    const feat = target
      ? `left=${target.availLeft},top=${target.availTop},width=${target.availWidth},height=${target.availHeight},popup=yes,fullscreen=yes`
      : "width=1024,height=768,popup=yes";
    const w = window.open(`/second-screen?fs=1${terminalId ? `&t=${encodeURIComponent(terminalId)}` : ""}`, "aipos_customer_display", feat);
    secondScreenRef.current = w;
    if (!w) {
      if (!auto) toast.error("Habilita ventanas emergentes para usar la 2ª pantalla");
      return false;
    }
    if (target) {
      try { w.moveTo(target.availLeft, target.availTop); w.resizeTo(target.availWidth, target.availHeight); } catch (_) {}
      setTimeout(() => { try { w.postMessage({ type: "aipos:fullscreen" }, "*"); } catch (_) {} }, 1200);
    } else if (force) {
      toast.info(permiso
        ? "No se detectó un segundo monitor. En Windows usa Pantalla → \"Extender estas pantallas\"."
        : "Para usar el monitor 2 en pantalla completa, acepta el permiso \"Administrar ventanas en todas tus pantallas\" en Chrome o Edge de escritorio.");
      setTimeout(() => { try { w.postMessage({ type: "aipos:fullscreen" }, "*"); } catch (_) {} }, 1200);
    }
    return true;
  }, [terminalId]);

  // Apertura automática al cargar el POS cuando el terminal tiene 2ª pantalla
  const autoSecondScreenTried = useRef(false);
  useEffect(() => {
    if (!terminal?.has_second_screen || autoSecondScreenTried.current) return;
    autoSecondScreenTried.current = true;
    (async () => {
      const ok = await openSecondScreen({ auto: true });
      if (!ok) {
        toast.info("Permite ventanas emergentes para abrir la 2ª pantalla", {
          duration: 15000,
          action: { label: "Abrir ahora", onClick: () => openSecondScreen({ force: true }) },
        });
      } else {
        // La pantalla completa en el monitor 2 necesita un clic del cajero (regla de Chrome)
        toast.info("2ª pantalla abierta", {
          duration: 12000,
          description: "Pulsa para dejarla a pantalla completa en el monitor 2",
          action: { label: "Pantalla completa", onClick: () => openSecondScreen({ force: true }) },
        });
      }
    })();
  }, [terminal?.has_second_screen, openSecondScreen]);
  // Enviar carrito a la segunda pantalla en cada cambio
  useEffect(() => {
    if (!terminal?.has_second_screen) return;
    const w = secondScreenRef.current;
    if (!w || w.closed) return;
    try {
      w.postMessage({
        type: "aipos:cart",
        cart,
        subtotal: rawSubtotal,
        promos: promoResult.applied.map((a) => ({ nombre: a.nombre, savings: a.savings })),
        redeem: redeemValue || 0,
        total: netTotalUi,
        company_name: company?.nombre_comercial || "Ai-POS",
        customer: custInfo?.found
          ? { name: custInfo?.name || custName || "", points: Number(custInfo?.points ?? custInfo?.puntos ?? 0) }
          : null,
        images: terminal?.second_screen_images || [],
        logo_url: terminal?.second_screen_logo_url || company?.logo_url || "",
        primary_color: terminal?.second_screen_primary_color || "#0284C7",
        secondary_color: terminal?.second_screen_secondary_color || "#4338CA",
        fullscreen: !!terminal?.second_screen_fullscreen,
      }, "*");
    } catch (_) { /* ignore */ }
  }, [cart, total, custInfo, custName, company?.nombre_comercial, company?.logo_url, terminal?.has_second_screen, terminal?.second_screen_images, terminal?.second_screen_logo_url, terminal?.second_screen_primary_color, terminal?.second_screen_secondary_color, terminal?.second_screen_fullscreen]);

  // Cuando la 2ª pantalla se anuncia lista, empujarle inmediatamente el estado actual
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.data?.type !== "aipos:second-screen-ready") return;
      const w = secondScreenRef.current;
      if (!w) return;
      try {
        w.postMessage({
          type: "aipos:cart",
          cart,
          subtotal: rawSubtotal,
          promos: promoResult.applied.map((a) => ({ nombre: a.nombre, savings: a.savings })),
          redeem: redeemValue || 0,
          total: netTotalUi,
          company_name: company?.nombre_comercial || "Ai-POS",
          customer: custInfo?.found
            ? { name: custInfo?.name || custName || "", points: Number(custInfo?.points ?? custInfo?.puntos ?? 0) }
            : null,
          images: terminal?.second_screen_images || [],
          logo_url: terminal?.second_screen_logo_url || company?.logo_url || "",
          primary_color: terminal?.second_screen_primary_color || "#0284C7",
          secondary_color: terminal?.second_screen_secondary_color || "#4338CA",
          fullscreen: !!terminal?.second_screen_fullscreen,
        }, "*");
      } catch (_) {}
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [cart, total, company, terminal]);

  // ================= AUTO-PRINT BT HELPERS =================
  // Estos helpers imprimen automáticamente si hay una impresora BT vinculada,
  // saltando la vista previa modal (defaults c1/b1 del sprint 21).
  // Retornan true si se imprimió correctamente en BT.
  const _hasBt = () => btOk && !!btName;

  // Gaveta de dinero (Doc 11 §6.2): con agente, abre al instante usando su propia config
  // (sin esperar al backend); el evento se registra en segundo plano. En Bluetooth, el
  // backend autoriza y entrega el comando ESC/POS que se transporta por BLE.
  const openCashDrawer = async (motivo = "MANUAL", saleId = null) => {
    if (!terminalId) return;
    if (agentOk || usbMode) {
      try {
        await hwDrawer();
        if (motivo === "MANUAL") toast.success("Gaveta abierta");
        api.post("/cash-drawer/open", { terminal_id: terminalId, motivo, sale_id: saleId }).catch(() => {});
      } catch (e) {
        toast.error(usbMode ? (e?.message || "No se pudo abrir la gaveta (impresora USB)") : "No se pudo abrir la gaveta (agente)");
      }
      return;
    }
    try {
      const r = await api.post("/cash-drawer/open", { terminal_id: terminalId, motivo, sale_id: saleId });
      if (_hasBt() && r.data?.command_base64) {
        await sendRawBase64(r.data.command_base64);
      }
      if (motivo === "MANUAL") toast.success("Gaveta abierta");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? d?.message : d;
      if (motivo === "MANUAL") toast.error(msg || "No se pudo abrir la gaveta");
    }
  };

  const autoPrintSaleBt = async (sale) => {
    if (!_hasBt() || !sale) return false;
    markTicketPrinted();
    try {
      const legal = await fetchBoletaLegal(sale);
      if (legal) {
        await printBoletaLiviana(legal, { ...boletaLegalCtx(sale), feedBeforeCut: terminal?.cut_feed_lines });
        await printCedibleBt(legal, sale);
      }
      if (!legal) {
        const receipt = saleToReceipt(sale, {
          company_name: company?.nombre_comercial || "Ai-POS",
          store_name: store?.nombre || "",
          footer_message: store?.ticket_footer || "", gracias_message: graciasMsg,
          caja: terminal?.nombre, cashier: operator?.name || user?.name,
          qr_data: sale.offline ? null : receiptUrl(sale.sale_id), qrScale: 4,
          width: 32, ...salePayCtx(sale),
        });
        await printReceipt(receipt);
      }
      // Imprimir vales de concurso post boleta (con líneas en blanco para corte manual)
      const vouchers = sale.contest_vouchers || [];
      for (const v of vouchers) {
        try {
          await printReceipt(contestVoucherToReceipt({
            nombre: v.contest_name,
            detalle: v.contest_detail,
            codigo: v.voucher_code,
            store_identifier: v.store_id,
            sale_number: v.sale_number,
            sale_total: v.sale_total,
            fecha: v.created_at,
          }, { company_name: company?.nombre_comercial || "Ai-POS", width: 32 }));
        } catch (_) { /* si falla uno, seguimos con el resto */ }
      }
      await printCambioSlipsBt(sale);
      return true;
    } catch (e) {
      btErrorToast(e);
      return false;
    }
  };

  const autoPrintPresaleBt = async (presale) => {
    if (!_hasBt() || !presale) return false;
    try {
      // Reutilizamos suspendedToReceipt cambiando encabezado (misma estructura)
      const receipt = suspendedToReceipt({
        codigo: presale.codigo,
        items: presale.items,
        total: presale.total,
        created_at: presale.created_at,
      }, { company_name: company?.nombre_comercial || "Ai-POS", store_name: store?.nombre || "", width: 32 });
      receipt.subheader[0] = "TICKET DE PRE-VENTA";
      // Aseguramos barcode (el helper ya lo incluye vía suspendedToReceipt)
      receipt.barcode = presale.codigo;
      receipt.footer = [
        "Presenta este codigo",
        "en caja para cobrar",
        "",
      ];
      await printReceipt(receipt);
      return true;
    } catch (e) {
      btErrorToast(e);
      return false;
    }
  };

  const autoPrintSuspendedBt = async (suspended) => {
    if (!_hasBt() || !suspended) return false;
    try {
      await printReceipt(suspendedToReceipt(suspended, {
        company_name: company?.nombre_comercial || "Ai-POS",
        store_name: store?.nombre || "",
        width: 32,
      }));
      return true;
    } catch (e) {
      toast.error("BT: " + (e?.message || "error de impresión"));
      return false;
    }
  };

  return (
    <div className="bg-slate-50 flex flex-col overflow-hidden" style={{ height: "calc(100dvh - var(--env-banner-h, 0px))" }}>
      {/* Aviso de producto vencido o por vencer (informativo, la venta sigue) */}
      {expiryAlert && (
        <div data-testid="pos-expiry-alert"
             className={`fixed top-20 left-1/2 -translate-x-1/2 z-[80] px-6 py-3 rounded-2xl shadow-2xl border-2 flex items-center gap-3 ${
               expiryAlert.dias < 0 ? "bg-rose-600 border-rose-300 text-white" : "bg-amber-400 border-amber-200 text-slate-900"}`}>
          <CalendarClock className="w-7 h-7 shrink-0" />
          <div>
            <p className="font-black text-lg leading-tight">
              {expiryAlert.dias < 0 ? "PRODUCTO VENCIDO" : `VENCE EN ${expiryAlert.dias} DÍA(S)`}
            </p>
            <p className="text-sm font-semibold">{expiryAlert.nombre} · {expiryAlert.fecha}</p>
          </div>
          <button onClick={() => setExpiryAlert(null)} data-testid="pos-expiry-alert-close"
                  className="ml-2 text-xs font-bold underline opacity-80">cerrar</button>
        </div>
      )}
      {/* Aviso mientras se recupera la cotización (la carga tarda unos segundos) */}
      {quoteLoading && (
        <div data-testid="pos-quote-loading" className="fixed inset-0 z-[90] bg-slate-900/90 backdrop-blur-sm flex flex-col items-center justify-center text-white">
          <Loader2 className="w-16 h-16 animate-spin text-sky-400" />
          <p className="mt-5 text-2xl font-black">Recuperando cotización…</p>
          <p className="text-slate-300 text-sm mt-1">Cargando los productos al carro de la caja</p>
        </div>
      )}
      {/* Barra de estado offline / sincronización. Desaparece sola al quedar en línea y sin pendientes. */}
      {(!isOnline || pendingCount > 0 || syncing) && (
        <div
          data-testid="pos-offline-banner"
          className={`print:hidden w-full px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-white text-sm font-bold ${
            !isOnline ? "bg-rose-600" : syncing ? "bg-sky-600" : "bg-amber-500"
          }`}
        >
          {!isOnline ? (
            <>
              <WifiOff className="w-4 h-4" />
              <span data-testid="pos-offline-banner-text">Sin conexión · vendiendo en modo offline</span>
            </>
          ) : syncing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span data-testid="pos-offline-banner-text">Conexión recuperada · sincronizando ventas…</span>
            </>
          ) : (
            <>
              <CloudUpload className="w-4 h-4" />
              <span data-testid="pos-offline-banner-text">Ventas por sincronizar</span>
            </>
          )}
          {pendingCount > 0 && (
            <span data-testid="pos-offline-banner-count" className="px-2 py-0.5 rounded-full bg-black/20 font-mono-num">
              {pendingCount} venta{pendingCount !== 1 ? "s" : ""} por enviar
            </span>
          )}
          {pendingCount > 0 && (
            <button
              data-testid="pos-offline-banner-detail"
              onClick={async () => { await refreshPendingCount(); setPendingOpen(true); }}
              className="underline underline-offset-2 text-xs font-semibold hover:opacity-80"
            >
              Ver detalle
            </button>
          )}
          {isOnline && pendingCount > 0 && !syncing && (
            <button
              data-testid="pos-offline-banner-sync"
              onClick={() => syncPending(false)}
              className="px-2 py-0.5 rounded-full bg-white/25 text-xs font-bold hover:bg-white/40"
            >
              Sincronizar ahora
            </button>
          )}
        </div>
      )}
      {/* Aviso preventivo: certificado digital por vencer */}
      <CertificadoAvisoCaja />
      {/* Topbar compacta · interfaz restaurante: una sola línea de información y las funciones en un panel */}
      {isTile && (
        <div data-testid="pos-topbar-tile" className="bg-white border-b border-slate-200 px-3 h-14 flex items-center gap-2 print:hidden shrink-0">
          <img src="/brand/aipos-logo.png" alt="Ai-POS" className="w-7 h-7 shrink-0" />
          <p data-testid="pos-tile-info" className="flex-1 min-w-0 truncate text-[13px] text-slate-600 font-semibold">
            <span data-testid="pos-business-name" className="text-slate-900 font-extrabold font-display">
              {company?.nombre_comercial || company?.razon_social || BRAND.name}
            </span>
            <span className="hidden sm:inline">
              {store?.nombre ? <> <span className="text-slate-300">·</span> {store.nombre}</> : null}
              {terminal?.nombre ? <> <span className="text-slate-300">·</span> {terminal.nombre}</> : null}
              {(operator?.name || user?.name) ? <> <span className="text-slate-300">·</span> {operator?.name || user?.name}</> : null}
              {" "}<span className="text-slate-300">·</span>{" "}
              <span className="tabular-nums font-mono-num">{nowTs.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
            </span>
          </p>
          <span data-testid="pos-online-indicator" title={isOnline ? "En línea" : "Sin conexión"}
                className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center ${isOnline ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
            {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
          </span>
          {isPreventaTerminal && (
            <span data-testid="pos-preventa-badge" className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-sky-50 text-sky-800 text-[11px] font-bold">
              <Ticket className="w-3.5 h-3.5" /> Pre-venta
            </span>
          )}
          {androidPos && (
            <button type="button" data-testid="pos-android-kb-toggle" onClick={() => setKbOn((v) => !v)}
                    onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    title={kbOn ? "Ocultar teclado en pantalla" : "Mostrar teclado en pantalla"}
                    className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center ${kbOn ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-500"}`}>
              <Keyboard className="w-4 h-4" />
            </button>
          )}
          {avisos.length > 0 && (
            <button data-testid="pos-avisos-btn" onClick={() => setAvisosOpen(true)} title="Avisos de administración por leer"
                    className={`shrink-0 h-9 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 animate-pulse ${hayAvisoUrgente ? "bg-red-600 text-white" : "bg-amber-100 text-amber-900"}`}>
              <Megaphone className="w-4 h-4" /> {avisos.length}
            </button>
          )}
          {!shift && !isPreventaTerminal && (
            <Button data-testid="pos-open-register-btn" {...lk("apertura_caja")} onClick={() => setOpenOpen(true)}
                    className="shrink-0 h-9 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold border-0">
              Abrir caja
            </Button>
          )}
          <Button data-testid="pos-functions-btn" onClick={() => setFuncOpen(true)}
                  className="shrink-0 h-9 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold border-0">
            <LayoutGrid className="w-4 h-4 mr-1.5" /> Funciones
          </Button>
        </div>
      )}
      {/* Topbar */}
      {!isTile && (
      <div className="bg-white border-b border-slate-100 px-4 py-3 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <div className="flex items-center gap-3 flex-wrap">
          {!kioskShell && (
            <Button variant="ghost" size="sm" data-testid="pos-admin-btn" onClick={() => nav("/admin")}><ArrowLeft className="w-4 h-4 mr-1" />Admin</Button>
          )}
          <div className="flex items-center gap-2">
            <img src="/brand/aipos-logo.png" alt="Ai-POS" className="w-8 h-8" />
            <div>
              <p className="text-sm font-bold text-slate-900 font-display leading-none">POS · {BRAND.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{store?.nombre} · {user?.name}</p>
            </div>
          </div>
          {/* Nombre negocio + terminal actual (solo lectura, no seleccionable) */}
          {(company?.nombre_comercial || terminal) && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-slate-900 text-white">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <div className="text-xs leading-tight">
                <p data-testid="pos-business-name" className="font-bold font-display truncate max-w-[160px]">{company?.nombre_comercial || company?.razon_social || "—"}</p>
                {terminal && (
                  <p className="text-[10px] text-slate-300 flex items-center gap-1">
                    <Cpu className="w-2.5 h-2.5" /> {terminal.nombre} · {TICKET_MODE_LABELS[terminal.ticket_mode] || terminal.ticket_mode}
                  </p>
                )}
              </div>
            </div>
          )}
          {isPreventaTerminal && (
            <span data-testid="pos-preventa-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-[11px] font-semibold">
              <Ticket className="w-3 h-3" /> Modo Pre-venta
            </span>
          )}
          {/* Indicador online / offline */}
          <span data-testid="pos-online-indicator" className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${isOnline ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-rose-50 border border-rose-200 text-rose-700"}`}>
            {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isOnline ? "En línea" : "Sin conexión"}
          </span>
          {cacheInfo.fromCache && (
            <span data-testid="pos-cache-indicator" title="Catálogo, precios y stock se están leyendo del equipo: el stock es aproximado hasta sincronizar"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-300 text-slate-700 text-[11px] font-semibold">
              <Database className="w-3 h-3" /> Datos locales: hace {Math.max(1, Math.round((Date.now() - cacheInfo.at) / 60000))} min · stock ≈
            </span>
          )}
          <button
            data-testid="pos-sync-catalog-btn"
            onClick={() => sincronizarCatalogo(true)}
            disabled={catSyncing}
            title="Buscar cambios de precios y productos ahora (se revisa solo cada 1 minuto)"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-[11px] font-semibold hover:bg-sky-100 disabled:opacity-60"
          >
            <RefreshCw className={`w-3 h-3 ${catSyncing ? "animate-spin" : ""}`} />
            {catSyncing ? "Sincronizando…" : "Sincronizar"}
          </button>
          {pendingCount > 0 && (
            <button
              data-testid="pos-pending-sync-btn"
              onClick={async () => { await refreshPendingCount(); setPendingOpen(true); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-bold hover:bg-amber-200"
              title="Ventas pendientes de sincronizar"
            >
              <CloudUpload className="w-3 h-3" /> {pendingCount} pendiente{pendingCount !== 1 ? "s" : ""}
            </button>
          )}
          {avisos.length > 0 && (
            <button
              data-testid="pos-avisos-btn"
              onClick={() => setAvisosOpen(true)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border animate-pulse ${
                hayAvisoUrgente
                  ? "bg-red-600 border-red-700 text-white hover:bg-red-700"
                  : "bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200"
              }`}
              title="Avisos de administración por leer"
            >
              <Megaphone className="w-3 h-3" /> {avisos.length} aviso{avisos.length !== 1 ? "s" : ""}
            </button>
          )}
          {suspendedCount > 0 && (
            <button
              data-testid="pos-suspended-list-btn"
              onClick={() => { loadSuspended(); setSuspendedOpen(true); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-100 border border-violet-300 text-violet-900 text-[11px] font-bold hover:bg-violet-200"
              title="Ventas suspendidas"
            >
              <PauseCircle className="w-3 h-3" /> {suspendedCount} suspendida{suspendedCount !== 1 ? "s" : ""}
            </button>
          )}
          {terminal?.has_second_screen && (
            <button
              data-testid="pos-second-screen-btn"
              onClick={() => openSecondScreen()}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-100 border border-cyan-300 text-cyan-900 text-[11px] font-bold hover:bg-cyan-200"
              title="Abrir segunda pantalla del cliente"
            >
              <Monitor className="w-3 h-3" /> 2ª pantalla
            </button>
          )}
          {terminal?.has_second_screen && (
            <button
              data-testid="pos-second-screen-fullscreen-btn"
              onClick={() => openSecondScreen({ force: true })}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-900 text-cyan-50 text-[11px] font-bold hover:bg-cyan-800"
              title="Reabrir la segunda pantalla en el monitor 2, a pantalla completa"
            >
              <Maximize2 className="w-3 h-3" /> Pantalla completa
            </button>
          )}
          <HelpButton className="h-7 rounded-full text-[11px] px-2.5" />
          {company?.trial_ends_at && !company?.active_plan && (
            <span data-testid="pos-demo-badge" className="hidden md:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold">
              Demo · solo ticket interno
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {shift ? (
            <>
              {operator && (
                <span data-testid="pos-operator-chip" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-slate-900 text-white text-xs font-bold">
                  <User className="w-3.5 h-3.5" /> {operator.name}
                </span>
              )}
              {!isPreventaTerminal && !isRetail && (
                <>
                  <Button data-testid="pos-reprint-btn" size="sm" {...lk("reimprimir")} onClick={openReprintLast} className="rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white shadow-md font-bold border-0">
                    <Printer className="w-3.5 h-3.5 mr-1" /> Reimprimir
                  </Button>
                  <Button data-testid="pos-cash-drawer-button" size="sm" {...(puede("ingreso") || puede("retiro") ? {} : lk("retiro"))} onClick={() => { abrirMov(); }} className="rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white shadow-md font-bold border-0">
                    <Wallet className="w-3.5 h-3.5 mr-1" /> Ingreso/Retiro
                  </Button>
                  {tpMetodo && (
                    <Button data-testid="pos-tp-abono-btn" size="sm" disabled={!shift} onClick={() => setTpAbonoOpen(true)} className="rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-white shadow-md font-bold border-0">
                      <CreditCard className="w-3.5 h-3.5 mr-1" /> Pago {tpMetodo.nombre}
                    </Button>
                  )}
                  <Button data-testid="pos-close-register-btn" size="sm" {...lk("cierre_caja")} onClick={openCloseModal} className="rounded-2xl bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-md font-bold border-0">
                    Cerrar caja
                  </Button>
                </>
              )}
              {isPreventaTerminal && (
                <Button data-testid="pos-close-register-btn" size="sm" {...lk("cierre_caja")} onClick={openCloseModal} className="rounded-2xl bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-md font-bold border-0">
                  Cerrar sesión
                </Button>
              )}
            </>
          ) : !isPreventaTerminal ? (
            <Button data-testid="pos-open-register-btn" {...lk("apertura_caja")} onClick={() => setOpenOpen(true)} className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-2xl font-bold shadow-md border-0">
              Abrir caja
            </Button>
          ) : null}
        </div>
      </div>
      )}

      {/* Guía de una sola vez para que Chrome recuerde la impresora Bluetooth */}
      <BtPermisoGuia open={btGuiaOpen} onOpenChange={setBtGuiaOpen} />

      {/* Panel único de funciones (interfaz restaurante): deja la caja limpia y todo a un toque */}
      <Dialog open={funcOpen} onOpenChange={setFuncOpen}>
        <DialogContent data-testid="pos-functions-dialog" className="rounded-3xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg"><LayoutGrid className="w-5 h-5 text-sky-500" /> Funciones</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Venta</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                <PosFnTile testid="pos-fn-qty" icon={Hash} tone="slate" label="Cantidad" hint={pendingQty !== null ? `próx. ×${pendingQty}` : null}
                           lockProps={lk("cantidad")} onClick={() => { setFuncOpen(false); setQtyInput(""); setQtyOpen(true); }} />
                <PosFnTile testid="pos-fn-remove-mode" icon={Minus} tone={removeMode ? "rose" : "amber"} label={removeMode ? "Remover activo" : "Remover ítem"}
                           disabled={!cart.length} lockProps={lk("remover")}
                           onClick={() => { setFuncOpen(false); setRemoveMode((v) => { const nv = !v; if (nv) { setLastProduct({ nombre: "🚫 ESCANEAR PRODUCTO A QUITAR", precio: 0, cantidad: 0, sku: "REMOVER" }); toast.info("Modo remover activo: escanea el producto a quitar", { duration: 2500 }); } else setLastProduct(null); setTimeout(refocusScan, 50); return nv; }); }} />
                <PosFnTile testid="pos-fn-suspend" icon={PauseCircle} tone="violet" label="Suspender venta" disabled={cart.length === 0}
                           lockProps={lk("suspender")} onClick={() => { setFuncOpen(false); suspendCurrentSale(); }} />
                <PosFnTile testid="pos-fn-quick-sale" icon={Zap} tone="amber" label="Venta rápida" disabled={!shift || isPreventaTerminal}
                           lockProps={lk("venta_rapida")} onClick={() => { setFuncOpen(false); setQuickMonto(""); setQuickOpen(true); }} />
                <PosFnTile testid="pos-fn-vale" icon={Repeat} tone="indigo" label="Vale de cambio"
                           lockProps={lk("vale_cambio")} onClick={() => { setFuncOpen(false); setValeCodigo(""); setValeOpen(true); }} />
                <PosFnTile testid="pos-fn-cotizacion" icon={FileText} tone="sky" label="Cotización"
                           lockProps={lk("cotizacion")} onClick={() => { setFuncOpen(false); setCotQ(""); setCotRows([]); setCotOpen(true); }} />
                {suspendedCount > 0 && (
                  <PosFnTile testid="pos-suspended-list-btn" icon={PauseCircle} tone="violet" label="Suspendidas" hint={`${suspendedCount}`}
                             onClick={() => { setFuncOpen(false); loadSuspended(); setSuspendedOpen(true); }} />
                )}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Caja</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {!isPreventaTerminal && (
                  <>
                    <PosFnTile testid="pos-reprint-btn" icon={Printer} tone="cyan" label="Reimprimir" disabled={!shift}
                               lockProps={lk("reimprimir")} onClick={() => { setFuncOpen(false); openReprintLast(); }} />
                    <PosFnTile testid="pos-cash-drawer-button" icon={Wallet} tone="sky" label="Ingreso / Retiro" disabled={!shift}
                               lockProps={puede("ingreso") || puede("retiro") ? null : lk("retiro")} onClick={() => { setFuncOpen(false); abrirMov(); }} />
                    {tpMetodo && (
                      <PosFnTile testid="pos-tp-abono-btn" icon={CreditCard} tone="violet" label={`Pago ${tpMetodo.nombre}`} disabled={!shift}
                                 onClick={() => { setFuncOpen(false); setTpAbonoOpen(true); }} />
                    )}
                  </>
                )}
                {operator && (
                  <PosFnTile testid="pos-logout-operator-btn" icon={KeyRound} tone="slate" label="Cerrar sesión cajero"
                             onClick={() => { setFuncOpen(false); clearOperator(); toast.info("Sesión del cajero cerrada"); }} />
                )}
                {shift && (
                  <PosFnTile testid="pos-close-register-btn" icon={Lock} tone="rose" label={isPreventaTerminal ? "Cerrar sesión" : "Cerrar caja"}
                             lockProps={lk("cierre_caja")} onClick={() => { setFuncOpen(false); openCloseModal(); }} />
                )}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Equipo</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                <PosFnTile testid="pos-sync-catalog-btn" icon={RefreshCw} tone="sky" label={catSyncing ? "Sincronizando…" : "Sincronizar"} disabled={catSyncing}
                           onClick={() => sincronizarCatalogo(true)} />
                {pendingCount > 0 && (
                  <PosFnTile testid="pos-pending-sync-btn" icon={CloudUpload} tone="amber" label="Por sincronizar" hint={`${pendingCount}`}
                             onClick={async () => { setFuncOpen(false); await refreshPendingCount(); setPendingOpen(true); }} />
                )}
                {terminal?.has_second_screen && (
                  <>
                    <PosFnTile testid="pos-second-screen-btn" icon={Monitor} tone="cyan" label="2ª pantalla"
                               onClick={() => { setFuncOpen(false); openSecondScreen(); }} />
                    <PosFnTile testid="pos-second-screen-fullscreen-btn" icon={Maximize2} tone="cyan" label="Pantalla completa"
                               onClick={() => { setFuncOpen(false); openSecondScreen({ force: true }); }} />
                  </>
                )}
                {!kioskShell && (
                  <PosFnTile testid="pos-admin-btn" icon={ArrowLeft} tone="slate" label="Administración"
                             onClick={() => nav("/admin")} />
                )}
                {btWanted && btOk && (
                  <PosFnTile testid="pos-fn-printer" icon={Bluetooth} tone={btReady ? "emerald" : "indigo"}
                             label="Impresora" hint={btReady ? "conectada" : (btName || "sin conectar")}
                             onClick={() => { setFuncOpen(false); if (btReady) setBtGuiaOpen(true); else connectBtPrinter(); }} />
                )}
                <div className="rounded-2xl border bg-white border-slate-200 grid place-items-center">
                  <HelpButton className="h-9 rounded-xl text-[12px] px-3" />
                </div>
              </div>
            </div>
            {cacheInfo.fromCache && (
              <p data-testid="pos-cache-indicator" className="text-[11px] font-semibold text-slate-500 flex items-center gap-1.5">
                <Database className="w-3 h-3" /> Datos locales: hace {Math.max(1, Math.round((Date.now() - cacheInfo.at) / 60000))} min · stock ≈
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Banner folios CAF (solo tipos habilitados en el terminal) */}
      {tbkEstado && (
        <div data-testid="pos-tbk-overlay" className="fixed inset-0 z-[200] bg-slate-950/80 grid place-items-center pointer-events-auto">
          <div className="bg-white rounded-3xl px-10 py-8 text-center shadow-2xl">
            <Loader2 className="w-10 h-10 animate-spin text-sky-600 mx-auto" />
            <p className="mt-3 text-xl font-bold text-slate-900">{tbkEstado}</p>
            <p className="text-sm text-slate-500 mt-1">{equipoActivo === "MP" ? "Sigue las instrucciones en el equipo Mercado Pago Point" : "Sigue las instrucciones en el POS Transbank"}</p>
            {(equipoActivo === "MP" || equipoActivo === "C2C") && (
              <button data-testid="pos-mp-cancelar" onClick={() => { mpCancelRef.current = true; }} className="mt-4 text-sm font-semibold text-rose-600 hover:underline">Cancelar cobro</button>
            )}
          </div>
        </div>
      )}
      <CafFoliosBanner terminal={terminal} />
      {shift && <TurnosAntiguosBanner terminalId={terminalId} compact />}

      {modsPick && (
        <ElegirModificadores product={modsPick.p} onCancel={() => setModsPick(null)}
          onConfirm={(res) => { const m = modsPick; setModsPick(null); addToCart({ ...m.p, _mods: res }, m.qty, m.precio); }} />
      )}
      <AndroidPosGuard enabled={androidPos} keyboard={kbOn} escOcupado={removeMode || pendingQty != null || !!q} />
      {androidPos && !isTile && (
        <button type="button" data-testid="pos-android-kb-toggle" onClick={() => setKbOn((v) => !v)}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }} style={{ pointerEvents: "auto" }}
                title={kbOn ? "Ocultar teclado en pantalla" : "Mostrar teclado en pantalla"}
                className={`fixed bottom-24 left-3 z-[60] print:hidden w-10 h-10 rounded-full shadow-lg grid place-items-center transition-colors ${kbOn ? "bg-sky-600 text-white" : "bg-white/90 text-slate-500 border border-slate-200"}`}>
          <Keyboard className="w-4 h-4" />
        </button>
      )}
      {usbMode && !usbReady && (
        <div data-testid="pos-usb-connect-banner" className="px-4 py-2.5 bg-sky-700 text-white print:hidden flex flex-wrap items-center gap-3">
          <Usb className="w-4 h-4 shrink-0" />
          <p className="text-xs sm:text-sm font-semibold flex-1 min-w-0">
            {isUsbSupported()
              ? <>Impresora USB 80 mm{usbSavedName() ? <> <strong>{usbSavedName()}</strong></> : ""}: conéctala y toca el botón (solo la primera vez) para que boletas, facturas y gaveta funcionen solas.</>
              : <>Este navegador no permite USB directo: abre Ai-POS en <strong>Google Chrome</strong> para imprimir en la USB de 80 mm.</>}
          </p>
          {isUsbSupported() && (
            <Button data-testid="pos-usb-connect-btn" size="sm" disabled={usbBusy} onClick={connectUsbPrinter}
                    className="bg-white text-sky-800 hover:bg-sky-50 rounded-xl font-bold border-0">
              {usbBusy ? "Conectando…" : "Conectar impresora USB"}
            </Button>
          )}
        </div>
      )}
      {usbMode && usbReady && usbEstado && (
        <div data-testid="pos-usb-status-banner"
             className={`px-4 py-2 print:hidden flex items-center gap-2 text-xs sm:text-sm font-bold ${usbEstado.nivel === "error" ? "bg-rose-600 text-white" : "bg-amber-100 text-amber-900"}`}>
          <Printer className="w-4 h-4 shrink-0" />
          <span className="flex-1">{usbEstado.texto}</span>
          <button type="button" data-testid="pos-usb-status-refresh" onClick={() => revisarUsb()} className="underline text-xs font-semibold">Revisar de nuevo</button>
        </div>
      )}
      {usbMode && usbReady && !usbEstado && (
        <div data-testid="pos-usb-ready-chip" className="px-4 py-1 bg-emerald-50 text-emerald-800 text-[11px] font-bold print:hidden flex items-center gap-1.5">
          <Usb className="w-3 h-3" /> Impresora USB conectada{usbSavedName() ? ` · ${usbSavedName()}` : ""}
        </div>
      )}
      {btWanted && btOk && !btReady && (
        <div data-testid="pos-bt-connect-banner" className="px-4 py-2.5 bg-indigo-600 text-white print:hidden flex flex-wrap items-center gap-3">
          <Bluetooth className="w-4 h-4 shrink-0" />
          <p className="text-xs sm:text-sm font-semibold flex-1 min-w-0">
            Impresora <strong>{btName || "Bluetooth"}</strong>: {btPersiste
              ? "está apagada o fuera de alcance. Enciéndela y se conecta sola."
              : "toca una vez para conectarla en este equipo."}
          </p>
          {!btPersiste && (
            <Button data-testid="pos-bt-guia-btn" size="sm" variant="outline"
                    onClick={() => setBtGuiaOpen(true)}
                    className="bg-transparent text-white border-white/50 hover:bg-white/10 rounded-xl font-bold">
              Recordarla siempre
            </Button>
          )}
          <Button data-testid="pos-bt-connect-btn" size="sm" disabled={btBusy} onClick={connectBtPrinter}
                  className="bg-white text-indigo-700 hover:bg-indigo-50 rounded-xl font-bold border-0">
            {btBusy ? "Conectando…" : "Conectar impresora"}
          </Button>
        </div>
      )}
      {btWanted && btOk && btReady && btProblem && (
        <div data-testid="pos-printer-alert" className="px-4 py-2.5 bg-rose-600 text-white print:hidden flex flex-wrap items-center gap-3">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <p className="text-xs sm:text-sm font-bold flex-1 min-w-0">
            Impresora {btName || ""}: <strong>{btProblem}</strong>. Los vales y boletas no están saliendo — revisa el papel y la tapa.
          </p>
          <Button data-testid="pos-printer-recheck-btn" size="sm"
                  onClick={async () => { const st = await btQueryStatus(); setBtStatus(st); if (!st || !(st.paper_out || st.cover_open || st.offline)) toast.success("Impresora lista"); }}
                  className="bg-white text-rose-700 hover:bg-rose-50 rounded-xl font-bold border-0">
            Ya lo resolví
          </Button>
        </div>
      )}
      {btWanted && btOk && btReady && !btProblem && (
        <div data-testid="pos-bt-ready-chip" className="px-4 py-1 bg-emerald-50 text-emerald-800 text-[11px] font-bold print:hidden flex items-center gap-1.5">
          <Bluetooth className="w-3 h-3" /> Impresora Bluetooth conectada{btName ? ` · ${btName}` : ""}
        </div>
      )}

      {/* Barra de estado atajos */}
      <div className={`${isTile ? "hidden" : "hidden sm:flex"} flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5 bg-slate-900 text-slate-300 text-[11px] font-mono-num print:hidden`}>
        <span className="flex items-center gap-1"><Keyboard className="w-3 h-3" /> Atajos:</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">FIN</kbd> Cobrar</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">SUPR</kbd> Limpiar</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">REPAG</kbd> Remover</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">nº F9</kbd> Cantidad</span>
        {enterRepiteUltimo && (
          <span data-testid="pos-atajo-enter"><kbd className="bg-slate-700 px-1.5 rounded">nº ENTER</kbd> Repite último</span>
        )}
        <span><kbd className="bg-slate-700 px-1.5 rounded">F1</kbd> Venta rápida</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">F2</kbd> Ingreso/Retiro</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">F3</kbd> Suspender</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">F8</kbd> Cerrar sesión</span>
        <span><kbd className="bg-slate-700 px-1.5 rounded">F10</kbd> Cerrar caja</span>
        {qtyBuffer && <span className="text-amber-300 ml-2">buffer: {qtyBuffer}</span>}
        {pendingQty !== null && <span data-testid="pos-pending-qty" className="text-emerald-300 ml-2 font-bold">Próx. ×{pendingQty}</span>}
        {removeMode && <span data-testid="pos-remove-mode" className="text-rose-300 ml-2 font-bold">⚠ Modo remover activo</span>}
        <span data-testid="pos-clock" className="ml-auto text-slate-100 font-bold tabular-nums">
          {nowTs.toLocaleDateString("es-CL", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}
          {" · "}
          {nowTs.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
        </span>
      </div>

      {/* Main split */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden print:hidden min-h-0">
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
          <div className="mb-4 relative">
            <BarcodeIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              ref={searchRef} data-testid="pos-barcode-search-input" value={q}
              onChange={(e) => { setQ(e.target.value); setRetailSel(0); }} onKeyDown={onBarcodeEnter}
              placeholder={uiMode === "RETAIL" ? "Escanea código o busca por descripción — flechas para navegar, Enter para agregar" : "Buscar producto o escanear código de barras..."}
              className={`pl-10 rounded-2xl border-4 border-emerald-500/70 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-300/60 font-mono-num ${uiMode === "RETAIL" ? "h-16 text-xl" : "h-12 text-base"}`}
            />
          </div>
          {uiMode === "TILE" && (
            <>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                <button data-testid="pos-category-tab-all" onClick={() => setCat("ALL")}
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${cat === "ALL" ? "bg-sky-500 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-sky-50"}`}>Todos</button>
                {categories.map((c) => (
                  <button key={c.category_id} data-testid={`pos-category-tab-${c.nombre}`} onClick={() => setCat(c.category_id)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${cat === c.category_id ? "bg-sky-500 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-sky-50"}`}>{c.nombre}</button>
                ))}
              </div>
              {/* Promociones vigentes para POS */}
              {promotions.filter((pr) => pr.is_active && pr.canal_pos).length > 0 && (
                <div data-testid="pos-promotions-strip" className="mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700 mb-1.5 flex items-center gap-1">
                    <span>🎉</span> Promociones vigentes — click para agregar al carrito
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {promotions.filter((pr) => pr.is_active && pr.canal_pos).map((pr) => {
                      const regular = (pr.items || []).reduce((s, pi) => {
                        const prod = products.find((x) => x.product_id === pi.product_id);
                        return s + ((prod?.precio || 0) * pi.cantidad);
                      }, 0);
                      const saving = Math.max(0, regular - (pr.precio_bundle || 0));
                      return (
                        <button
                          key={pr.promotion_id}
                          data-testid={`pos-promo-card-${pr.promotion_id}`}
                          onClick={() => applyPromotion(pr)}
                          className="shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white px-4 py-2.5 text-left hover:shadow-lg hover:-translate-y-0.5 active:scale-95 transition min-w-[190px]"
                        >
                          <p className="text-xs font-bold leading-tight truncate">{pr.nombre}</p>
                          <div className="flex items-center justify-between gap-2 mt-1">
                            <span className="text-base font-extrabold font-mono-num leading-none">{clp(pr.precio_bundle)}</span>
                            {saving > 0 && (
                              <span className="text-[10px] bg-white/25 rounded-full px-1.5 py-0.5 font-bold">-{clp(saving)}</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2">
                {filtered.slice(0, visibles).map((p) => (
                  <button key={p.product_id} data-testid={`pos-product-card-${p.sku}`}
                    onClick={() => p.es_pesable ? openWeightModal(p) : addWithPendingQty(p)}
                    className="bg-white rounded-2xl border border-slate-200 hover:border-sky-400 hover:shadow-lg active:scale-[0.98] transition-all p-2 text-left overflow-hidden relative">
                    {p.es_pesable && <span className="absolute top-1.5 right-1.5 z-10 bg-amber-500 text-white text-[9px] font-bold rounded px-1 py-0.5 flex items-center gap-0.5"><Scale className="w-2.5 h-2.5" />KG</span>}
                    {p.image_url ? (
                      <img src={resolveMedia(p.image_url)} alt="" className="w-full aspect-square object-cover rounded-lg mb-1" />
                    ) : (
                      <div className="w-full aspect-square rounded-lg bg-sky-50 grid place-items-center text-sky-500 text-2xl font-bold mb-1">{p.nombre.charAt(0)}</div>
                    )}
                    <p className="text-[11px] font-semibold text-slate-900 line-clamp-2 leading-tight min-h-[1.8rem]">{p.nombre}</p>
                    {normalizeTiers(p.precios_cantidad).length > 0 && (
                      <p className="text-[9px] font-bold text-amber-700 leading-tight" data-testid={`pos-tiers-${p.sku}`}>
                        {tiersLabel(p.precios_cantidad, clp)}
                      </p>
                    )}
                    <div className="flex items-end justify-between mt-1">
                      <span className="text-base font-extrabold text-slate-900 font-mono-num leading-none">{clp(p.precio)}</span>
                      <span data-testid={`pos-stock-${p.sku}`}
                        title={stockApprox ? "Stock aproximado: sin conexión se confirma al sincronizar" : undefined}
                        className={`text-[10px] font-mono-num ${stockOf(p) <= (p.stock_minimo || 0) ? "text-amber-600 font-semibold" : "text-slate-400"}`}>
                        {stockApprox ? "≈" : "×"}{stockOf(p)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              {filtered.length > visibles && (
                <div className="mt-3 flex flex-col items-center gap-2">
                  <p className="text-xs text-slate-500">
                    Mostrando {visibles.toLocaleString("es-CL")} de {filtered.length.toLocaleString("es-CL")} productos · busca o escanea para encontrar más rápido
                  </p>
                  <Button
                    data-testid="pos-show-more-products"
                    variant="outline"
                    onClick={() => setVisibles((n) => n + PAGINA_GRID)}
                    className="rounded-xl"
                  >
                    Ver más productos
                  </Button>
                </div>
              )}
            </>
          )}
          {uiMode === "RETAIL" && (
            <div className="space-y-3" data-testid="pos-retail-panel">
              {/* Resultados de búsqueda por descripción (flechas + Enter) */}
              {q.trim().length >= 2 && filtered.length > 0 && (
                <div className="rounded-2xl bg-white border-2 border-sky-200 p-2 space-y-1 max-h-[55vh] overflow-y-auto" data-testid="pos-retail-search-results">
                  {filtered.slice(0, RETAIL_MAX_RESULTADOS).map((p, idx) => (
                    <button
                      key={p.product_id}
                      ref={idx === retailSel ? (el) => el?.scrollIntoView({ block: "nearest" }) : undefined}
                      data-testid={`pos-retail-row-${p.sku}`}
                      onClick={() => { p.es_pesable ? openWeightModal(p) : addWithPendingQty(p); setQ(""); setRetailSel(0); refocusScan(); }}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg text-left ${idx === retailSel ? "bg-sky-100 ring-2 ring-sky-400" : "hover:bg-sky-50"}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold text-slate-900 truncate">{p.nombre}</p>
                        <p className="text-[11px] text-slate-500 font-mono-num">SKU {p.sku} · Stock {stockApprox ? "≈" : ""}{stockOf(p)}</p>
                      </div>
                      <span className="text-lg font-extrabold text-sky-700 font-mono-num">{clp(p.precio)}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Artículos marcados — estilo supermercado: el centro de la pantalla es la lista */}
              <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-[1fr_72px_110px_120px] gap-2 px-4 py-2 bg-slate-900 text-slate-300 text-[11px] font-bold uppercase tracking-wider">
                  <span>Artículo</span><span className="text-center">Cant.</span>
                  <span className="text-right">Precio</span><span className="text-right">Subtotal</span>
                </div>
                {cart.length === 0 ? (
                  <p data-testid="pos-retail-empty" className="py-16 text-center text-slate-400 text-lg">
                    Escanea el primer producto para comenzar
                  </p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {[...cart].reverse().map((it, i) => (
                      <div key={it.product_id + i} data-testid={`pos-retail-line-${it.sku}`}
                           className={`grid grid-cols-[1fr_72px_110px_120px] gap-2 px-4 py-2.5 items-center ${i === 0 ? "bg-sky-50" : ""}`}>
                        <div className="min-w-0">
                          <p className="text-base font-bold text-slate-900 truncate">{it.nombre}</p>
                          {it.modificadores?.length > 0 && <p className="text-xs text-sky-700 font-semibold truncate" data-testid={`pos-line-mods-${it.sku}`}>{it.modificadores.map((m) => m.nombre).join(" · ")}</p>}
                          <p className="text-[11px] text-slate-500 font-mono-num">SKU {it.sku}</p>
                        </div>
                        <span className="text-center text-lg font-extrabold font-mono-num text-slate-700">{it.cantidad}</span>
                        <span className="text-right text-base font-mono-num text-slate-600">{clp(it.precio)}</span>
                        <span className="text-right text-xl font-black font-mono-num text-slate-900">{clp(it.precio * it.cantidad)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {promoResult.applied.length > 0 && (
                  <div className="divide-y divide-emerald-100 border-t-2 border-emerald-200 bg-emerald-50">
                    {promoResult.applied.map((a, i) => (
                      <div key={`promo-${i}`} data-testid={`pos-retail-promo-${a.promotion_id}`}
                           className="grid grid-cols-[1fr_72px_110px_120px] gap-2 px-4 py-2.5 items-center">
                        <div className="min-w-0">
                          <p className="text-base font-bold text-emerald-800 truncate">🎉 {a.nombre}</p>
                          <p className="text-[11px] text-emerald-600 font-semibold uppercase tracking-wider">Promoción aplicada</p>
                        </div>
                        <span className="text-center text-lg font-extrabold font-mono-num text-emerald-700">—</span>
                        <span className="text-right text-base font-mono-num text-emerald-600">—</span>
                        <span className="text-right text-xl font-black font-mono-num text-emerald-700">-{clp(a.savings)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {vale && (
                  <div data-testid="pos-retail-vale-row" className="border-t-2 border-indigo-200 bg-indigo-50 grid grid-cols-[1fr_72px_110px_120px] gap-2 px-4 py-2.5 items-center">
                    <div className="min-w-0">
                      <p className="text-base font-bold text-indigo-800 truncate flex items-center gap-1.5"><Repeat className="w-4 h-4" /> Vale de cambio #{vale.codigo}</p>
                      <p className="text-[11px] text-indigo-600 font-semibold uppercase tracking-wider">Valor del vale {clp(vale.total)}</p>
                    </div>
                    <span className="text-center text-lg font-extrabold font-mono-num text-indigo-700">—</span>
                    <span className="text-right text-base font-mono-num text-indigo-600">—</span>
                    <span className="text-right text-xl font-black font-mono-num text-indigo-700">-{clp(valeMonto)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
          {/* Último ítem marcado: queda bajo la grilla, sin cruzar el panel del carrito */}
      {lastProduct && (
        <div data-testid="pos-last-product-banner" className={`shrink-0 border-t ${isTile ? "px-4 py-1.5" : "px-4 py-2.5"} ${removeMode ? "bg-rose-500 text-white" : `bg-gradient-to-r ${BANNER_COLORS[bannerColor] || BANNER_COLORS.sky} text-white`} print:hidden`}>
          {isTile ? (
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest opacity-70 font-bold shrink-0">Último</span>
              <p className="flex-1 min-w-0 truncate text-base font-bold font-display">{lastProduct.nombre}</p>
              <p className="text-lg font-extrabold font-mono-num shrink-0">{clp(lastProduct.precio)}</p>
              <button data-testid="pos-last-product-close" onClick={() => setLastProduct(null)} className="text-white/60 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </div>
          ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-widest opacity-80 font-semibold">Último ítem</p>
              <p className="text-2xl sm:text-3xl font-extrabold font-display leading-tight truncate">{lastProduct.nombre}</p>
              <p className="text-xs opacity-80 mt-0.5">SKU {lastProduct.sku} · Cantidad {lastProduct.cantidad}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl sm:text-4xl font-extrabold font-mono-num">{clp(lastProduct.precio)}</p>
            </div>
            <button data-testid="pos-last-product-close" onClick={() => setLastProduct(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          )}
        </div>
      )}

      {/* Modal cobro */}
      {/* Aviso al cajero: producto con venta restringida por edad */}
      <Dialog open={ageOpen} onOpenChange={setAgeOpen}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="pos-age-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black font-display tracking-tight text-rose-700">
              Pide la cédula · venta {edadMinimaCarro}+
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Estos productos solo se pueden vender a mayores de {edadMinimaCarro} años.
              Verifica la cédula del cliente antes de cobrar:
            </p>
            <ul className="rounded-xl bg-rose-50 border border-rose-100 p-3 space-y-1">
              {itemsRestringidos.map((x) => (
                <li key={x.product_id} data-testid={`pos-age-item-${x.product_id}`}
                    className="text-sm font-semibold text-rose-900 flex justify-between gap-2">
                  <span>{x.nombre}</span>
                  <span className="font-mono-num">×{x.cantidad}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500">
              Queda registrado en la venta que tú autorizaste: {operator?.name || user?.name}.
            </p>
          </div>
          <DialogFooter>
            <Button data-testid="pos-age-cancel" variant="outline" onClick={() => setAgeOpen(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="pos-age-confirm"
              onClick={() => {
                const auth = { modo: "VISUAL", edad_minima: edadMinimaCarro,
                               productos: itemsRestringidos.map((x) => x.nombre) };
                ageAuthRef.current = auth;
                setAgeAuth(auth);
                setAgeOpen(false);
                setTimeout(openPayModal, 0);
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
            >
              Verifiqué la cédula · continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={cerrarCobro}>
        {/* Ventana de cobro: tamaño fijo y horizontal, se adapta a la pantalla del equipo */}
        <DialogContent
          data-testid="pos-pay-dialog"
          className="rounded-2xl p-4 sm:p-5 gap-3 flex flex-col w-[96vw] max-w-[1180px] h-[92vh] max-h-[760px]"
        >
          <DialogHeader className="sr-only"><DialogTitle>Total a pagar</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[250px_1fr_280px] gap-3">
          {/* Columna 1 · documento y forma de pago */}
          <div className="space-y-3 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
            {presaleTickets.length > 0 && (
              <div data-testid="pos-pay-presale-list" className="rounded-xl bg-sky-50 border border-sky-100 p-3 text-xs">
                <p className="font-semibold text-sky-900 mb-1">Cobrando {presaleTickets.length} pre-venta(s):</p>
                <div className="flex flex-wrap gap-1.5">
                  {presaleTickets.map((t) => (
                    <span key={t.codigo} className="font-mono-num bg-white border border-sky-200 px-2 py-0.5 rounded">{t.codigo}</span>
                  ))}
                </div>
              </div>
            )}
            {puedeDescuentoManual && cupon && (
              <p data-testid="pos-desc-manual-bloqueado" className="text-xs font-semibold text-slate-500 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                Descuento manual no disponible: hay un cupón aplicado (quítalo para usar el descuento manual).
              </p>
            )}
            {puedeDescuentoManual && !cupon && (
              <DescuentoManual value={descManual} onChange={(v) => { setDescManual(v); setDescPin(null); }}
                base={total - (redeemValue || 0)} tope={Number(terminal?.descuento_max_pct || 0)}
                autorizadoPor={descPin?.nombre} onAutorizar={autorizarDescuento}
                onEnter={() => { const el = recibidoRef.current; if (el) { el.focus(); el.select?.(); } else confirmVentaRef.current?.focus(); }} />
            )}
            {puedeCupon && (
              <CuponPOS value={cupon} onChange={setCupon} base={total - (redeemValue || 0)} storeId={store?.store_id}
                        bloqueado={descManualMonto > 0} />
            )}
            {enabledDocs.length > 0 && (
              <div data-testid="pos-pay-doc-selector">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Tipo de documento</p>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    data-testid="pos-pay-doc-boleta"
                    type="button"
                    onClick={() => setPayDocType("BOLETA")}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition ${payDocType === "BOLETA" ? "bg-sky-500 text-white border-sky-500 shadow-md" : "bg-white text-slate-700 border-slate-200 hover:border-sky-300"}`}
                  >
                    Boleta
                    <span className={`block text-[9px] font-normal mt-0.5 ${payDocType === "BOLETA" ? "text-sky-100" : "text-slate-400"}`}>Afecta o exenta automática</span>
                  </button>
                  {enabledDocs.map((d) => {
                    const active = payDocType === d.key;
                    return (
                      <button
                        key={d.key}
                        data-testid={`pos-pay-doc-${d.key.toLowerCase()}`}
                        type="button"
                        onClick={() => { setPayDocType(d.key); if (d.key === "FACTURA") setFacturaModal(true); }}
                        className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition ${active ? (d.key === "TICKET" ? "bg-violet-600 text-white border-violet-600 shadow-md" : "bg-sky-500 text-white border-sky-500 shadow-md") : "bg-white text-slate-700 border-slate-200 hover:border-sky-300"}`}
                      >
                        {d.label}
                        <span className={`block text-[9px] font-normal mt-0.5 ${active ? (d.key === "TICKET" ? "text-violet-100" : "text-sky-100") : "text-slate-400"}`}>{d.hint}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {payDocType === "FACTURA" && (
              <button type="button" onClick={() => setFacturaModal(true)} data-testid="pos-pay-factura-resumen"
                className="w-full text-left rounded-xl border-2 border-sky-200 bg-sky-50/60 p-2.5 hover:bg-sky-100 transition-colors">
                <p className="text-[10px] font-bold uppercase tracking-widest text-sky-800">Cliente de la factura · editar</p>
                <p className="text-sm font-bold text-slate-900 truncate">{facturaData.razon_social || "Sin datos: toca para ingresarlos"}</p>
                {facturaData.rut && <p className="text-[11px] text-slate-500">{facturaData.rut}</p>}
              </button>
            )}
            <FacturaClienteModal open={facturaModal} onOpenChange={setFacturaModal} value={facturaData}
              onChange={setFacturaData} obs={facturaObs} onObs={setFacturaObs} transporte={facturaTransp} onTransporte={setFacturaTransp}
              referencias={facturaRefs} onReferencias={setFacturaRefs} storeId={terminal?.store_id} />
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Forma de pago · F1-F4</p>
            <div className="grid grid-cols-2 gap-2">
              {posPayMethods.map((m, idx) => {
                const meta = pmMeta(m.codigo);
                const Icon = meta.icon;
                const active = payMethod === m.codigo;
                const blocked = !payAllowed(m.codigo);
                return (
                  <button key={m.method_id} data-testid={`pos-pay-method-${m.codigo.toLowerCase()}`}
                    onClick={() => pickMethod(m)}
                    disabled={parciales.some((p) => p.codigo === m.codigo)}
                    className={`relative rounded-2xl overflow-hidden border-2 text-left transition-all ${active ? "border-slate-900 shadow-lg -translate-y-0.5" : "border-transparent hover:border-slate-200"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                    <div className={`bg-gradient-to-br ${meta.gradient} text-white p-2.5 h-[68px] flex flex-col justify-between ${blocked ? "grayscale opacity-60" : ""}`}>
                      <Icon className="w-5 h-5 opacity-95" />
                      <p className="text-[11px] font-bold leading-tight line-clamp-2">{m.nombre}</p>
                    </div>
                    {idx < 4 && (
                      <span data-testid={`pos-pay-method-key-${idx + 1}`}
                            className="absolute bottom-1 right-1.5 px-1.5 py-0.5 rounded-md bg-black/35 text-[9px] font-black text-white tracking-wider">
                        F{idx + 1}
                      </span>
                    )}
                    {blocked && (
                      <span data-testid={`pos-pay-method-locked-${m.codigo.toLowerCase()}`}
                            className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/95 text-[9px] font-bold text-slate-700 shadow">
                        <Lock className="w-2.5 h-2.5" /> CON PLAN
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Columna 2 · cobro: el total va arriba solo en el centro, los laterales suben */}
          <div className="space-y-3 lg:min-h-0 lg:overflow-y-auto px-0.5">
            <div className="text-center pt-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-slate-400">Total a pagar</p>
              <p data-testid="pos-pay-total" className="font-mono-num font-black tracking-tight leading-none text-5xl lg:text-6xl xl:text-7xl text-slate-900">
                {clp(total - descManualMonto - cuponMonto - valeMonto)}
              </p>
              {valeMonto > 0 && (
                <p data-testid="pos-pay-total-vale" className="text-sm font-bold text-emerald-700 mt-1">
                  Vale de cambio {vale.codigo} aplicado: -{clp(valeMonto)}
                </p>
              )}
              {cuponMonto > 0 && (
                <p data-testid="pos-pay-total-cupon" className="text-sm font-bold text-emerald-700 mt-1">
                  <span className="line-through text-slate-400 mr-2">{clp(total)}</span>
                  Cupón {cupon.code} -{clp(cuponMonto)}
                </p>
              )}
              {descManualMonto > 0 && (
                <p data-testid="pos-pay-total-descuento" className="text-sm font-bold text-amber-700 mt-1">
                  <span className="line-through text-slate-400 mr-2">{clp(total)}</span>
                  Descuento manual -{clp(descManualMonto)}
                </p>
              )}
            </div>
            {parciales.length > 0 && (
              <div data-testid="pos-pay-parciales" className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
                {parciales.map((p, i) => (
                  <div key={p.codigo} data-testid={`pos-pay-parcial-${i}`} className="flex items-center justify-between gap-2 px-4 py-2">
                    <span className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      {p.nombre}
                      {p.via && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">APROBADO</span>}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono-num text-lg font-black text-slate-900">{clp(p.monto)}</span>
                      {!p.via && (
                        <button data-testid={`pos-pay-parcial-remove-${i}`} onClick={() => quitarParcial(i)} title="Quitar este abono"
                                className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                {parciales.length ? `Saldo pendiente · ${clp(saldoUi)}` : "Recibido"}
              </Label>
              <Input data-testid="pos-cash-tendered-input" ref={recibidoRef} type="text" inputMode="numeric"
                     value={miles(recibido)}
                     autoFocus
                     onFocus={(e) => e.target.select()}
                     onChange={(e) => { setRecibidoTouched(true); setRecibido(soloDigitos(e.target.value)); }}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); charge(); } }}
                     className="h-20 rounded-2xl text-5xl md:text-5xl font-mono-num font-black text-right border-2 border-slate-900/20 focus-visible:border-sky-500" />
              <p className="text-[11px] text-slate-500 mt-1">
                Escribe un monto menor y elige el medio para pagar en partes · <kbd className="bg-slate-100 px-1 rounded font-mono-num">Enter</kbd> cobra
              </p>
            </div>
            {!isCashMethod(payMethod) && (
              <div data-testid="pos-pay-method-summary" className="rounded-2xl border-2 border-slate-900/10 bg-slate-50 p-5 text-center">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Cobrar con</p>
                <p className="text-2xl font-black text-slate-900 mt-1">
                  {methods.find((m) => m.codigo === payMethod)?.nombre || payMethod}
                </p>
                <p className="mt-2 font-mono-num text-3xl font-black text-slate-900">{clp(Number(recibido || 0))}</p>
                {payMethod === "TARJETA_PROPIA" ? (
                  <div className="mt-3 space-y-3">
                    <TPValidar nombre={tpMetodo?.nombre || "Tarjeta Propia"} monto={Number(montoSugerido("TARJETA_PROPIA"))} auth={tpAuth} onAuth={setTpAuth} />
                    {tpAuth && <p className="text-xs text-slate-500">Confirma la venta para cargarla a la tarjeta.</p>}
                  </div>
                ) : usaTbk(payMethod) ? (
                  <>
                    {tbkError && (
                      <p data-testid="pos-tbk-error" className="mt-2 text-sm font-bold text-rose-600">Transbank: {tbkError}</p>
                    )}
                    <Button data-testid="pos-tbk-retry" onClick={() => charge({ methodOverride: payMethod })} disabled={!!tbkEstado}
                            className="mt-3 bg-sky-500 hover:bg-sky-600 text-white">
                      {tbkError ? "Reintentar en POS Transbank" : "Enviar al POS Transbank"}
                    </Button>
                    <p className="text-xs text-slate-500 mt-2">Si no se aprueba, puedes reintentar o elegir otro medio de pago.</p>
                  </>
                ) : (
                  <p className="text-xs text-slate-500 mt-2">Confirma la venta cuando el pago esté aprobado.</p>
                )}
              </div>
            )}
            {isCashMethod(payMethod) && (
              <>
                <div className="grid grid-cols-5 gap-2">
                  {DENOMS.map((d) => (
                    <button key={d} data-testid={`pos-cash-bill-${d}`} type="button"
                      onClick={() => {
                        // Billete que alcanza el total ⇒ cobra al instante (sin sumar ni confirmar)
                        const acumulado = recibidoTouched ? Number(recibido || 0) + d : d;
                        const monto = d >= totalAPagar ? d : acumulado;
                        setRecibido(monto);
                        setRecibidoTouched(true);
                        if (monto >= totalAPagar) charge({ cashOverride: monto, methodOverride: payMethod });
                      }}
                      className={`h-14 rounded-xl border-2 text-sm font-mono-num font-extrabold shadow-sm hover:-translate-y-0.5 active:scale-95 transition-all ${BILL_STYLES[d]}`}>
                      {clp(d).replace(/\s/g, "")}
                    </button>
                  ))}
                </div>

                <div className="bg-emerald-50 rounded-xl px-4 py-3 flex justify-between items-center">
                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">Vuelto</span>
                  <span data-testid="pos-cash-change-display" className="text-4xl font-black font-mono-num text-emerald-700">{clp(vuelto)}</span>
                </div>
                {roundInfo.ajuste !== 0 && (
                  <div data-testid="pos-rounding-box" className="bg-amber-50 border border-amber-100 rounded-xl p-3 space-y-1">
                    <div className="flex justify-between text-xs text-amber-900">
                      <span>Total documento</span>
                      <span className="font-mono-num">{clp(roundInfo.total_documento)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-amber-900">
                      <span>Redondeo legal</span>
                      <span data-testid="pos-rounding-adjust" className="font-mono-num">
                        {roundInfo.ajuste > 0 ? "+" : ""}{clp(roundInfo.ajuste)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm font-bold text-amber-900 pt-1 border-t border-amber-200">
                      <span>Paga en efectivo</span>
                      <span data-testid="pos-rounding-total" className="font-mono-num">{clp(totalAPagar)}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Columna 3 · teclado, boleta digital y cliente */}
          <div className="space-y-3 lg:min-h-0 lg:overflow-y-auto pl-0.5">
            {(
              <NumPad testid="pos-cash-numpad" value={recibidoTouched ? String(recibido ?? "") : ""} enterLabel="Cobrar"
                      onChange={(v) => { setRecibidoTouched(true); setRecibido(v); }} onEnter={() => charge()} />
            )}
            {terminal?.email_receipt_enabled && (
              <div data-testid="pos-receipt-email-box" className="rounded-xl bg-sky-50 border border-sky-100 p-2.5">
                <Label className="text-[11px] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Correo del cliente (boleta digital)</Label>
                <Input data-testid="pos-receipt-email-input" type="email" value={receiptEmail}
                       placeholder="cliente@correo.cl"
                       onChange={(e) => setReceiptEmail(e.target.value)}
                       className="h-10 rounded-xl mt-1" />
              </div>
            )}
            {loyaltyActive && presaleTickets.length === 0 && (              <div data-testid="pos-loyalty-section" className="rounded-2xl border border-sky-100 bg-sky-50/60 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-sky-600" />
                  <p className="text-sm font-bold text-sky-900">Cliente para fidelización <span className="font-normal text-slate-500">(opcional)</span></p>
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <Input data-testid="pos-loyalty-rut-input" placeholder="RUT o escanea la cédula" value={custRut}
                    onChange={(e) => {
                      const v = e.target.value;
                      // El lector de cédula escribe la URL del Registro Civil carácter por
                      // carácter: se guarda cruda y se procesa cuando termina de escribir.
                      if (lectorCedula.alEscribir(v)) { setCustRut(v); return; }
                      setCustRut(formatRut(v));
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      if (lectorCedula.alEnter(custRut)) return;
                      lookupCustomer();
                    }}
                    className={`h-11 rounded-xl font-mono-num ${custRut && !validateRut(custRut) ? "border-rose-400" : ""}`} />
                  <Button data-testid="pos-loyalty-lookup-btn" type="button" variant="outline" onClick={() => lookupCustomer()}
                    disabled={!custRut || custLoading} className="h-11 rounded-xl px-3">
                    <Search className="w-4 h-4 mr-1" /> Buscar
                  </Button>
                </div>
                {custNacimiento && (
                  <p data-testid="pos-loyalty-birthdate" className="text-[11px] text-sky-700 font-semibold">
                    Cédula: nacimiento {custNacimiento.split("-").reverse().join("-")}
                    {calcularEdad(custNacimiento) != null ? ` · ${calcularEdad(custNacimiento)} años` : ""}
                  </p>
                )}
                <Input data-testid="pos-loyalty-name-input" placeholder="Nombre del cliente" value={custName}
                  onChange={(e) => setCustName(e.target.value)} className="h-11 rounded-xl" />
                {custInfo?.found && (
                  <div data-testid="pos-loyalty-badge" className="flex items-center justify-between rounded-xl bg-white border border-sky-100 px-3 py-2">
                    <div className="text-xs">
                      <p className="font-semibold text-slate-900">{custInfo.name || custName || "Cliente"}</p>
                      <p className="text-slate-500">Saldo: <span className="font-mono-num font-bold text-emerald-600">{clp(custInfo.balance || 0)}</span></p>
                    </div>
                  </div>
                )}
                {custInfo?.found && maxRedeem > 0 && (
                  <div data-testid="pos-loyalty-redeem" className="rounded-xl bg-white border border-emerald-200 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-emerald-800">Usar puntos como descuento</p>
                      <button
                        data-testid="pos-loyalty-redeem-max"
                        type="button"
                        onClick={() => setRedeemAmount(String(maxRedeem))}
                        className="text-[11px] font-bold text-emerald-700 underline underline-offset-2"
                      >
                        Usar todo ({clp(maxRedeem)})
                      </button>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Input
                        data-testid="pos-loyalty-redeem-input"
                        inputMode="numeric"
                        placeholder={`Máximo ${clp(maxRedeem)}`}
                        value={redeemAmount}
                        onChange={(e) => setRedeemAmount(e.target.value.replace(/[^0-9]/g, ""))}
                        className="h-11 rounded-xl font-mono-num"
                      />
                      <Button
                        data-testid="pos-loyalty-redeem-clear" type="button" variant="outline"
                        onClick={() => setRedeemAmount("")} disabled={!redeemAmount}
                        className="h-11 rounded-xl px-3"
                      >
                        Quitar
                      </Button>
                    </div>
                    {redeemValue > 0 && (
                      <div data-testid="pos-loyalty-redeem-summary" className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-900">
                        <p className="flex justify-between"><span>Total venta</span><span className="font-mono-num">{clp(total)}</span></p>
                        <p className="flex justify-between"><span>Canje de puntos</span><span className="font-mono-num font-bold">− {clp(redeemValue)}</span></p>
                        <p className="flex justify-between mt-1 pt-1 border-t border-emerald-200 text-sm font-bold">
                          <span>A pagar</span><span className="font-mono-num">{clp(Math.max(0, total - redeemValue))}</span>
                        </p>
                      </div>
                    )}
                    {loyaltyCfg?.min_redeem_amount > 0 && (
                      <p className="text-[11px] text-slate-400">Canje mínimo {clp(loyaltyCfg.min_redeem_amount)} · se confirma al procesar la venta y queda registrado.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
          <DialogFooter className="shrink-0 sm:items-end">
            <Button data-testid="pos-pay-cancel" variant="outline" disabled={pagoBloqueado} onClick={() => cerrarCobro(false)}
                    title={pagoBloqueado ? "Hay un pago con tarjeta aprobado: paga el saldo pendiente" : undefined}>Cancelar</Button>
            <Button ref={confirmVentaRef} data-testid="pos-finish-sale-button" onClick={() => charge()} className="h-14 px-8 text-base font-bold rounded-xl bg-sky-500 hover:bg-sky-600 text-white shadow-md">
              {Number(recibido || 0) > 0 && Number(recibido || 0) < totalAPagar - 0.5 ? "Abonar pago" : "Confirmar venta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar limpiar carrito */}
      <Dialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader><DialogTitle>¿Limpiar la lista?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Se eliminarán {cart.length} ítem(s) del carrito.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearConfirmOpen(false)}>Cancelar</Button>
            <Button data-testid="pos-clear-cart-confirm" autoFocus onClick={() => { clearAll(); setClearConfirmOpen(false); }} className="bg-rose-500 hover:bg-rose-600 text-white">Limpiar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal ticket preventa */}
      <Dialog open={preOpen} onOpenChange={setPreOpen}>
        <DialogContent className="rounded-2xl max-w-sm print:shadow-none print:border-0">
          <DialogHeader><DialogTitle>Vale de pre-venta</DialogTitle></DialogHeader>
          {lastPresale && (
            <div className="bg-white p-4 rounded-lg font-mono-num text-sm" id="ticket-print">
              <div className="text-center mb-3">
                <p className="font-bold">{company?.nombre_comercial}</p>
                <p className="text-xs text-slate-500">{store?.nombre} · Pre-venta</p>
                <p className="text-xs text-slate-500 mt-1">{new Date(lastPresale.created_at).toLocaleString("es-CL")}</p>
              </div>
              <div className="border-t border-dashed py-2 space-y-1 text-xs">
                {lastPresale.items.map((it, i) => (
                  <div key={i} className="flex justify-between"><span>{it.cantidad}x {it.nombre}</span><span>{clp(it.subtotal)}</span></div>
                ))}
              </div>
              <div className="border-t border-dashed pt-2 flex justify-between font-bold">
                <span>TOTAL</span><span>{clp(lastPresale.total)}</span>
              </div>
              <div className="mt-4 flex flex-col items-center bg-white p-2 rounded border border-dashed">
                <p className="text-[10px] text-slate-500 mb-1">Preséntalo en caja para pagar</p>
                <Barcode
                  value={lastPresale.codigo}
                  format={preventaBarcodeType === "EAN13" ? "CODE39" : "CODE39"}
                  width={1.5}
                  height={60}
                  fontSize={14}
                  displayValue
                />
                <p data-testid="pos-presale-code" className="text-2xl font-extrabold tracking-widest mt-1">{lastPresale.codigo}</p>
              </div>
              <p className="text-center text-[10px] text-slate-500 mt-2">Este vale NO es comprobante de pago</p>
            </div>
          )}
          <DialogFooter className="print:hidden">
            <Button variant="outline" onClick={() => setPreOpen(false)}><X className="w-4 h-4 mr-1" /> Cerrar</Button>
            <Button data-testid="pos-print-presale-btn" onClick={printTicket} className="bg-sky-500 hover:bg-sky-600 text-white"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal ticket final */}
      <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
        <DialogContent className="rounded-2xl max-w-sm print:shadow-none print:border-0">
          <DialogHeader><DialogTitle>Ticket de venta</DialogTitle></DialogHeader>
          {lastSale && (
            <SaleTicket sale={lastSale} companyName={company?.nombre_comercial} storeName={store?.nombre} graciasMessage={graciasMsg} />
          )}
          <DialogFooter className="print:hidden flex-wrap gap-2">
            <div className="flex-1 flex items-center gap-2 min-w-0">
              {btOk ? (
                btName ? (
                  <>
                    <Button data-testid="pos-bt-print-btn" size="sm" disabled={btBusy} onClick={() => doPrintBt(lastSale)} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                      <Bluetooth className="w-4 h-4 mr-1" /> {btBusy ? "Enviando..." : `Imprimir BT`}
                    </Button>
                    <span className="text-[10px] text-slate-500 truncate hidden sm:inline" title={btName}>· {btName}</span>
                    <Button data-testid="pos-bt-forget-btn" size="sm" variant="outline" onClick={doForgetBt} title="Olvidar impresora BT"><BluetoothOff className="w-3.5 h-3.5" /></Button>
                  </>
                ) : (
                  <Button data-testid="pos-bt-pair-btn" size="sm" variant="outline" disabled={btBusy} onClick={doPairPrinter} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    <BluetoothSearching className="w-4 h-4 mr-1" /> Vincular BT
                  </Button>
                )
              ) : null}
            </div>
            <Button variant="outline" onClick={() => setTicketOpen(false)}><X className="w-4 h-4 mr-1" /> Cerrar</Button>
            <Button data-testid="pos-print-receipt-btn" onClick={printTicket} className="bg-sky-500 hover:bg-sky-600 text-white"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Abrir caja */}
      <Dialog open={openOpen} onOpenChange={setOpenOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Abrir caja</DialogTitle></DialogHeader>
          <div>
            <Label>Fondo inicial</Label>
            <Input data-testid="register-initial-fund-input" ref={fundInputRef} type="text" inputMode="numeric"
              value={miles(fondo)} placeholder="0" autoFocus
              onChange={(e) => setFondo(soloDigitos(e.target.value))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); requestOpenFund(); } }}
              className="h-12 text-lg font-mono-num rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenOpen(false)}>Cancelar</Button>
            <Button data-testid="register-open-submit-button" onClick={requestOpenFund} className="bg-sky-500 hover:bg-sky-600 text-white">Abrir caja</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar fondo inicial */}
      <Dialog open={confirmFundOpen} onOpenChange={setConfirmFundOpen}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="confirm-fund-dialog">
          <DialogHeader><DialogTitle>¿El monto es correcto?</DialogTitle></DialogHeader>
          <p className="text-center text-3xl font-bold font-mono-num text-sky-700 py-2" data-testid="confirm-fund-amount">{clp(Number(fondo) || 0)}</p>
          <p className="text-center text-sm text-slate-500">Fondo inicial de la caja</p>
          <DialogFooter>
            <Button variant="outline" data-testid="confirm-fund-no"
              onClick={() => { setConfirmFundOpen(false); setTimeout(() => { try { fundInputRef.current?.focus(); fundInputRef.current?.select(); } catch (_) {} }, 50); }}>
              No, corregir
            </Button>
            <Button data-testid="confirm-fund-yes" autoFocus className="bg-emerald-500 hover:bg-emerald-600 text-white"
              onClick={() => { setConfirmFundOpen(false); openShift(); }}>
              Sí, abrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar cierre de caja */}
      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="confirm-close-dialog">
          <DialogHeader><DialogTitle>¿Cerrar la caja?</DialogTitle></DialogHeader>
          <p className="text-center text-sm text-slate-600 py-2">
            Esta acción cierra el turno {closeMode === "ARQUEO" ? "con arqueo" : "actual"} y no se puede deshacer. ¿Confirmas?
          </p>
          {tbkIntegrado && <TbkCuadre tot={tbkTot} sistema={cuadratura?.tbk_sistema} />}
          {(c2cHabilitado || cuadratura?.c2c_sistema?.cantidad > 0) && <C2cCuadre sistema={cuadratura?.c2c_sistema} />}
          <DialogFooter>
            <Button variant="outline" data-testid="confirm-close-no" onClick={() => setConfirmCloseOpen(false)}>Cancelar</Button>
            <Button data-testid="confirm-close-yes" autoFocus className="bg-rose-500 hover:bg-rose-600 text-white"
              disabled={!!tbkTot?.cargando}
              onClick={() => { setConfirmCloseOpen(false); closeShift(); }}>
              Sí, cerrar caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cerrar caja */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="rounded-2xl max-w-3xl" data-testid="pos-close-dialog">
          <DialogHeader><DialogTitle>Cerrar caja {closeMode === "ARQUEO" ? "· con arqueo" : "· cierre simple"}</DialogTitle></DialogHeader>
          {/* El modo de cierre lo fija el administrador en la configuración del terminal */}
          <div data-testid="pos-close-mode" className={`rounded-2xl p-3 border-2 ${closeMode === "ARQUEO" ? "border-amber-400 bg-amber-50" : "border-sky-400 bg-sky-50"}`}>
            <p className="font-bold text-sm">{closeMode === "ARQUEO" ? "Cierre con arqueo" : "Cierre simple"}</p>
            <p className="text-[11px] text-slate-600 mt-0.5">
              {closeMode === "ARQUEO"
                ? "Cuenta los billetes y monedas de la caja para calcular la diferencia."
                : "Sin arqueo: se cierra con el efectivo esperado del turno."}
            </p>
          </div>

          {shift && (
            <div className="space-y-2 text-sm bg-slate-50 rounded-xl p-3 font-mono-num" data-testid="pos-close-summary">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Resumen del turno</p>
              <div className="flex justify-between"><span>Apertura</span><span>{new Date(shift.opened_at || shift.created_at).toLocaleString("es-CL")}</span></div>
              <div className="flex justify-between"><span>Fondo inicial</span><span>{clp(shift.fondo_inicial)}</span></div>
              {cuadratura ? (
                <div data-testid="pos-close-cuadratura" className="pt-1 space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Ventas por medio de pago</p>
                  {cuadratura.medios.length === 0 && <p className="text-slate-400 text-xs">Sin ventas en este turno</p>}
                  {cuadratura.medios.map((m) => (
                    <div key={m.codigo} data-testid={`pos-close-medio-${m.codigo}`} className="flex justify-between">
                      <span>{m.nombre || m.codigo.replace(/_/g, " ")} <span className="text-slate-400">· {m.n}</span></span>
                      <span className="font-mono-num">{clp(m.monto)}</span>
                    </div>
                  ))}
                  {cuadratura.redondeo_dia !== 0 && (
                    <div data-testid="pos-close-redondeo" className="flex justify-between text-amber-700">
                      <span>Ajustes por redondeo del día</span>
                      <span className="font-mono-num">{cuadratura.redondeo_dia > 0 ? "+" : ""}{clp(cuadratura.redondeo_dia)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-1 border-t border-slate-200">
                    <span>Total cobrado ({cuadratura.num_ventas} ventas)</span>
                    <span className="font-mono-num">{clp(cuadratura.ventas_cobrado)}</span>
                  </div>
                  {[["ventas_boleta", "Ventas con boleta"], ["ventas_factura", "Ventas con factura"], ["tickets_sin_dte", "Tickets sin DTE"]].map(([k, l]) => cuadratura[k]?.cantidad > 0 && (
                    <div key={k} data-testid={`pos-close-${k}`} className="flex justify-between text-slate-600 text-xs">
                      <span>{l} · {cuadratura[k].cantidad}</span><span className="font-mono-num">{clp(cuadratura[k].total)}</span>
                    </div>
                  ))}
                  {cuadratura.ventas_simuladas > 0 && (
                    <p className="text-[11px] text-rose-600">Incluye {clp(cuadratura.ventas_simuladas)} de cobros simulados</p>
                  )}
                  <CuadraturaCredito c={cuadratura} testid="pos-close" />
                  {cuadratura.ventas_nulas > 0 && (
                    <div data-testid="pos-close-nulas" className="flex justify-between text-rose-700 font-bold">
                      <span>Venta nula · {cuadratura.ventas_nulas}</span>
                      <span className="font-mono-num">{clp(cuadratura.monto_nulas)}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex justify-between text-emerald-700"><span>Ventas efectivo</span><span>{clp(shift.ventas_efectivo)}</span></div>
              )}
              {(shift.ingresos > 0) && <div className="flex justify-between"><span>Ingresos</span><span>+{clp(shift.ingresos)}</span></div>}
              {(shift.retiros > 0) && <div className="flex justify-between"><span>Retiros</span><span>-{clp(shift.retiros)}</span></div>}
            </div>
          )}

          {closeMode === "ARQUEO" && shift && (
            <div className="space-y-2" data-testid="pos-close-arqueo-panel">
              <p className="text-xs font-bold text-amber-900 uppercase tracking-widest">Ingresa las cantidades contadas</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                {DENOMS_ARQUEO.map((d) => {
                  const qty = arqueo[d] || "";
                  const subt = (parseInt(qty || 0, 10) * d);
                  return (
                    <div key={d} className="bg-white border-2 border-slate-200 rounded-xl p-2">
                      <p className="text-lg font-extrabold text-slate-800 font-mono-num leading-none">{clp(d)}</p>
                      <Input
                        data-testid={`arqueo-denom-${d}`}
                        type="number" min="0" value={qty}
                        onChange={(e) => setArqueo({ ...arqueo, [d]: e.target.value })}
                        placeholder="0"
                        className="h-11 text-center rounded-lg font-mono-num text-lg font-bold mt-1.5 border-slate-200"
                      />
                      {subt > 0 && <p className="text-[11px] text-emerald-700 font-mono-num font-bold text-right mt-0.5">={clp(subt)}</p>}
                    </div>
                  );
                })}
              </div>
              <div className="bg-slate-900 text-white rounded-2xl p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Total contado</p>
                <p data-testid="pos-arqueo-total" className="text-3xl font-black font-mono-num">{clp(arqueoTotal)}</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Cuenta el dinero y registra lo que hay. La diferencia se calcula al cerrar.
                </p>
              </div>
              {terminal?.cash_drawer_enabled && (
                <Button data-testid="pos-arqueo-open-drawer" type="button" variant="outline"
                        onClick={() => openCashDrawer("ARQUEO")}
                        className="w-full h-12 rounded-xl border-2 border-amber-300 text-amber-700 font-bold hover:bg-amber-50">
                  <Wallet className="w-4 h-4 mr-1.5" /> Abrir gaveta para contar
                </Button>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancelar</Button>
            <Button data-testid="register-close-submit-button" onClick={pedirCierre} className="bg-rose-500 hover:bg-rose-600 text-white">
              {closeMode === "ARQUEO" ? "Cerrar con arqueo" : "Confirmar cierre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movimiento */}
      {tpMetodo && shift && (
        <TPAbonoDialog open={tpAbonoOpen} onClose={() => setTpAbonoOpen(false)} nombre={tpMetodo.nombre} shift={shift} terminalId={terminalId}
          metodos={posPayMethods.filter((m) => m.codigo !== "TARJETA_PROPIA" && payAllowed(m.codigo))}
          cobrarEnEquipo={cobrarEnEquipo} onDone={(a) => { imprimirAbono(a); load(); }} />
      )}
      <Dialog open={movOpen} onOpenChange={setMovOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Movimiento de caja</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="pos-mov-ingreso" {...lk("ingreso")} onClick={() => setMov({ ...mov, tipo: "INGRESO" })} className={`h-14 rounded-xl font-bold text-base border-2 ${mov.tipo === "INGRESO" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-slate-200"}`}>Ingreso</button>
              <button data-testid="pos-mov-retiro" {...lk("retiro")} onClick={() => setMov({ ...mov, tipo: "RETIRO" })} className={`h-14 rounded-xl font-bold text-base border-2 ${mov.tipo === "RETIRO" ? "bg-rose-500 text-white border-rose-500" : "bg-white border-slate-200"}`}>Retiro</button>
            </div>
            <div>
              <Label>Monto</Label>
              <Input data-testid="pos-mov-amount" ref={movMontoRef} type="text" inputMode="numeric" autoFocus
                     value={miles(mov.monto)} placeholder="0"
                     onChange={(e) => setMov({ ...mov, monto: soloDigitos(e.target.value) })}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); movMotivoRef.current?.focus(); } }}
                     className="h-14 rounded-xl text-2xl font-mono-num font-bold" />
              <div className="mt-2">
                <NumPad testid="pos-mov-numpad" value={String(mov.monto ?? "")} enterLabel="Siguiente"
                        onChange={(v) => setMov({ ...mov, monto: v })}
                        onEnter={() => movMotivoRef.current?.focus()} />
              </div>
            </div>
            <div>
              <Label>Motivo</Label>
              <Input data-testid="pos-mov-reason" ref={movMotivoRef} value={mov.motivo}
                     placeholder="Ej: retiro a bóveda"
                     onChange={(e) => setMov({ ...mov, motivo: e.target.value })}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveMov(); } }}
                     className="h-12 rounded-xl" />
              <p className="text-[11px] text-slate-500 mt-1">
                Monto → <kbd className="bg-slate-100 px-1 rounded">Enter</kbd> → motivo → <kbd className="bg-slate-100 px-1 rounded">Enter</kbd>: guarda e imprime al instante.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovOpen(false)}>Cancelar</Button>
            <Button data-testid="pos-mov-save" onClick={saveMov} className="bg-sky-500 hover:bg-sky-600 text-white">Guardar e imprimir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Ventas pendientes de sincronizar */}
      <Dialog open={pendingOpen} onOpenChange={setPendingOpen}>        <DialogContent className="rounded-2xl max-w-2xl" data-testid="pos-pending-dialog">
          <DialogHeader><DialogTitle>Ventas pendientes de sincronización</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {pendingList.length === 0 ? (
              <p className="text-center text-slate-500 py-10 text-sm">No hay ventas pendientes.</p>
            ) : pendingList.map((it) => {
              const t = (it.payload.items || []).reduce((s, x) => s + (x.precio_unitario || 0) * x.cantidad, 0);
              return (
                <div key={it.offline_id} data-testid={`pos-pending-${it.offline_id}`} className={`rounded-xl p-3 border ${it.status === "SYNCED" ? "bg-emerald-50 border-emerald-200" : it.status === "FAILED" ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono-num text-xs text-slate-600">{it.offline_id}</p>
                      <p className="text-xs text-slate-500">{new Date(it.created_at).toLocaleString("es-CL")} · {it.payload.items?.length || 0} ítem(s) · {clp(t)}</p>
                      {it.error && <p className="text-[11px] text-rose-700 mt-1">⚠ {it.error}</p>}
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${it.status === "SYNCED" ? "bg-emerald-500 text-white" : it.status === "FAILED" ? "bg-rose-500 text-white" : "bg-amber-500 text-white"}`}>
                      {it.status === "SYNCED" ? "Sincronizada" : it.status === "FAILED" ? "Error" : "Pendiente"}
                    </span>
                    {it.status === "FAILED" && (
                      <Button data-testid={`pos-pending-discard-${it.offline_id}`} size="sm" variant="ghost" onClick={async () => { await discardItem(it.offline_id); refreshPendingCount(); }} className="text-rose-600 hover:bg-rose-100">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={async () => { await purgeSynced(); refreshPendingCount(); }}>
              Limpiar sincronizadas
            </Button>
            <Button data-testid="pos-pending-sync-now" disabled={syncing || !isOnline} onClick={() => syncPending(false)} className="bg-sky-500 hover:bg-sky-600 text-white">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando…" : isOnline ? "Sincronizar ahora" : "Sin conexión"}
            </Button>
            <Button variant="ghost" onClick={() => setPendingOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Producto NO REGISTRADO — overlay rojo bloqueante (solo ESC dismiss) */}
      {unknownCode && (
        <div
          data-testid="pos-unknown-product-overlay"
          className="fixed inset-0 z-[9999] bg-rose-600/95 backdrop-blur-sm grid place-items-center p-6 animate-in fade-in"
          role="alertdialog"
          aria-modal="true"
        >
          <div className="max-w-3xl w-full text-center text-white">
            <AlertOctagon className="w-32 h-32 mx-auto mb-6 animate-pulse" strokeWidth={1.5} />
            <p className="text-sm uppercase tracking-[0.5em] opacity-90 font-bold mb-4">Atención cajero</p>
            <h1 className="text-6xl sm:text-7xl md:text-8xl font-black font-display leading-none uppercase tracking-tight">
              Producto no registrado
            </h1>
            <p className="mt-8 text-xl sm:text-2xl font-mono-num opacity-90">Código escaneado:</p>
            <p className="text-3xl sm:text-5xl font-black font-mono-num mt-2 bg-black/25 rounded-2xl inline-block px-8 py-3">{unknownCode}</p>
            <p className="mt-10 text-lg sm:text-xl font-bold">
              Presiona <kbd className="bg-white text-rose-600 px-4 py-2 rounded-xl font-black text-2xl mx-1">ESC</kbd> para continuar
            </p>
            <p className="mt-3 text-sm opacity-75">Revisa el producto antes de seguir escaneando. Este mensaje sólo se cierra con la tecla ESC.</p>
          </div>
        </div>
      )}

      {/* Modal peso pesable */}
      <Dialog open={!!weightModal} onOpenChange={(v) => { if (!v) { setWeightModal(null); setWeightInput(""); } }}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="pos-weight-dialog" onKeyDown={(e) => { if (e.key === "Enter") confirmWeight(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Scale className="w-5 h-5 text-amber-600" /> Ingresar peso</DialogTitle>
          </DialogHeader>
          {weightModal && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3 text-center">
                <p className="text-xs uppercase tracking-widest text-amber-700 font-bold">Producto pesable</p>
                <p className="text-lg font-bold text-slate-900 mt-1">{weightModal.product.nombre}</p>
                <p className="text-sm text-slate-600 mt-1 font-mono-num">{clp(weightModal.product.precio)} / kg</p>
              </div>
              <div className="rounded-2xl bg-slate-900 text-white p-4 text-center">
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Peso ingresado</p>
                <p data-testid="pos-weight-display" className="text-5xl font-black font-mono-num tracking-wider min-h-[3.5rem] leading-none mt-1">
                  {weightInput || "0.000"} <span className="text-2xl text-slate-400">kg</span>
                </p>
                {weightInput && (
                  <p className="text-sm text-emerald-300 font-mono-num font-bold mt-2">
                    Subtotal: {clp(weightModal.product.precio * (parseFloat(weightInput) || 0))}
                  </p>
                )}
              </div>
              {/* Teclado numérico */}
              <div className="grid grid-cols-3 gap-2">
                {["7", "8", "9", "4", "5", "6", "1", "2", "3", ".", "0", "⌫"].map((k) => (
                  <button
                    key={k}
                    data-testid={`pos-weight-key-${k === "⌫" ? "back" : k === "." ? "dot" : k}`}
                    onClick={() => {
                      if (k === "⌫") setWeightInput((s) => s.slice(0, -1));
                      else if (k === ".") setWeightInput((s) => s.includes(".") ? s : (s || "0") + ".");
                      else {
                        // Máximo 3 decimales
                        setWeightInput((s) => {
                          const next = s + k;
                          if (next.includes(".")) {
                            const [intp, dec] = next.split(".");
                            if (dec.length > 3) return s;
                          }
                          return next.length > 8 ? s : next;
                        });
                      }
                    }}
                    className={`h-14 rounded-2xl font-bold text-2xl font-mono-num transition ${k === "⌫" ? "bg-rose-500 hover:bg-rose-600 text-white" : k === "." ? "bg-slate-200 hover:bg-slate-300 text-slate-900" : "bg-white border-2 border-slate-200 hover:bg-sky-50 text-slate-900"}`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setWeightModal(null); setWeightInput(""); }}>Cancelar</Button>
            <Button data-testid="pos-weight-confirm" onClick={confirmWeight} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              Agregar al carrito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Ventas suspendidas */}
      <Dialog open={avisosOpen} onOpenChange={(v) => { if (!v) { setAvisosOpen(false); refocusScan(); } }}>
        <DialogContent
          className={`rounded-2xl max-w-lg ${hayAvisoUrgente ? "border-4 border-red-600 bg-red-50" : ""}`}
          data-testid="pos-avisos-dialog"
        >
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${hayAvisoUrgente ? "text-red-700" : ""}`}>
              <Megaphone className={`w-5 h-5 ${hayAvisoUrgente ? "text-red-600" : "text-rose-600"}`} />
              {hayAvisoUrgente ? "¡Aviso urgente!" : "Avisos de administración"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {avisos.map((a) => (
              <div key={a.aviso_id} data-testid={`pos-aviso-${a.aviso_id}`}
                   className={a.prioridad === "URGENTE"
                     ? "rounded-2xl border-2 border-red-600 bg-red-600 text-white p-3 shadow-lg shadow-red-600/30"
                     : "rounded-2xl border border-slate-200 bg-slate-50 p-3"}>
                <div className="flex items-center gap-2">
                  {a.prioridad === "URGENTE" && <AlertTriangle className="w-5 h-5 text-white animate-pulse" />}
                  <p className={`font-bold ${a.prioridad === "URGENTE" ? "text-white text-lg uppercase tracking-wide" : "text-slate-900"}`}>{a.titulo}</p>
                </div>
                <p className={`text-sm mt-1 whitespace-pre-wrap ${a.prioridad === "URGENTE" ? "text-white font-semibold" : "text-slate-700"}`}>{a.mensaje}</p>
                {a.image_url && (
                  <img src={resolveMedia(a.image_url)} alt="" data-testid={`pos-aviso-img-${a.aviso_id}`}
                       className="mt-2 w-full max-h-56 object-contain rounded-xl bg-white border border-black/10" />
                )}
                <p className={`text-[11px] mt-1 ${a.prioridad === "URGENTE" ? "text-red-100" : "text-slate-400"}`}>De {a.created_by_name}</p>
                <Button data-testid={`pos-aviso-ok-${a.aviso_id}`} onClick={() => confirmarAviso(a)}
                        disabled={avisoBusy === a.aviso_id}
                        className={`mt-2 h-9 rounded-xl font-bold ${a.prioridad === "URGENTE"
                          ? "bg-white text-red-700 hover:bg-red-50"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                  <Check className="w-4 h-4 mr-1" /> Entendido
                </Button>
              </div>
            ))}
            {!avisos.length && <p className="text-sm text-slate-400 py-6 text-center">No tienes avisos pendientes.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={suspendedOpen} onOpenChange={setSuspendedOpen}>
        <DialogContent className="rounded-2xl max-w-2xl" data-testid="pos-suspended-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PauseCircle className="w-5 h-5 text-violet-600" /> Ventas suspendidas</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {suspendedList.length === 0 ? (
              <p className="text-center py-10 text-sm text-slate-400">No hay ventas suspendidas.</p>
            ) : suspendedList.map((s) => (
              <div key={s.suspended_id} data-testid={`pos-suspended-${s.codigo}`} className="rounded-xl border border-violet-100 bg-violet-50/50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-black font-mono-num text-violet-900 tracking-widest">{s.codigo}</p>
                    <p className="text-xs text-slate-600">{new Date(s.created_at).toLocaleString("es-CL")} · {s.items.length} ítem(s) · <strong>{clp(s.total)}</strong></p>
                    {s.cliente_nombre && <p className="text-xs text-slate-500">Cliente: {s.cliente_nombre}</p>}
                  </div>
                  <Button size="sm" data-testid={`pos-resume-${s.codigo}`} onClick={() => consumeSuspended(s.codigo)} className="bg-violet-500 hover:bg-violet-600 text-white rounded-xl">
                    <PlayCircle className="w-4 h-4 mr-1" /> Recuperar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteSuspended(s.suspended_id)} className="text-rose-600 hover:bg-rose-100">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSuspendedOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal ticket suspendido */}
      <Dialog open={suspendTicketOpen} onOpenChange={setSuspendTicketOpen}>
        <DialogContent className="rounded-2xl max-w-sm print:shadow-none print:border-0">
          <DialogHeader><DialogTitle>Vale de venta suspendida</DialogTitle></DialogHeader>
          {lastSuspended && (
            <div className="bg-white p-4 rounded-lg font-mono-num text-sm" id="ticket-print">
              <div className="text-center mb-3">
                <p className="font-bold">{company?.nombre_comercial}</p>
                <p className="text-xs text-slate-500">{store?.nombre} · Venta suspendida</p>
                <p className="text-xs text-slate-500 mt-1">{new Date(lastSuspended.created_at).toLocaleString("es-CL")}</p>
              </div>
              <div className="border-t border-dashed py-2 space-y-1 text-xs">
                {lastSuspended.items.map((it, i) => (
                  <div key={i} className="flex justify-between"><span>{it.cantidad}× {it.nombre}</span><span>{clp(it.cantidad * it.precio_unitario)}</span></div>
                ))}
              </div>
              <div className="border-t border-dashed pt-2 flex justify-between font-bold">
                <span>TOTAL</span><span>{clp(lastSuspended.total)}</span>
              </div>
              <div className="mt-4 flex flex-col items-center bg-white p-2 rounded border border-dashed">
                <p className="text-[10px] text-slate-500 mb-1">Escanea este vale en caja para recuperar la venta</p>
                <Barcode value={lastSuspended.codigo} format="CODE39" width={1.5} height={60} fontSize={14} displayValue />
                <p data-testid="pos-suspended-code" className="text-2xl font-extrabold tracking-widest mt-1">{lastSuspended.codigo}</p>
              </div>
              <p className="text-center text-[10px] text-slate-500 mt-2">Este vale NO es comprobante de pago. Solo válido para recuperar la venta.</p>
            </div>
          )}
          <DialogFooter className="print:hidden flex-wrap gap-2">
            <Button variant="outline" onClick={() => setSuspendTicketOpen(false)}><X className="w-4 h-4 mr-1" /> Cerrar</Button>
            {btOk && btName && (
              <Button data-testid="pos-bt-print-suspend-btn" disabled={btBusy} onClick={async () => { setBtBusy(true); const ok = await autoPrintSuspendedBt(lastSuspended); setBtBusy(false); if (ok) toast.success("Vale enviado a impresora BT"); }} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                <Bluetooth className="w-4 h-4 mr-1" /> Imprimir BT
              </Button>
            )}
            <Button data-testid="pos-print-suspend-btn" onClick={() => window.print()} className="bg-violet-500 hover:bg-violet-600 text-white"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Reimpresión última venta */}
      <Dialog open={reprintTicketOpen} onOpenChange={setReprintTicketOpen}>
        <DialogContent className="rounded-2xl max-w-sm print:shadow-none print:border-0" data-testid="pos-reprint-dialog">
          <DialogHeader><DialogTitle>Reimpresión · Última venta</DialogTitle></DialogHeader>
          {reprintSale && (
            <div className="bg-white p-4 rounded-lg font-mono-num text-sm" id="ticket-print">
              <div className="text-center mb-3">
                <p className="font-bold">{company?.nombre_comercial}</p>
                <p className="text-xs text-slate-500">{store?.nombre}</p>
                <p className="text-xs inline-block px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-300 mt-1 font-bold uppercase tracking-widest text-[10px]">
                  ⟳ Reimpresión
                </p>
                <p className="text-xs text-slate-500 mt-1">Venta #{reprintSale.numero}</p>
                <p className="text-xs text-slate-500">{new Date(reprintSale.created_at).toLocaleString("es-CL")}</p>
              </div>
              <div className="border-t border-dashed py-2 space-y-1 text-xs">
                {reprintSale.items.map((it, i) => (
                  <div key={i} className="flex justify-between"><span>{it.cantidad}× {it.nombre}</span><span>{clp(it.subtotal)}</span></div>
                ))}
              </div>
              <div className="border-t border-dashed pt-2 space-y-1 text-xs">
                <div className="flex justify-between"><span>Neto</span><span>{clp(reprintSale.total - reprintSale.iva)}</span></div>
                <div className="flex justify-between"><span>IVA</span><span>{clp(reprintSale.iva)}</span></div>
                <div className="flex justify-between font-bold text-base pt-1"><span>Total</span><span>{clp(reprintSale.total)}</span></div>
                <div className="flex justify-between"><span>Pago</span><span>{reprintSale.payment_method}</span></div>
              </div>
            </div>
          )}
          <DialogFooter className="print:hidden flex-wrap gap-2">
            {btOk && btName && (
              <Button data-testid="pos-reprint-bt-btn" size="sm" disabled={btBusy} onClick={() => doPrintBt(reprintSale)} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                <Bluetooth className="w-4 h-4 mr-1" /> BT
              </Button>
            )}
            <Button variant="outline" onClick={() => setReprintTicketOpen(false)}><X className="w-4 h-4 mr-1" /> Cerrar</Button>
            <Button data-testid="pos-reprint-print-btn" onClick={printReprint} className="bg-cyan-500 hover:bg-cyan-600 text-white"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Devolución */}
      <Dialog open={!!returnModal} onOpenChange={(v) => { if (!v) { setReturnModal(null); setReturnQty({}); } }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="pos-return-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-orange-600" /> Devolución de venta</DialogTitle></DialogHeader>
          {returnModal && (
            <div className="space-y-3">
              <div className="rounded-xl bg-orange-50 border border-orange-100 p-3 text-xs">
                <p className="font-bold text-slate-900">Venta #{returnModal.sale.numero}</p>
                <p className="text-slate-600">{new Date(returnModal.sale.created_at).toLocaleString("es-CL")}</p>
                <p className="text-slate-600">Total: <strong>{clp(returnModal.sale.total)}</strong> · Pago: {returnModal.sale.payment_method}</p>
              </div>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {returnModal.sale.items.filter((it) => !it.agregado_de).map((it) => {
                  const agregados = returnModal.sale.items.filter((a) => a.agregado_de === it.product_id);
                  const yaDev = returnModal.already[it.product_id] || 0;
                  const max = it.cantidad - yaDev;
                  const val = returnQty[it.product_id] || 0;
                  return (
                    <div key={it.product_id} className="rounded-xl bg-slate-50 p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{it.nombre}</p>
                        {it.modificadores?.some((m) => !(it.mods_separados && m.precio > 0)) && <p data-testid={`return-mods-${it.product_id}`} className="text-[11px] text-sky-700 font-semibold truncate">{it.modificadores.filter((m) => !(it.mods_separados && m.precio > 0)).map((m) => m.nombre).join(" · ")}</p>}
                        {agregados.length > 0 && <p data-testid={`return-agregados-${it.product_id}`} className="text-[11px] text-slate-500 truncate">Incluye {agregados.map((a) => a.nombre).join(" · ")}</p>}
                        <p className="text-[11px] text-slate-500">Vendido: {it.cantidad} · Ya devuelto: {yaDev} · Disponible: {max}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setReturnQty({ ...returnQty, [it.product_id]: Math.max(0, val - 1) })}
                          disabled={val <= 0}
                          className="w-8 h-8 rounded-lg bg-white border border-slate-200 grid place-items-center disabled:opacity-40"
                        ><Minus className="w-3.5 h-3.5" /></button>
                        <span data-testid={`return-qty-${it.product_id}`} className="w-10 text-center font-mono-num font-bold">{val}</span>
                        <button
                          onClick={() => setReturnQty({ ...returnQty, [it.product_id]: Math.min(max, val + 1) })}
                          disabled={val >= max}
                          className="w-8 h-8 rounded-lg bg-white border border-slate-200 grid place-items-center disabled:opacity-40"
                        ><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="rounded-xl bg-orange-100 p-3 flex justify-between items-center">
                <span className="text-sm font-bold text-orange-900">Nota crédito por</span>
                <span data-testid="pos-return-total" className="text-2xl font-black text-orange-900 font-mono-num">
                  {clp(returnModal.sale.items.reduce((s, it) => s + it.precio_unitario * Math.min(returnQty[it.agregado_de || it.product_id] || 0, it.cantidad - (returnModal.already[it.product_id] || 0)), 0))}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReturnModal(null); setReturnQty({}); }}>Cancelar</Button>
            <Button data-testid="pos-return-confirm" onClick={confirmReturn} className="bg-orange-500 hover:bg-orange-600 text-white">Emitir nota crédito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </div>

        {/* Cart */}
        <aside className={`pos-cart-aside w-full shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-white flex flex-col max-h-[65vh] lg:max-h-none lg:h-full min-h-0 ${isTile ? "pos-cart-aside-tile lg:w-[430px] xl:w-[470px]" : "lg:w-[400px]"}`}>
          <div className="pos-cart-head px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
            <div className="pos-cart-head-txt flex items-baseline gap-2 flex-wrap min-w-0">
              <h3 data-testid="pos-cart-title" className={`font-extrabold text-slate-900 font-display leading-tight ${isTile ? "text-base" : "text-xl"}`}>Orden actual</h3>
              <p data-testid="pos-cart-count" className={`font-bold text-sky-600 ${isTile ? "text-sm" : "text-base"}`}>{cart.length} ítem{cart.length !== 1 ? "s" : ""}</p>
              {cart.length >= MAX_LINEAS_DTE && (
                <p data-testid="pos-cart-limite" className="text-xs font-bold text-rose-600 mt-0.5 max-w-[220px]">
                  Máximo {MAX_LINEAS_DTE} productos por documento: cobra y sigue en una venta nueva
                </p>
              )}
            </div>
            {quoteNumero && (
              <span data-testid="pos-quote-badge" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold">
                <FileText className="w-3 h-3" /> {quoteNumero}
              </span>
            )}

            {presaleTickets.length > 0 && (
              <span data-testid="pos-presale-count-badge" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-100 text-sky-800 text-[11px] font-bold">
                <Layers className="w-3 h-3" /> {presaleTickets.length} preventa{presaleTickets.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          {isRetail && (
            <>
            <div data-testid="pos-retail-actions" className="shrink-0 p-3 grid grid-cols-2 gap-2">
              <Button data-testid="pos-clear-cart-btn" disabled={!cart.length} {...lk("limpiar")}
                      onClick={() => { if (confirmClearCart) setClearConfirmOpen(true); else clearAll(); }}
                      className="h-20 rounded-2xl flex-col gap-1 bg-gradient-to-br from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-bold border-0 shadow-md disabled:opacity-40">
                <Trash2 className="w-6 h-6" /> Limpiar <span className="text-[10px] font-mono-num opacity-80">SUPR</span>
              </Button>
              <Button data-testid="pos-remove-mode-btn" disabled={!cart.length} {...lk("remover")}
                      onClick={() => setRemoveMode((v) => {
                        const nv = !v;
                        if (nv) {
                          setLastProduct({ nombre: "🚫 ESCANEAR PRODUCTO A QUITAR", precio: 0, cantidad: 0, sku: "REMOVER" });
                          toast.info("Modo remover activo: escanea el producto a quitar", { duration: 2500 });
                        } else setLastProduct(null);
                        setTimeout(refocusScan, 50);
                        return nv;
                      })}
                      className={`h-20 rounded-2xl flex-col gap-1 font-bold border-0 shadow-md disabled:opacity-40 text-white ${removeMode ? "bg-gradient-to-br from-rose-600 to-rose-700 ring-4 ring-rose-300 animate-pulse" : "bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"}`}>
                <Minus className="w-6 h-6" /> {removeMode ? "Remover activo" : "Remover"} <span className="text-[10px] font-mono-num opacity-80">REPAG</span>
              </Button>
              {!isPreventaTerminal && (
                <>
                  <Button data-testid="pos-reprint-btn" onClick={openReprintLast} disabled={!shift} {...lk("reimprimir")}
                          className="h-20 rounded-2xl flex-col gap-1 bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-bold border-0 shadow-md disabled:opacity-40">
                    <Printer className="w-6 h-6" /> Reimprimir
                  </Button>
                  <Button data-testid="pos-cash-drawer-button" disabled={!shift} {...(puede("ingreso") || puede("retiro") ? {} : lk("retiro"))}
                          onClick={() => { abrirMov(); }}
                          className="h-20 rounded-2xl flex-col gap-1 bg-gradient-to-br from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-bold border-0 shadow-md disabled:opacity-40">
                    <Wallet className="w-6 h-6" /> Ingreso/Retiro
                  </Button>
                  {tpMetodo && (
                    <Button data-testid="pos-tp-abono-btn-retail" disabled={!shift} onClick={() => setTpAbonoOpen(true)}
                            className="h-20 rounded-2xl flex-col gap-1 bg-gradient-to-br from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-white font-bold border-0 shadow-md disabled:opacity-40 text-xs leading-tight">
                      <CreditCard className="w-6 h-6" /> Pago {tpMetodo.nombre}
                    </Button>
                  )}
                </>
              )}
              <Button data-testid="pos-logout-operator-btn" variant="outline" disabled={!operator}
                      onClick={() => { clearOperator(); toast.info("Sesión del cajero cerrada"); }}
                      className="h-20 rounded-2xl flex-col gap-1 border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-bold disabled:opacity-40">
                <KeyRound className="w-6 h-6" /> Cerrar sesión
              </Button>
              <Button data-testid="pos-close-register-btn" disabled={!shift} {...lk("cierre_caja")} onClick={openCloseModal}
                      className="h-20 rounded-2xl flex-col gap-1 bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-bold border-0 shadow-md disabled:opacity-40">
                <Lock className="w-6 h-6" /> {isPreventaTerminal ? "Cerrar sesión" : "Cerrar caja"}
              </Button>
            </div>
            <div className="flex-1 min-h-0" />
            </>
          )}
          {!isRetail && (
          <div data-testid="pos-cart-container" className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 min-h-0">
            {cart.length === 0 && <p className="text-center text-sm text-slate-400 py-10">Toca un producto o escanea código</p>}
            {/* Último ingresado primero */}
            {[...cart].reverse().map((it) => (
              <div key={`${it.product_id}-${it.precio}`} data-testid={`pos-cart-line-${it.sku}`}
                   className={`rounded-2xl px-3 py-2.5 border ${it._fromPresale ? "bg-sky-50 border-sky-200" : "bg-white border-slate-200"}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-slate-900 leading-snug break-words">
                      {it._fromPresale && <TicketCheck className="w-4 h-4 inline text-sky-600 mr-1" />}
                      {it.nombre}
                    </p>
                    <p className="text-[12px] text-slate-500 font-mono-num mt-0.5">
                      {it.es_pesable ? `${it.cantidad.toFixed(3)} kg` : `${it.cantidad} ×`} {clp(it.precio)}
                    </p>
                  </div>
                  <span className="text-lg font-extrabold font-mono-num text-slate-900 leading-none whitespace-nowrap">{clp(it.precio * it.cantidad)}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  {it.es_pesable ? (
                    <div className="flex items-center gap-2">
                      <Scale className="w-4 h-4 text-amber-600" />
                      <span className="font-mono-num font-bold text-sm">{it.cantidad.toFixed(3)} kg</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button data-testid={`pos-cart-minus-${it.sku}`} onClick={() => inc(it.product_id, -1)} className="w-9 h-9 rounded-xl bg-slate-100 border border-slate-200 grid place-items-center active:scale-95 hover:bg-sky-50"><Minus className="w-4 h-4" /></button>
                      <span className="w-10 text-center font-mono-num font-extrabold text-base">{it.cantidad}</span>
                      <button data-testid={`pos-cart-plus-${it.sku}`} onClick={() => inc(it.product_id, 1)} className="w-9 h-9 rounded-xl bg-slate-100 border border-slate-200 grid place-items-center active:scale-95 hover:bg-sky-50"><Plus className="w-4 h-4" /></button>
                    </div>
                  )}
                  <button data-testid={`pos-cart-remove-${it.sku}`} onClick={() => rm(it.product_id)}
                          className="w-9 h-9 rounded-xl grid place-items-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 active:scale-95"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {vale && (
              <div data-testid="pos-cart-vale-row" className="rounded-xl p-3 bg-indigo-50 border border-indigo-100 flex justify-between items-center">
                <span className="text-sm font-semibold text-indigo-800 flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5" /> Vale de cambio #{vale.codigo}</span>
                <span className="text-sm font-bold font-mono-num text-indigo-700">-{clp(valeMonto)}</span>
              </div>
            )}
          </div>
          )}
          {/* Footer sticky */}
          <div className="border-t border-slate-100 bg-white shrink-0">
            <div className={isRetail ? "hidden" : "px-4 py-2 space-y-0.5 text-sm"}>
              {!isRetail && (
              <>
              {promoResult.applied.length > 0 && (
                <>
                  <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="font-mono-num">{clp(rawSubtotal)}</span></div>
                  {promoResult.applied.map((a, i) => (
                    <div key={i} data-testid={`pos-cart-promo-${a.promotion_id}`} className="flex justify-between text-emerald-600">
                      <span className="truncate">🎉 {a.nombre}</span>
                      <span className="font-mono-num">-{clp(a.savings)}</span>
                    </div>
                  ))}
                </>
              )}
              {vale && (
                <div data-testid="pos-vale-aplicado"
                     className="flex justify-between text-indigo-700 text-xs font-bold">
                  <span>Vale de cambio #{vale.codigo}</span>
                  <span className="font-mono-num">-{clp(valeMonto)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-400 text-[11px]">
                <span>Neto {clp(neto)}</span><span>IVA 19% {clp(iva)}</span>
              </div>
              <div className="flex justify-between items-center pt-1.5 border-t border-slate-100">
                <span className="text-slate-900 font-bold">Total</span>
                <span data-testid="pos-cart-total-amount" className="text-2xl font-bold text-slate-900 font-mono-num">{clp(total)}</span>
              </div>
              </>
              )}
            </div>
            <div className={`pos-side-actions px-3 pb-3 space-y-2 ${isRetail ? "pos-side-actions-retail" : ""}`}>
              {isTile && (
                <div className="grid grid-cols-2 gap-2">
                  <Button data-testid="pos-functions-btn-cart" onClick={() => setFuncOpen(true)}
                          variant="outline"
                          className="h-11 rounded-xl border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-bold text-xs">
                    <LayoutGrid className="w-4 h-4 mr-1" /> Funciones
                  </Button>
                  <Button data-testid="pos-clear-cart-btn-compact" disabled={!cart.length} {...lk("limpiar")}
                          onClick={() => { if (confirmClearCart) setClearConfirmOpen(true); else clearAll(); }}
                          variant="outline"
                          className="h-11 rounded-xl border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-bold text-xs disabled:opacity-40">
                    <Trash2 className="w-4 h-4 mr-1" /> Limpiar
                  </Button>
                </div>
              )}
              {isRetail && (
                <>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="pos-vale-cambio-btn" {...lk("vale_cambio")}
                  onClick={() => { setValeCodigo(""); setValeOpen(true); }}
                  variant="outline"
                  className="h-11 rounded-xl border-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-bold text-xs"
                >
                  <Repeat className="w-4 h-4 mr-1" /> Vale de cambio
                </Button>
                <Button
                  data-testid="pos-cotizacion-btn" {...lk("cotizacion")}
                  onClick={() => { setCotQ(""); setCotRows([]); setCotOpen(true); }}
                  variant="outline"
                  className="h-11 rounded-xl border-2 border-sky-300 text-sky-700 hover:bg-sky-50 font-bold text-xs"
                >
                  <FileText className="w-4 h-4 mr-1" /> Cotización
                </Button>
              </div>
              <Button
                data-testid="pos-suspend-btn"
                disabled={cart.length === 0}
                {...lk("suspender")}
                onClick={suspendCurrentSale}
                variant="outline"
                className="w-full h-11 rounded-xl border-2 border-violet-300 text-violet-700 hover:bg-violet-50 hover:border-violet-400 font-bold disabled:opacity-40"
              >
                <PauseCircle className="w-4 h-4 mr-1.5" /> Suspender venta
              </Button>
              <Button
                data-testid="pos-quick-sale-btn"
                onClick={() => { setQuickMonto(""); setQuickOpen(true); }}
                disabled={!shift || isPreventaTerminal}
                {...lk("venta_rapida")}
                variant="outline"
                className="w-full h-11 rounded-xl border-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 font-bold disabled:opacity-40"
              >
                <Zap className="w-4 h-4 mr-1.5" /> Venta rápida por monto
              </Button>
              <Button
                data-testid="pos-qty-btn" {...lk("cantidad")}
                onClick={() => { setQtyInput(""); setQtyOpen(true); }}
                variant="outline"
                className="w-full h-11 rounded-xl border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-bold"
              >
                <Hash className="w-4 h-4 mr-1.5" /> Cantidad {pendingQty !== null ? `· próx. ×${pendingQty}` : ""}
              </Button>
              </>
              )}
              {isRetail && (
                <div data-testid="pos-retail-units" className="flex items-center justify-between rounded-xl bg-slate-100 px-4 py-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Unidades</span>
                  <span data-testid="pos-retail-units-count" className="text-2xl font-black font-mono-num text-slate-900">
                    {cart.reduce((a, b) => a + (b.es_pesable ? 1 : b.cantidad), 0)}
                  </span>
                </div>
              )}
              {!isTile && (
                <p className="text-center text-[10px] text-slate-400">Presiona <kbd className="bg-slate-100 px-1 rounded font-mono-num">FIN</kbd> para cobrar</p>
              )}
              <Button
                data-testid="pos-charge-submit-button" disabled={cart.length === 0 || !shift}
                onClick={openPayModal}
                className={`pos-cobrar-btn w-full rounded-2xl text-white font-extrabold shadow-lg disabled:opacity-40 flex flex-col gap-0 leading-none ${isTile ? "h-20" : "h-24"} ${isPreventaTerminal ? "bg-sky-600 hover:bg-sky-700 shadow-sky-500/25" : "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/25"}`}
              >
                <span className="text-sm uppercase tracking-widest opacity-90">
                  {!shift ? "Abre caja para operar" : isPreventaTerminal ? "Emitir pre-venta" : "Cobrar"}
                </span>
                {shift && <span className="pos-cobrar-total text-4xl font-black font-mono-num mt-1">{clp(Math.max(0, total - valeMonto))}</span>}
              </Button>
            </div>
          </div>
        </aside>
      </div>

      {/* Cierre de venta: vuelto bien grande + aviso mientras sale la boleta */}
      {printingSale && (
        <div data-testid="pos-printing-overlay" className="fixed inset-0 z-[200] bg-slate-900/95 backdrop-blur-sm grid place-items-center p-6 print:hidden">
          <div className="text-center text-white max-w-md">
            {printingSale.vuelto != null && (
              <>
                <p className="text-sm uppercase tracking-[0.3em] text-slate-400">Vuelto</p>
                <p data-testid="pos-printing-vuelto" className="text-7xl sm:text-8xl font-black font-mono-num text-emerald-400 leading-none mt-1">
                  {clp(printingSale.vuelto)}
                </p>
              </>
            )}
            <p className={printingSale.vuelto != null ? "text-lg font-bold mt-6" : "text-2xl font-bold mt-2"}>
              {printingSale.sale?.numero ? `Venta #${printingSale.sale.numero} cobrada` : "Venta cobrada"}
            </p>
            <p data-testid="pos-printing-status"
               className={`text-slate-100 mt-3 flex items-center justify-center gap-3 font-black ${printingSale.vuelto != null ? "text-xl" : "text-5xl sm:text-6xl"}`}>
              {printingSale.pendiente
                ? <><Loader2 className={`animate-spin ${printingSale.vuelto != null ? "w-6 h-6" : "w-14 h-14"}`} /> Procesando…</>
                : printingSale.done
                  ? <><Check className={printingSale.vuelto != null ? "w-6 h-6 text-emerald-400" : "w-14 h-14 text-emerald-400"} /> {docImpreso(printingSale.sale)} impresa</>
                  : <><Printer className={`animate-pulse ${printingSale.vuelto != null ? "w-6 h-6" : "w-14 h-14"}`} /> Imprimiendo tu {docImpreso(printingSale.sale).toLowerCase()}…</>}
            </p>
          </div>
        </div>
      )}

      {/* Cantidad para el próximo producto (equipos táctiles sin tecla F9) */}
      <Dialog open={qtyOpen} onOpenChange={setQtyOpen}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="pos-qty-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Hash className="w-5 h-5 text-sky-500" /> Cantidad</DialogTitle></DialogHeader>
          <Input data-testid="pos-qty-input" type="number" autoFocus value={qtyInput} placeholder="1"
                 onChange={(e) => setQtyInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyQtyInput(); } }}
                 className="h-16 rounded-xl text-3xl font-mono-num font-bold text-center" />
          <p className="text-[11px] text-slate-500">Se aplica al próximo producto que escanees o toques.</p>
          <NumPad testid="pos-qty-numpad" value={qtyInput} enterLabel="Aplicar"
                  onChange={setQtyInput} onEnter={applyQtyInput} />
        </DialogContent>
      </Dialog>

      {/* Venta rápida por monto: escribe el monto y Enter lo agrega al carrito */}
      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="quick-sale-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Venta rápida</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Monto</Label>
            <Input
              data-testid="quick-sale-amount-input" type="text" inputMode="numeric" autoFocus value={miles(quickMonto)}
              placeholder="0"
              onChange={(e) => setQuickMonto(soloDigitos(e.target.value))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addQuickSaleItem(); } }}
              className="h-16 rounded-xl text-3xl font-mono-num font-bold text-center"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Escribe el monto y presiona <kbd className="bg-slate-100 px-1 rounded font-mono-num">Enter</kbd>: se agrega como ítem <strong>Venta Rápida</strong> a la lista.
            </p>
            <div className="mt-3">
              <NumPad testid="quick-sale-numpad" value={quickMonto} enterLabel="Agregar"
                      onChange={setQuickMonto} onEnter={addQuickSaleItem} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickOpen(false)}>Cancelar</Button>
            <Button data-testid="quick-sale-add-btn" onClick={addQuickSaleItem} className="bg-amber-500 hover:bg-amber-600 text-white">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle opcional de la venta rápida por monto (no se imprime) */}
      <Dialog open={quickDetalleOpen != null} onOpenChange={(v) => { if (!v) setQuickDetalleOpen(null); }}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="quick-detalle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" /> ¿Qué estás cobrando?
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-3xl font-black font-mono-num text-slate-900 -mt-1">
            {clp(Number(quickDetalleOpen) || 0)}
          </p>
          <Input
            data-testid="quick-detalle-input" autoFocus value={quickDetalle}
            placeholder="Ej: pack de 6 bebidas sin código"
            onChange={(e) => setQuickDetalle(e.target.value.slice(0, 120))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmarQuickSale(quickDetalle); } }}
            className="h-12 rounded-xl"
          />
          <p className="text-[11px] text-slate-500 -mt-2">
            Es <strong>opcional</strong>: escribe el detalle y presiona Enter, o presiona Enter sin
            escribir nada. No se imprime en el vale, queda guardado para el jefe de tienda.
          </p>
          <DialogFooter>
            <Button data-testid="quick-detalle-skip" variant="outline" onClick={() => confirmarQuickSale("")}>
              Sin detalle
            </Button>
            <Button data-testid="quick-detalle-confirm" onClick={() => confirmarQuickSale(quickDetalle)}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold">
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Canje de vale de cambio */}
      <Dialog open={valeOpen} onOpenChange={(v) => { setValeOpen(v); if (!v) setValeCodigo(""); }}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="vale-cambio-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-indigo-500" /> Canjear vale de cambio
            </DialogTitle>
          </DialogHeader>
          <Input
            data-testid="vale-cambio-input" autoFocus value={valeCodigo} inputMode="numeric"
            placeholder="Código de 5 dígitos"
            onChange={(e) => setValeCodigo(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarVale(); } }}
            className="h-14 rounded-xl text-2xl font-mono-num font-bold text-center tracking-widest"
          />
          <p className="text-[11px] text-slate-500 -mt-2">
            El monto del vale se descuenta del total de esta venta y los productos devueltos
            vuelven al stock al confirmar el cobro.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValeOpen(false)}>Cancelar</Button>
            <Button data-testid="vale-cambio-buscar" onClick={buscarVale} disabled={valeBusy || valeCodigo.length < 3}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
              {valeBusy ? "Buscando…" : "Aplicar vale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recuperar cotización */}
      <Dialog open={cotOpen} onOpenChange={setCotOpen}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="cotizacion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-500" /> Recuperar cotización
            </DialogTitle>
          </DialogHeader>
          <Input
            data-testid="cotizacion-search-input" autoFocus value={cotQ}
            placeholder="Número de cotización, RUT o nombre del cliente"
            onChange={(e) => setCotQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarCotizaciones(); } }}
            className="h-12 rounded-xl"
          />
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
            {cotBusy && <p className="py-6 text-center text-sm text-slate-400">Buscando…</p>}
            {!cotBusy && cotRows.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-400">
                Escribe el número o el cliente y presiona Enter
              </p>
            )}
            {cotRows.map((c) => (
              <button key={c.quote_id} data-testid={`cotizacion-row-${c.quote_id}`}
                      onClick={() => cargarCotizacion(c)}
                      className="w-full text-left py-2.5 px-1 hover:bg-sky-50 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 text-sm">
                    #{c.numero} · {c.cliente_nombre || "Sin nombre"}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {c.cliente_rut || "—"} · {(c.items || []).length} ítem(s) · {c.estado}
                  </p>
                </div>
                <span className="font-mono-num font-bold text-slate-800">{clp(c.total)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* PIN obligatorio: el POS necesita saber qué vendedor está trabajando */}
      <Dialog open={!operator} onOpenChange={() => {}}>
        <DialogContent className="rounded-2xl max-w-sm [&>button]:hidden" data-testid="pos-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-sky-500" /> Ingresa tu PIN</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 -mt-2">
            {isPreventaTerminal ? "Pre-venta" : "Caja"} {terminal?.nombre ? `· ${terminal.nombre}` : ""} · identifícate para comenzar tu turno.
          </p>
          <PasswordInput
            data-testid="pos-pin-input" inputMode="numeric" autoFocus value={pinValue}
            placeholder="● ● ● ●"
            onChange={(e) => setPinValue(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); identifyOperator(); } }}
            className="h-16 rounded-xl text-3xl font-mono-num font-bold text-center tracking-[0.5em]"
          />
          <NumPad testid="pos-pin-numpad" value={pinValue} enterLabel="Entrar"
                  onChange={(v) => setPinValue(String(v).slice(0, 6))} onEnter={identifyOperator} />
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button data-testid="pos-pin-submit" disabled={pinBusy || pinValue.length < 4} onClick={identifyOperator}
                    className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-bold">
              {pinBusy ? "Verificando…" : "Entrar"}
            </Button>
            <button data-testid="pos-pin-exit" onClick={exitPos} className="text-xs text-slate-500 hover:text-slate-800 underline">
              Salir del punto de venta
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function C2cCuadre({ sistema }) {
  const sis = sistema || { cantidad: 0, total: 0 };
  return (
    <div data-testid="c2c-cuadre" className="rounded-xl border-2 border-fuchsia-200 bg-fuchsia-50 p-3 text-sm">
      <p className="font-bold text-slate-900 mb-1">Transbank C2C</p>
      <div className="flex justify-between"><span>Cobrado en Ai-POS</span><span data-testid="c2c-cuadre-sistema" className="font-mono-num">{sis.cantidad} tx · {clp(sis.total)}</span></div>
      <p className="mt-1 text-xs text-slate-600">Compáralo con el total del reporte de cierre del equipo Transbank.</p>
    </div>
  );
}

function TbkCuadre({ tot, sistema }) {
  const sis = sistema || { cantidad: 0, total: 0 };
  if (tot?.cargando) return <p data-testid="tbk-cuadre-loading" className="text-sm text-center text-sky-700 font-semibold">Consultando totales del equipo Transbank…</p>;
  const difN = tot?.ok ? tot.cantidad - sis.cantidad : null;
  const difM = tot?.ok ? tot.total - sis.total : null;
  const cuadra = tot?.ok && difN === 0 && Math.abs(difM) < 1;
  return (
    <div data-testid="tbk-cuadre" className={`rounded-xl border-2 p-3 text-sm ${cuadra ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
      <p className="font-bold text-slate-900 mb-1">Transbank POS Integrado</p>
      <div className="flex justify-between"><span>Ai-POS</span><span data-testid="tbk-cuadre-sistema" className="font-mono-num">{sis.cantidad} tx · {clp(sis.total)}</span></div>
      {tot?.ok ? (
        <>
          <div className="flex justify-between"><span>Equipo</span><span data-testid="tbk-cuadre-equipo" className="font-mono-num">{tot.cantidad} tx · {clp(tot.total)}</span></div>
          <div className="flex justify-between font-bold"><span>Diferencia</span><span data-testid="tbk-cuadre-diferencia" className="font-mono-num">{difN} tx · {clp(difM)}</span></div>
          <p className={`mt-1 font-bold ${cuadra ? "text-emerald-700" : "text-amber-700"}`}>{cuadra ? "Cuadra con el equipo" : "No cuadra: puedes cerrar igual, la diferencia queda impresa y guardada"}</p>
        </>
      ) : (
        <p data-testid="tbk-cuadre-error" className="mt-1 text-amber-700 font-semibold">
          {tot?.mensaje || "Equipo no consultado"} · puedes cerrar igual; quedará "Cierre Transbank no realizado" si el equipo no responde.
        </p>
      )}
    </div>
  );
}
